// =============================================================================
// Project Management module (PROTOTYPE) — project detail
//
// Surfaces, for one managed project: its lifecycle + current stage, a stage-advance
// control with an audited transition history, the per-stage task checklist, and the
// first-class Document Management panel (typed slots, version control with
// supersede / approve / reject / revert).
// =============================================================================

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate, Link } from 'react-router-dom';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { Tooltip } from 'react-tooltip';
import { strings } from '/imports/strings';
import { css } from '/imports/ui/css';
import { calendarDateInputValue, formatCalendarDate, formatDateTime, parseCalendarDate, parseTypedAmount, queryStringToObject, roundNumber } from '/imports/utils';
import { Page } from '/imports/ui/Page';
import { PageHeading } from '/imports/ui/PageHeading';
import { PageBody } from '/imports/ui/PageBody';
import { Button } from '/imports/ui/Button';
import { Select } from '/imports/ui/Select';
import { RichMultiSelect } from '/imports/ui/RichMultiSelect';
import { quickbooksApplyCashSuggestion, quickbooksDismissCashSuggestion, quickbooksOutflowSync, quickbooksCashSync } from '/imports/api/quickbooks';
import { Input } from '/imports/ui/Input';
import { CurrencyInput } from '/imports/ui/CurrencyInput';
import { PhoneInput } from '/imports/ui/PhoneInput';
import { ConversationFeed } from '/imports/ui/ConversationFeed';
import { eventActionLabel } from '/imports/ui/projectManagementEventLabels';
import { Conversations, projectManagementConversation } from '/imports/api/conversations';
import { MessageReceipts } from '/imports/api/messages';
import { ImageInput } from '/imports/ui/ImageInput';
import { TextArea } from '/imports/ui/TextArea';
import { ProjectStatusBadge, ProjectStatusTooltip } from '/imports/ui/ProjectManagementProjectStatus';
import { ProjectManagementBudgetResTpo } from '/imports/ui/ProjectManagementBudgetResTpo';
import { projectManagementValueInput, projectManagementToStored } from '/imports/ui/ProjectManagementCellEditor';
import { DateInput } from '/imports/ui/DateInput';
import { Modal, ModalStyled } from '/imports/ui/Modal';
import { ConfirmModal } from '/imports/ui/ConfirmModal';
import { AddressInput } from '/imports/ui/AddressInput';
import { DividerTabs } from '/imports/ui/DividerTabs';
import { AccountPicker } from '/imports/ui/AccountPicker';
import { PartyBulkManager } from '/imports/ui/PartyBulkManager';
import { ProjectManagementScheduleView } from '/imports/ui/ProjectManagementScheduleView';
import { Icon } from '/imports/ui/Icon';
import { FileInput } from '/imports/ui/FileInput';
import { FileViewerModal, fileIsViewable } from '/imports/ui/FileViewerModal';
import { DriveGalleryModal } from '/imports/ui/DriveGalleryModal';
import { driveExtOf, driveIsArchive } from '/imports/api/drive';
import { Checkbox } from '/imports/ui/Checkbox';
import { LoadingFlex } from '/imports/ui/LoadingFlex';
import { NotPermittedPage } from '/imports/ui/NotPermittedPage';
import { toastOnError } from '/imports/ui/utils';
import { useUser, useFindOne, useSubscribe, useTracker } from '/imports/hooks';
import { Publications } from '/imports/publications';
import {
  ProjectManagementProjects, ProjectManagementProjectEvents, ProjectManagementTasks, ProjectManagementDocuments, ProjectManagementDocumentVersions,
  ProjectManagementMilestones, ProjectManagementFundingSources, ProjectManagementCashEvents, ProjectManagementParameters, ProjectManagementStakeholders, ProjectManagementStakeholderProfiles, ProjectManagementProjectInvoices,
  ProjectManagementChangeOrders, ProjectManagementChangeOrder, projectManagementChangeOrderSummary, projectManagementChangeOrderNeedsIc, projectManagementChangeOrderIcThreshold,
  projectManagementSaveChangeOrder, projectManagementDeleteChangeOrder, projectManagementSetChangeOrderStatus, projectManagementSetProjectStatus, projectManagementProjectStatuses, projectManagementProjectStatus, ProjectManagementProjectStatus, projectManagementStatusNeedsReason, projectStatusLabel, projectManagementCanApproveInvoices, projectManagementCanSeeFees, projectManagementCanSeeFundingSources, projectManagementAddBudgetAttachment, ProjectManagementBudgetAttachmentKind, projectManagementHiddenBudgetKinds, projectManagementBudgetRefVisible,
  projectManagementSetMilestoneReview, projectManagementMilestoneReviewStatuses, projectManagementMilestoneReviewLabel, projectManagementMilestoneReviewBadge, ProjectManagementMilestoneReviewStatus,
  ProjectManagementPurchaseOrders, ProjectManagementPurchaseOrder,
  projectManagementSavePurchaseOrder, projectManagementDeletePurchaseOrder,
  ProjectManagementFundingSourceType, projectManagementFundingSourceTypes, projectManagementFundingSourceTypeLabel,
  ProjectManagementProject, ProjectManagementProjectDetail, ProjectManagementDocumentWithVersions, ProjectManagementDocumentVersion, ProjectManagementParameter, ProjectManagementProjectInvoice, projectManagementCanAccess, projectManagementIsAdmin, projectManagementCanApproveDocuments, projectManagementCanSeeFinances, projectManagementInvoiceNumberLabel,
  ProjectManagementFundingSource, ProjectManagementCashEvent, ProjectManagementStakeholder, ProjectManagementStakeholderProfile, ProjectManagementMilestone, ProjectManagementTask,
  ProjectManagementContacts, ProjectManagementContact, projectManagementSaveContact, projectManagementDeleteContact,
  ProjectManagementLinks, ProjectManagementLink, projectManagementSaveLink, projectManagementDeleteLink,
  ProjectManagementCashSuggestions, ProjectManagementCashSuggestion,
  ProjectManagementPortfolios,
  ProjectManagementOwners, ProjectManagementEpcs, ProjectManagementUtilities,
  projectManagementOwnerModelClass, projectManagementEpcModelClass, projectManagementUtilityModelClass,
  ProjectManagementProjectEvent, ProjectManagementEventKind,
  ProjectManagementParameterType, projectManagementParameterTypes, projectManagementTracksActual, projectManagementParameterFilled, projectManagementParameterIsNumeric, projectManagementParameterVariance, projectManagementParameterTypeLabel,
  ProjectManagementParameterCategory, projectManagementParameterCategories, projectManagementParameterCategoryLabel,
  projectManagementExternalIdLabel,
  projectManagementUpdateProject, projectManagementDeleteProject, projectManagementSetTaskStatus, projectManagementCreateDocument,
  projectManagementSetParameterOptional, projectManagementSetDocumentOptional,
  projectManagementDrawShare, projectManagementSetDrawSplit, projectManagementDevFeeShare, projectManagementDevFeeReleaseSplit, projectManagementSetDevFeeSplit, projectManagementSolarCostDrawSplit, projectManagementTotalSolarCostParameter, projectManagementCanSetOptional, projectManagementCanCreateProject,
  projectManagementAddDocumentVersion, projectManagementSetVersionApproval, projectManagementRevertVersion, projectManagementBatchUploadDocument,
  projectManagementSearchDocuments, projectManagementMakeUtilityAccountLink, projectManagementSaveMilestone, projectManagementDeleteMilestone, projectManagementSetMilestoneDate, projectManagementSaveTask, projectManagementDeleteTask, projectManagementSaveTaskDocument, projectManagementDeleteTaskDocument, projectManagementDecideSuggestions, projectManagementLlmParseDocument, ProjectManagementParameterSuggestion, ProjectManagementParameterSuggestions, projectManagementCreateInvoice, projectManagementUpdateInvoice, projectManagementDeleteInvoice, projectManagementDecideInvoice, projectManagementSetInvoiceAllocations, projectManagementParseInvoice,
  projectManagementFinancialSummary, projectManagementUsesLedger, projectManagementEpcContractDocType, projectManagementFundingSourcesEstimate, projectManagementFundingSourceTotals, projectManagementUseFees, projectManagementTotalFees, projectManagementCompleteProjectFees, projectManagementSaveFundingSource, projectManagementDeleteFundingSource,
  projectManagementSaveCashEvent, projectManagementDeleteCashEvent, projectManagementSaveParameter, projectManagementDeleteParameter,
  projectManagementParseModel, projectManagementApplyParsedModel, projectManagementUploadFinancialModel, ProjectManagementParsedModelRow,
  projectManagementSetTaskDate, projectManagementTaskDateAfterMilestone, projectManagementDayStart,
  projectManagementStakeholderDocTypes,
  projectManagementSaveStakeholder, projectManagementDeleteStakeholder,
  projectManagementSetTaskOwner, projectManagementMoney as money, projectManagementDocumentFileName,
  projectManagementDocumentGallery,
  ProjectManagementAiRead, projectManagementIsReadingWithAi,
  projectManagementParameterUnits, projectManagementFormatValue as formatValue,
  ProjectManagementTaskNotes, ProjectManagementTaskNote, projectManagementSaveTaskNote, projectManagementDeleteTaskNote, projectManagementCanEditTaskNote,
  projectManagementOwnersOf,
} from '/imports/api/projectManagement';
import { getProjectManagementLifecycle, projectManagementFeeUseCodes, projectManagementFinanceModel, projectManagementProjectDetailsHidden, projectManagementPtoEstimate, projectManagementSourcesFigure, projectManagementStageLabel } from '/imports/api/projectManagementLifecycles';
import { ProjectManagementMetricsBanner, projectManagementMetricDot } from '/imports/ui/ProjectManagementMetricsBanner';
import { Account, Accounts, accountDisplayName, isLuminiaUser } from '/imports/api/accounts';
import { fileHref, maxInvoiceUploadBytes, maxSpreadsheetUploadBytes } from '/imports/api/files';
import { ProjectManagementInvoiceReviewModal } from '/imports/ui/ProjectManagementInvoiceReviewModal';
import { PartyPicker } from '/imports/ui/ProjectManagementPartyPicker';
import { FileMeta } from '/imports/types';
import { UtilityAccounts } from '/imports/api/utilityAccounts';
import { ConsumptionMeters } from '/imports/api/consumptionMeters';
import { copyLinkToClipboard, copyToClipboard } from '/imports/utils';
import {
  InventoryDeliveries, InventoryPallets, InventorySites,
  inventoryAllocationStatus, inventoryAllocationStatusLabel, inventoryDeliveryStatusLabel, inventoryPalletsFor,
  inventorySiteModulesRequired,
} from '/imports/api/inventory';

/** Budget-tab money: to the cent, so the rows visibly add up to their totals (whole-dollar rounding of each
 *  row made the M0–M6 uses sum $2 short of the ledger total). The banner and other tabs keep whole dollars. */
const cents = (n?: number) => money(n, true);

const statusColor: { [k: string]: string } = {
  approved: css.green, pending: css.orange, rejected: css.red5, none: css.grey5, superseded: css.grey5,
  submitted: css.orange, underReview: css.teal1, accepted: css.green,
};

/** Timeline dot color, computed live (stored health goes stale between writes): green = complete;
 *  red = incomplete past its estimated date; yellow = incomplete and due within two weeks (matching
 *  the server's milestoneHealth window); grey = incomplete otherwise (incl. undated). */
const milestoneDotColor = (m: ProjectManagementMilestone): string => {
  if (m.actualDate) return css.green;
  if (!m.plannedDate) return css.grey5;
  const days = (new Date(m.plannedDate).getTime() - Date.now()) / 86400000;
  if (days < 0) return css.red2;
  if (days < 14) return css.orange;
  return css.grey5;
};

// The task card's styles, shared by the page block below and TaskCardModalStyled: the card renders
// inside a Modal, which portals to document.body where the page's scoped rules cannot reach. (The
// page still needs them too — e.g. the financial-model upload pill reuses .task-doc-upload.)
const taskCardStyles = `
  /* Each task is a card: an accent rail (green once done) plus its own border separates it from the
     next one. baseline: the label and the smaller milestone tag share a text baseline. */
  .task {
    display: flex; gap: 0.5rem; align-items: baseline; flex-wrap: wrap;
    padding: 0.5rem 0.7rem; margin-bottom: 0.4rem;
    border: 1px solid ${css.grey7}; border-left: 3px solid ${css.grey6}; border-radius: 0.5rem;
    background: ${css.white}; transition: border-color 0.12s, box-shadow 0.12s;
  }
  .task:hover { border-color: ${css.grey6}; border-left-color: ${css.grey5}; box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06); }
  .task.done { background: ${css.grey9}; border-left-color: ${css.green3}; }
  .task.done:hover { border-left-color: ${css.green2}; }
  .task > label { font-weight: 600; }
  /* flex-basis: 100% drops it onto its own line in the card's wrapping header row. The fill alone would
     all but vanish on a done card (grey9 on grey9), so the hairline border is what actually delimits it
     in both states. */
  .task .task-description {
    flex-basis: 100%; margin: 0.15rem 0 0.05rem; padding: 0.4rem 0.55rem;
    background: ${css.grey8}; border: 1px solid ${css.grey7}; border-radius: 0.35rem;
    color: ${css.grey3}; font-size: 0.82rem; line-height: 1.35;
  }
  .task input { transform: translateY(0.12rem); }  // checkboxes baseline-align at their bottom edge; nudge onto the text line
  .task .task-done-check { align-self: center; }   // read-only done check (members) — center on the row instead of baseline
  .task .role { color: ${css.primaryColor}; font-weight: 600; font-size: 0.8rem; }
  .task .task-ms { display: inline-flex; align-items: center; gap: 0.3rem; color: ${css.grey4}; font-size: 0.75rem; white-space: nowrap; }
  .task .task-ms .task-ms-dot { align-self: center; }
  .task .task-ms-dot { width: 0.5rem; height: 0.5rem; border-radius: 50%; flex: none; }
  .task .task-utility { display: inline-flex; align-items: center; gap: 0.4rem; }
  .task .task-utility .icon { cursor: pointer; }
  .task .task-utility svg { width: 1.1rem; height: 1.1rem; }
  .task .task-utility-status { color: ${css.grey4}; font-size: 0.75rem; white-space: nowrap; }
  /* Details as a real table: name | estimated | actual, so values line up down the columns and a
     missing actual reads as an empty cell rather than an absent suffix. */
  .task .task-params {
    flex-basis: 100%; margin: 0.35rem 0 0.2rem 1.5rem;
    display: grid; grid-template-columns: minmax(5rem, 1fr) minmax(4rem, 1fr) 1.1rem minmax(4rem, 1fr) minmax(4rem, 1fr) 4.5rem 1rem;
    gap: 0 0.75rem; align-items: baseline; font-size: 0.8rem;
  }
  .task .task-params.single-value {
    grid-template-columns: minmax(5rem, 1fr) minmax(4rem, 1fr) minmax(4rem, 1fr) 4.5rem 1rem;
  }
  .task .task-params .tp-notes { color: ${css.grey4}; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  /* Title and AI banner render as blocks BETWEEN the two grids, not as cells inside one — a grid-column
     span only works while they live in a grid, and they now sit outside both. */
  .task .task-params-title-block { flex-basis: 100%; margin-left: 1.5rem; }
  .task .tp-banner-block { flex-basis: 100%; margin-left: 1.5rem; }
  .task .task-params-title {
    grid-column: 1 / -1; flex-basis: 100%;
    color: ${css.grey3}; font-size: 0.72rem; font-weight: 600; text-transform: uppercase; letter-spacing: 0.06em;
    margin: 0.1rem 0 0.15rem;
  }
  /* Sits between the flex row and the docs GRID (a title inside the grid becomes a squeezed cell). */
  .task .task-docs-title { margin: 0.45rem 0 0.15rem 1.5rem; }
  .task .task-notes-title { margin: 0.45rem 0 0.15rem 1.5rem; }
  .task .task-notes { flex-basis: 100%; margin-left: 1.5rem; }
  .task .task-notes textarea { width: 100%; min-height: 2.6rem; font-size: 0.82rem; padding: 0.4rem 0.6rem; border: 1px solid ${css.grey6}; resize: vertical; }
  .task .task-notes textarea:focus { border-color: ${css.primaryColor}; outline: none; }
  /* The submit sits INSIDE the textarea's box (which is why the composer is relative and the textarea
     carries right padding), and only exists while there is something to submit. */
  .task .note-composer { position: relative; }
  .task .note-composer textarea { padding-right: 5rem; }
  .task .note-composer .note-submit { position: absolute; right: 0.4rem; bottom: 0.5rem; }
  /* flex-basis, like every other full-width block in this wrapping card: without it the feed sizes to its
     longest note instead of the modal. */
  .task .note-feed { flex-basis: 100%; margin: 0.4rem 0 0 1.5rem; max-height: 14rem; overflow-y: auto; }
  .task .note-feed .note { padding: 0.35rem 0; border-bottom: 1px solid ${css.grey8}; }
  .task .note-feed .note:last-child { border-bottom: none; }
  .task .note-feed .note-meta { display: flex; align-items: baseline; gap: 0.4rem; color: ${css.grey4}; font-size: 0.72rem; }
  .task .note-feed .note-meta .who { color: ${css.grey3}; font-weight: 600; }
  .task .note-feed .note-meta .note-actions { margin-left: auto; display: flex; gap: 0.3rem; align-items: baseline; }
  .task .note-feed .note-body { font-size: 0.82rem; color: ${css.grey2}; white-space: pre-wrap; overflow-wrap: anywhere; }
  .task .note-feed .note-edit textarea { width: 100%; font-size: 0.82rem; padding: 0.3rem 0.5rem; border: 1px solid ${css.grey6}; resize: vertical; }
  .task .note-feed .note-edit .note-edit-actions { display: flex; gap: 0.4rem; margin-top: 0.25rem; }
  .task .tp-head { display: contents; }
  /* The grid aligns on BASELINE, and the two empty header cells have no text to give them one — they
     fall back to their own (3px) bottom edge, leaving their underline 7px above the labelled ones.
     Aligning the header row on a common bottom edge fixes it without padding the spans with content. */
  .task .tp-head > span {
    color: ${css.grey4}; font-size: 0.68rem; text-transform: uppercase; letter-spacing: 0.05em;
    padding-bottom: 0.15rem; border-bottom: 1px solid ${css.grey7}; align-self: end;
  }
  /* A real row box, not display: contents, so the row can take a hover tint the way the Project Details
     table's rows do. subgrid keeps the cells on the parent's tracks, so the layout is byte-identical to
     the contents version — baseline alignment included. */
  .task .task-param { display: grid; grid-column: 1 / -1; grid-template-columns: subgrid; align-items: baseline; }
  .task .task-param:hover { background: ${css.grey8}; }
  .task .task-param > * { padding: 0.22rem 0; border-bottom: 1px solid ${css.grey8}; min-width: 0; }
  .task .task-param .tp-name { color: ${css.grey3}; overflow-wrap: anywhere; }
  /* The number replaces the bullet that used to mark each row — two markers on one line is noise.
     Tabular figures so 9. and 10. line their names up instead of shuffling them a pixel. */
  .task .item-no { color: ${css.grey4}; font-variant-numeric: tabular-nums; margin-right: 0.35rem; }
  .task .task-param .tp-est { color: ${css.grey2}; font-weight: 600; }
  .task .task-param .tp-act { color: ${css.grey2}; font-weight: 600; }
  /* An un-entered actual: a faint placeholder dash, visually a blank waiting to be filled. */
  .task .task-param .tp-missing { color: ${css.grey5}; font-weight: normal; }
  .task .task-param .tp-missing .link-btn { color: ${css.grey5}; font-weight: normal; }
  .task .task-param .tp-missing .link-btn:hover { color: ${css.primaryColor}; }
  .task .task-param .tp-tools { text-align: right; }
  /* Centred on its track, and the Checkbox's own trailing margin (there for a label this cell has none
     of) is backed out so it sits on the column's middle rather than left of it. Sized down from the
     component's 1.5rem to sit with the grid's 0.8rem text: BOTH dimensions, because the component's
     aspect-ratio only squares a box that has one definite side, and its own height would otherwise
     stand. The tick is a centred background at 70%, so it follows the box down by itself. */
  .task .tp-optional { text-align: center; }
  .task .tp-optional input { margin-right: 0; width: 1rem; height: 1rem; }
  .task .task-param .tp-promote { text-align: center; padding: 0; }
  /* AI review: a suggestion renders INSIDE the estimated cell, never as a cell of its own — it tints the
     column it would fill, and the grid keeps exactly four children per row. */
  .task .tp-banner { grid-column: 1 / -1; display: flex; align-items: center; gap: 0.4rem; font-size: 0.78rem; color: ${css.grey2}; background: ${css.lightBlue}; border-radius: 0.3rem; padding: 0.2rem 0.45rem; margin: 0.15rem 0; }
  .task .tp-banner .icon svg { width: 0.95rem; height: 0.95rem; }
  .task .tp-banner .sep { color: ${css.grey5}; }
  .task .task-param .tp-suggestion { display: flex; flex-wrap: wrap; align-items: baseline; gap: 0 0.3rem; color: ${css.primaryColor}; overflow-wrap: anywhere; }
  .task .task-param .tp-suggestion .link-btn { font-size: 0.9em; }
  .task .task-param .tp-suggestion .ok { color: ${css.green2}; font-weight: 600; }
  .task .task-param .tp-suggestion .no { color: ${css.grey4}; }
  .task .task-param .tp-suggested { background: ${css.lightBlue}; border-radius: 0.2rem; }
  /* Names open their editor but stay body-coloured — a table of blue links would be unreadable. */
  .task .name-btn { color: inherit; font: inherit; text-align: left; }
  .task .name-btn:hover { color: ${css.primaryColor}; text-decoration: underline; }
  /* Done reads as a (green) checkmark, not a strike-through — the latter reads as "forget it". */
  .task.done > label { color: ${css.grey4}; }
  .task.done > input[type=checkbox] { accent-color: ${css.green2}; }
  /* One shared grid so the badge / download / upload columns line up across all of a task's doc rows. */
  .task-docs { flex-basis: 100%; margin: 0 0 0.15rem 1.5rem; display: grid; grid-template-columns: 1rem minmax(8rem, 20rem) 6rem 4rem 4.5rem auto; gap: 0 0.6rem; align-items: center; font-size: 0.82rem; }
  .task-doc { display: contents; }
  .task-doc > * { padding: 0.25rem 0; border-bottom: 1px solid ${css.grey8}; }
  .task-doc .label { color: ${css.grey2}; }
  .task-doc .doc-badge { cursor: pointer; }
  .task-doc .cell { display: flex; align-items: center; }
  /* Sleek, compact upload affordance — reshapes FileInput's dashed drop-target into a small inline pill. */
  /* Pinned to the right of its track and sized to its content, so the wider "Drop to upload" label grows
     leftward into the label column's slack instead of out from under the status badge beside it. */
  .task-doc-upload { display: inline-flex; justify-self: end; width: max-content; }
  .task-doc-upload .target { border: none; background: ${css.grey6}; color: ${css.grey2}; border-radius: 1rem; padding: 0.28rem 0.6rem; display: inline-flex; align-items: center; gap: 0.3rem; font-size: 0.78rem; line-height: 1; cursor: pointer; white-space: nowrap; transition: background 0.1s; }
  .task-doc-upload .target:hover { background: ${css.grey5}; color: ${css.grey1}; }
  /* The pill has no border for a dashed outline to recolour, and the rule above outranks FileInput's own
     dragging style, so the drag feedback has to be restated here — as an outline, which costs no layout. */
  .task-doc-upload.dragging .target { background: ${css.lightBlue}; color: ${css.grey1}; outline: 2px dashed ${css.primaryColor}; outline-offset: 1px; }
  .task-doc-upload .target > div { line-height: 1; }
  .task-doc-upload .target .icon { margin: 0; }
  .task-doc-upload .target .icon svg { width: 0.85rem; height: 0.85rem; min-width: 0.85rem; }
  .task-owner { margin-left: auto; min-width: 10rem; }
  .task .task-actions { display: flex; gap: 0.3rem; align-items: center; }
  .task .task-actions .sep { color: ${css.grey5}; }
  .task .task-params-title .title-add { margin-left: 0.6rem; font-size: 0.72rem; text-transform: none; letter-spacing: 0; }
  .task .task-param .tp-del { color: ${css.grey4}; }
  .task-doc .task-doc-actions { display: flex; gap: 0.3rem; align-items: center; margin-left: 0.4rem; }
  .task-doc .task-doc-actions .sep { color: ${css.grey5}; }
  .task-owner-readonly { color: ${css.grey3}; font-size: 0.85rem; }
`;

const ProjectManagementProjectPageStyled = styled(Page)`
  /* Pinned while the page scrolls under them. Needs an opaque background (panel content passes
     beneath) and a z-index above it; the small negative top closes the hairline the tabs' own top
     border would otherwise show through. */
  .tab-area .main-tabs {
    position: sticky; top: -1px; z-index: 3;
    background: ${css.white}; padding-top: 0.25rem;
  }
  /* Eight tabs rarely fit on one row beside the 24rem rail, so they pack tighter than the strip's default. */
  .tab-area .main-tabs .btn { padding: 0.5rem 0.6rem; }
  /* The PAGE scrolls, not the panels. The body must not be a scroll container of its own or the sticky
     tabs would anchor to it and never move — .page is the scrollport, so this stays visible. */
  .page-body { overflow: visible; }
  .heading-buttons { display: flex; gap: 0.5rem; align-items: center; flex-wrap: wrap; justify-content: flex-end; }
  .heading-buttons .chat-btn { position: relative; }
  .heading-buttons .chat-badge {
    margin-left: 0.4rem; background: ${css.red2}; color: ${css.white};
    border-radius: 0.75rem; padding: 0.05rem 0.4rem; font-size: 0.75rem; font-weight: 700;
  }
  /* The grid grows to its content and the page scrolls past it — a panel is no longer a scrollport. */
  .pm-grid { display: flex; gap: 1.5rem; flex-wrap: wrap; }
  /* Heading is a row: logo, then the name and address stacked beside it. The TEXT COLUMN sets the
     height and the logo stretches to it, so the mark spans both lines rather than just the title. */
  .page-heading h1 { display: flex; align-items: stretch; gap: 0.6rem; }
  .page-heading h1 .title-text { display: flex; flex-direction: column; justify-content: center; min-width: 0; cursor: pointer; }
  /* height:auto + align-self:stretch lets flex set the height; the intrinsic aspect ratio then drives
     the width, so the logo keeps its proportions at whatever height the two lines come to. */
  /* max-width caps a wide wordmark from crowding the title; the stretched height still sets the
     size, and object-fit keeps the aspect ratio rather than squashing it into the cap. */
  .page-heading h1 .project-logo { align-self: stretch; height: auto; width: auto; max-width: 15rem; max-height: 8rem; flex: none; object-fit: contain; object-position: left center; }
  /* Its own line-height — the h1's is sized for the title and would leave a gap under the name. */
  .page-heading h1 .project-address { color: ${css.grey3}; font-size: 0.9rem; font-weight: normal; line-height: 1.2; }
  /* Placement only — the banner's own styling lives in ProjectManagementMetricsBanner. */
  .metrics-banner { margin-top: 1.375rem; }
  /* Meta facts and the onsite contact sit side by side; the contact is a fixed column so the meta block
     keeps every pixel it can for wrapping its items. */
  .meta-row { display: flex; align-items: stretch; gap: 0.9rem; margin: 0.25rem 0 0.9rem; }
  .meta-row .project-meta { flex: 1; margin: 0; min-width: 0; }
  .contact-card {
    flex: none; width: 15rem; box-sizing: border-box; padding: 0.65rem 1rem;
    border: ${css.inputBorder}; border-radius: 0.5rem; background: ${css.grey8};
    display: flex; flex-direction: column; gap: 0.15rem;
  }
  .contact-card .k { color: ${css.grey4}; font-size: 0.68rem; font-weight: 600; text-transform: uppercase; letter-spacing: 0.05em; }
  .contact-card .name { color: ${css.grey2}; font-size: 1.05rem; font-weight: 600; }
  .contact-card .line { color: ${css.grey3}; font-size: 0.85rem; }
  .contact-card a.line:hover { color: ${css.primaryColor}; }
  /* Email and phone share a row. Neither may wrap or the row becomes two, so the email — the one that
     can be arbitrarily long — takes the squeeze and ellipsises, while the phone keeps its full width:
     half a phone number is useless, half an email still identifies the person. */
  .contact-card .contact-lines { display: flex; align-items: baseline; gap: 0.6rem; min-width: 0; }
  .contact-card .contact-lines .line { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .contact-card .contact-lines .phone { flex: none; }
  ${css.mobileMedia} {
    /* The logo stretches to the title block's height, and on a narrow screen the title wraps to two or
       three lines — which made the mark balloon to ~90px and dominate the header. Cap it here; the
       desktop rule still ties it to the text height. */
    .page-heading h1 .project-logo { align-self: center; max-height: 2.6rem; max-width: 4rem; }
    /* No room for two columns; the contact drops under the meta block and spans the width. */
    .meta-row { flex-direction: column; }
    .contact-card { width: auto; }
  }
  .project-meta {
    display: flex; flex-wrap: wrap; gap: 0.5rem 2.25rem;
    margin: 0.25rem 0 0.9rem; padding: 0.65rem 1rem;
    border: ${css.inputBorder}; border-radius: 0.5rem; background: ${css.grey8};
  }
  .project-meta .meta-item { display: flex; flex-direction: column; gap: 0.1rem; }
  .project-meta .k { color: ${css.grey4}; font-size: 0.68rem; font-weight: 600; text-transform: uppercase; letter-spacing: 0.05em; }
  .project-meta .v { color: ${css.grey2}; font-size: 1.3rem; font-weight: 600; }
  /* Shared by the owner and EPC items — sized to the text rows so a logo doesn't change the header height. */
  .project-meta .v .meta-logo { display: block; height: 1.8rem; max-width: 13rem; object-fit: contain; object-position: left; }
  /* The reason under an On Hold / Canceled badge — quiet, and allowed to wrap unlike the values above it. */
  .project-meta .status-note { color: ${css.grey3}; font-size: 0.8rem; max-width: 18rem; white-space: normal; }
  /* Attention Now: what is late, then what lands next. Same card treatment as .project-meta so the two
     read as one header block rather than competing panels. */
  .attention {
    margin: 0 0 0.9rem; padding: 0.65rem 1rem;
    border: ${css.inputBorder}; border-radius: 0.5rem; background: ${css.grey9};
  }
  /* The head is the toggle: full-width button, but styled as a plain row so it reads as a header. */
  .attention .attention-head {
    display: flex; align-items: baseline; gap: 0.6rem; width: 100%;
    background: none; border: none; padding: 0; font: inherit; text-align: left; cursor: pointer;
  }
  .attention .attention-head .caret { color: ${css.grey4}; font-size: 0.75rem; }
  .attention .attention-head:hover .attention-title { color: ${css.primaryColor}; }
  /* Collapsed, this line IS the card — pushed right so it lands where the row timings do when open. */
  .attention .attention-summary { margin-left: auto; color: ${css.grey3}; font-size: 0.82rem; white-space: nowrap; }
  .attention .attention-summary .overdue { color: ${css.red1}; font-weight: 600; }
  .attention .attention-summary .sep { color: ${css.grey5}; margin: 0 0.3rem; }
  /* Only the OPEN card needs breathing room under the header. */
  .attention .attention-row:first-of-type { margin-top: 0.35rem; }
  .attention .attention-title { font-weight: 600; font-size: 0.9rem; color: ${css.grey1}; }
  .attention .attention-stage { color: ${css.grey4}; font-size: 0.78rem; }
  .attention .attention-row {
    display: flex; align-items: center; gap: 0.5rem;
    padding: 0.28rem 0; border-top: 1px solid ${css.grey7}; font-size: 0.85rem;
  }
  .attention .attention-row .dot { width: 0.5rem; height: 0.5rem; border-radius: 0.1rem; flex: none; background: ${css.grey5}; }
  .attention .attention-row.late .dot { background: ${css.red1}; }
  .attention .attention-row.soon .dot { background: ${css.orange}; }
  .attention .attention-row .label { font-weight: 600; color: ${css.grey1}; }
  .attention .attention-row .role { color: ${css.grey4}; font-size: 0.78rem; }
  /* Pushes the timing and the action to the right edge, so the column reads as a list of deadlines. */
  .attention .attention-row .when { margin-left: auto; color: ${css.grey3}; white-space: nowrap; }
  .attention .attention-row.late .when { color: ${css.red1}; font-weight: 600; }
  /* Tabs on the left, milestones timeline pinned right of them on every tab. */
  .content-row { display: flex; gap: 1.5rem; }
  /* overflow must stay visible: any clipping ancestor between .main-tabs and the scrollport kills the
     sticky, and it is what used to bound the panels. */
  .content-row > .tab-area { flex: 1; min-width: 0; display: flex; flex-direction: column; }
  /* Sidebar: pinned while the page scrolls past it — title + search fixed, only the timeline scrolls.
     align-self is what makes the sticky work: a flex item stretches to the row height by default, and
     an element as tall as its container has nothing to stick within. The max-height then bounds it to
     the viewport so a long milestone list still reaches its end. */
  .timeline-side {
    width: 24rem; flex: none; display: flex; flex-direction: column; overflow: hidden; padding-right: 0.25rem;
    position: sticky; top: 0; align-self: flex-start; max-height: calc(100vh - ${css.appHeaderHeight});
  }
  .timeline-side .panel { display: flex; flex-direction: column; min-height: 0; }
  .timeline-side .timeline { flex: 1; min-height: 0; overflow-y: auto; }
  /* Beside the fixed sidebar there is no room for a 24rem rail AND a usable tab pane, so the milestones
     drop under the tabs and take the full width; the sticky is moot once the rail is in the flow. */
  ${css.compactMedia} {
    .content-row { flex-direction: column; }
    .timeline-side { width: auto; position: static; max-height: none; padding-right: 0; }
  }
  .panel {
    padding: 1rem 0;
    flex: 1;
    min-width: 22rem;
    &.divider {
      padding-right: 1.5rem;
    }
    .panel-title { font-weight: bold; margin-bottom: 0.75rem; display: flex; align-items: center; gap: 0.4rem; }
  }
  /* Nested sub-tab bar inside the Delivery tab — lighter pill style vs the main divider tab row. */
  .badge {
    display: inline-block;
    border-radius: 1rem;
    padding: 0.1rem 0.6rem;
    font-size: 0.8rem;
    color: ${css.white};
  }
  .meta-row { color: ${css.grey3}; font-size: 0.9rem; margin: 0.1rem 0; }
  ${taskCardStyles}
  /* Wizard-style bucket strip: one step per role bucket; the selected step drives the table below.
     Circles and connectors fill as buckets complete. */
  .bucket-strip { flex: none; display: flex; align-items: flex-start; overflow-x: auto; padding: 0.4rem 0.25rem 0.35rem; margin-bottom: 0.5rem; border-bottom: 1px solid ${css.grey7}; }
  /* Sits under the count, quieter than it — the date is context for a finished bucket, not its headline. */
  .bucket-strip .step-done-date { font-size: 0.7rem; color: ${css.grey4}; white-space: nowrap; }
  .bucket-step { display: flex; align-items: flex-start; flex: 1; min-width: 0; }
  .bucket-step:last-child { flex: 0 0 auto; }
  .bucket-step .step-col { display: flex; flex-direction: column; align-items: center; gap: 0.25rem; min-width: 6.5rem; background: none; border: none; padding: 0; cursor: pointer; font-family: inherit; }
  .bucket-step .step-circle { width: 2rem; height: 2rem; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-weight: 700; font-size: 0.85rem; background: ${css.grey7}; color: ${css.grey3}; }
  .bucket-step .step-circle svg { width: 1.1rem; height: 1.1rem; }
  .bucket-step.complete .step-circle { background: ${css.green2}; color: ${css.white}; }
  .bucket-step.selected .step-circle { background: ${css.primaryColor}; color: ${css.white}; box-shadow: 0 0 0 3px ${css.lightBlue}; }
  .bucket-step .step-label { font-size: 0.72rem; font-weight: 600; color: ${css.grey4}; white-space: nowrap; }
  .bucket-step.selected .step-label { color: ${css.grey1}; }
  .bucket-step .step-count { font-size: 0.72rem; color: ${css.grey4}; }
  .bucket-step .step-line { flex: 1; height: 2px; margin: 1rem 0.2rem 0; min-width: 1rem; background: ${css.grey7}; }
  .bucket-step.complete .step-line { background: ${css.green2}; }
  /* Compact per-bucket task table; the whole row is the click target, the card opens in a modal. */
  .task-table tbody tr { cursor: pointer; }
  .task-table .status-badge.done { background: ${css.green2}; }
  .task-table .status-badge.open { background: ${css.grey5}; }
  .task-table td.completion.complete { color: ${css.green2}; font-weight: 600; }
  /* The card's .task-ms rules are scoped to .task, which the table rows sit outside; the cell keeps
     the table's own type and just borrows the dot. */
  .task-table .task-ms { display: inline-flex; align-items: center; gap: 0.35rem; white-space: nowrap; }
  .task-table .task-ms-dot { width: 0.5rem; height: 0.5rem; border-radius: 50%; flex: none; }
  .doc {
    border-top: 1px solid ${css.grey6};
    padding: 0.6rem 0;
  }
  .docs-hint { color: ${css.grey3}; font-size: 0.85rem; margin: 0.25rem 0 0.5rem; }
  .doc .doc-head { display: flex; gap: 0.5rem; align-items: baseline; }
  .doc .doc-label { font-weight: 600; }
  .doc .req { font-size: 0.75rem; color: ${css.grey4}; }
  .doc .current { font-size: 0.85rem; margin: 0.3rem 0; }
  .doc .reject-reason { font-size: 0.82rem; color: ${css.red5}; margin: 0.2rem 0; overflow-wrap: anywhere; }
  .doc .versions { margin: 0.3rem 0 0 0.5rem; border-left: 2px solid ${css.grey6}; padding-left: 0.6rem; }
  .doc .version { font-size: 0.82rem; display: flex; gap: 0.5rem; align-items: center; padding: 0.15rem 0; flex-wrap: wrap; }
  .doc .actions { display: flex; gap: 0.4rem; margin-top: 0.3rem; flex-wrap: wrap; }
  .doc .uploader { margin-top: 0.3rem; max-width: 18rem; }
  .model-icon { display: inline-flex; vertical-align: text-bottom; margin-left: 0.3rem; }
  .model-icon svg { width: 1.1rem; height: 1.1rem; }
  /* Milestones as a vertical timeline: rail + dots down the left, dates driving the sequence. */
  .timeline { position: relative; margin-top: 0.25rem; }
  .tl-item { position: relative; padding: 0 0 1rem 1.7rem; }
  .tl-item::before { content: ''; position: absolute; left: 0.36rem; top: 0.5rem; bottom: -0.35rem; width: 2px; background: ${css.grey6}; }
  .tl-item:last-child::before { display: none; }
  .tl-dot { position: absolute; left: 0; top: 0.3rem; width: 0.8rem; height: 0.8rem; border-radius: 50%; border: 2px solid #FFF; box-shadow: 0 0 0 1px ${css.grey6}; }
  .tl-date { color: ${css.grey4}; font-size: 0.8rem; white-space: nowrap; }
  .tl-label { font-weight: 600; font-size: 0.85rem; }
  .tl-label .pay-icon, .tl-label .ms-done { display: inline-flex; vertical-align: text-bottom; margin-left: 0.3rem; }
  .tl-label .pay-icon svg, .tl-label .ms-done svg { width: 1.25rem; height: 1.25rem; }
  .tl-actions { font-size: 0.8rem; }
  .timeline.compact .tl-item { padding-bottom: 0.4rem; }
  .timeline.compact .tl-item::before { bottom: -0.1rem; }
  .timeline.compact .tl-body { display: flex; align-items: baseline; gap: 0.6rem; }
  .timeline.compact .tl-label { flex: 1; }
  .task-filters > .add-task { flex: 0 0 auto; }
  .finances { flex-basis: 100%; }
  /* Budget header: Funding Sources and Gross Fees side by side. Wraps to one column when the pane is
     narrow — the Budget tab shares its width with the milestone rail, so it is often not wide. The
     basis is what a cash table needs to read without a squeeze; a panel that still ends up narrower
     than its table scrolls it sideways rather than widening the page. */
  .fin-panels { display: flex; gap: 1.5rem; flex-wrap: wrap; align-items: flex-start; }
  .fin-panel { flex: 1 1 30rem; min-width: 0; overflow-x: auto; }
  /* Sources and Uses carry seven columns that need ~590px at their tightest, so the pair only sits side
     by side when each gets that much; below it they stack rather than scroll. */
  .fin-panels.cash .fin-panel { flex-basis: 37rem; }
  /* The panels ARE the top of the tab; the section titles inside them should not re-add top margin. */
  .fin-panel > .fin-section-title:first-child { margin-top: 0; }
  .fin-table .draw-share { margin-left: 0.5rem; color: ${css.grey4}; font-size: 0.78rem; }
  /* Budget tables read as separate cards: without an outline the sections ran together, since every
     table already draws its own row rules. Scoped to .budget — .finances is shared with the Project
     Details tab, whose parameter tables are grouped differently and should stay flat. */
  .budget .fin-table {
    border: 1px solid ${css.grey6}; border-radius: 0.4rem;
    border-collapse: separate; border-spacing: 0; overflow: hidden; background: ${css.white};
  }
  .budget .fin-table thead th { background: ${css.grey9}; color: ${css.grey3}; font-size: 0.78rem; }
  /* border-collapse: separate means each cell draws its own rule — bottom only, and never on the last
     row, so the row rules do not double up against the table's own border. */
  .budget .fin-table th, .budget .fin-table td { border-bottom: 1px solid ${css.grey7}; }
  .budget .fin-table tbody tr:last-child td { border-bottom: none; }
  /* Breathing room between cards, so the outlines are what separates them rather than sheer proximity. */
  .budget .fin-table { margin-bottom: 1rem; }
  .budget .fin-panel > .fin-table:last-child { margin-bottom: 0; }
  /* A fee figure's footnote — how it was built, or what change orders have taken from it. */
  .budget .fin-table .sub { display: block; color: ${css.grey4}; font-size: 0.72rem; font-weight: normal; white-space: normal; }
  /* Documents tab: Milestone → Task → Documents, each level its own accordion head. Rows are a grid so
     the badge, download and version columns line up across every group regardless of nesting depth. */
  .doc-tree .tree-head {
    display: flex; align-items: center; gap: 0.5rem; width: 100%;
    background: none; border: none; border-bottom: 1px solid ${css.grey7};
    padding: 0.45rem 0.25rem; font: inherit; text-align: left; cursor: pointer;
  }
  .doc-tree .tree-head .caret { flex: none; width: 0.7rem; color: ${css.grey4}; font-size: 0.7rem; }
  .doc-tree .tree-head:hover .tree-label { color: ${css.primaryColor}; }
  .doc-tree .tree-head.stage .tree-label { font-weight: 600; color: ${css.grey1}; }
  .doc-tree .tree-count { margin-left: auto; color: ${css.grey4}; font-size: 0.78rem; }
  .doc-tree .doc-row {
    display: grid; grid-template-columns: 1fr 7rem 6rem 2.5rem; gap: 0.5rem; align-items: center;
    padding: 0.3rem 0.25rem 0.3rem 3.2rem; border-bottom: 1px solid ${css.grey8}; font-size: 0.88rem;
  }
  .doc-tree .doc-row .num { text-align: right; color: ${css.grey4}; font-size: 0.78rem; }
  .doc-tree .doc-row .meta-row { font-size: 0.78rem; }
  .doc-tree .doc-row .doc-task { color: ${css.grey4}; font-size: 0.78rem; }
  .doc-tree .doc-row .doc-task::before { content: '·'; margin: 0 0.4rem; color: ${css.grey5}; }
  .tree-empty { padding: 0.6rem 0.25rem; }
  .fin-section-title { font-weight: 600; font-size: 1rem; color: ${css.primaryColor}; margin: 0.6rem 0 0.25rem; display: flex; align-items: center; gap: 0.5rem; flex-wrap: wrap; }
  .fin-section-title .disabled-tip { display: inline-flex; }
  .fin-section-title .disabled-tip > button:disabled { pointer-events: none; }
  .fin-section-title .qb-suggestions-badge { background: ${css.primaryColor}; color: ${css.white}; border-radius: 0.75rem; padding: 0.05rem 0.45rem; font-size: 0.75rem; font-weight: 700; line-height: 1.4; cursor: pointer; }
  .fin-section-title .qb-suggestions-badge:hover { filter: brightness(1.1); }

  /* An upload is read by the LLM off the request path, so the row carries the only sign that anything is
     still happening. Three dots rather than a spinner: it sits inline with text, at text size. */
  .ai-read { display: inline-flex; align-items: center; gap: 0.3rem; color: ${css.grey4}; font-size: 0.78rem; white-space: nowrap; }
  .ai-read .dots { display: inline-flex; gap: 0.15rem; }
  .ai-read .dots i { width: 0.28rem; height: 0.28rem; border-radius: 50%; background: ${css.primaryColor}; animation: ai-read-bounce 1.4s infinite ease-in-out both; }
  .ai-read .dots i:nth-child(1) { animation-delay: -0.32s; }
  .ai-read .dots i:nth-child(2) { animation-delay: -0.16s; }
  .ai-read.failed { color: ${css.red5}; }
  @keyframes ai-read-bounce { 0%, 80%, 100% { transform: scale(0); } 40% { transform: scale(1); } }
  @media (prefers-reduced-motion: reduce) { .ai-read .dots i { animation: none; transform: scale(1); } }
  .budget-milestone-filter { margin-top: 1rem; select { min-width: 14rem; } }
  /* A tab panel is a tools row over its body; both now sit in the page's own scroll flow. */
  .pm-grid > .panel { display: flex; flex-direction: column; box-sizing: border-box; }
  .panel > .panel-tools {
    flex: none; display: flex; align-items: center; gap: 0.5rem; width: 100%;
    margin-bottom: 0.6rem; padding-bottom: 0.6rem; border-bottom: 1px solid ${css.grey7};
  }
  .panel > .panel-tools > .param-search { flex: 1; max-width: none; margin: 0; }
  .panel > .panel-scroll { padding-right: 0.25rem; }
  .param-search { max-width: 22rem; margin: 0.25rem 0 0.75rem; }
  .param-group { margin-bottom: 1.25rem; }
  .param-group .fin-section-title { border-bottom: 1px solid ${css.grey6}; padding-bottom: 0.25rem; }
  /* Fixed layout so the parameter tables space their columns evenly across the width (the Notes/Status
     column no longer crowds the one before it) rather than each column sizing to its own content — which
     also lines the value columns up across categories. The tiny promote arrow and the actions column are
     sized to fit; the remaining columns split the rest evenly. */
  .param-group .fin-table { table-layout: fixed; }
  .param-group .fin-table .promote-col { width: 1.75rem; }
  .param-group .fin-table th:last-child, .param-group .fin-table td:last-child { width: 10.5rem; white-space: nowrap; }
  .param-group .fin-table td, .param-group .fin-table th { overflow: hidden; text-overflow: ellipsis; }
  /* Centered: the columns before it are right-aligned numbers, so a left-aligned Notes value would butt
     right against them. Centering gives it comfortable space on both sides, matching their rhythm. */
  .param-group .fin-table .pd-notes { color: ${css.grey3}; text-align: center; }
  .task-filters > * { flex: 1; }
  /* The Input's wrapper span flexes, but the inner <input> keeps its browser-default width without this. */
  .task-filters input { width: 100%; box-sizing: border-box; }
  .fin-table { width: 100%; border-collapse: collapse; font-size: 0.85rem; }
  .fin-table th, .fin-table td { text-align: left; padding: 0.25rem 0.5rem; border-bottom: 1px solid ${css.grey6}; }
  .fin-table tbody tr:hover { background: ${css.grey8}; }
  /* A Budget row is the whole affordance now — it opens the record's modal, which is where every action
     that used to sit in a trailing column of links lives. */
  .fin-table tbody tr.br-row { cursor: pointer; }
  /* A change order over the lifecycle's IC threshold. The colour is the whole indicator, so it carries a
     dotted underline too — red alone is invisible to a reader who cannot see it, and the underline is what
     says "there is something to hover here". */
  .needs-ic { color: ${css.red2}; font-weight: 600; text-decoration: underline dotted; text-underline-offset: 0.15rem; cursor: help; }
  .fin-table th.num, .fin-table td.num { text-align: right; }
  /* Muted invoice-uploader cell — colour only, so the <td> stays a table-cell and its border-bottom stays
     aligned (the shared .meta-row class sets display:flex, which would drop the cell out of the row). */
  .fin-table td.invoice-uploader { color: ${css.grey3}; font-size: 0.9rem; }
  .co-uploader { max-width: 12rem; margin-top: 0.2rem; }
  .transition { font-size: 0.85rem; padding: 0.25rem 0; border-top: 1px solid ${css.grey6}; }
  .transition .when { color: ${css.grey4}; }
  .transition .hide-kind { float: right; cursor: pointer; color: ${css.grey5}; opacity: 0; transition: opacity 0.1s; }
  .transition:hover .hide-kind { opacity: 1; }  /* mute affordance appears on row hover */
  .transition .hide-kind:hover { color: ${css.grey2}; }
  .hidden-kinds { display: flex; align-items: center; flex-wrap: wrap; gap: 0.4rem; padding: 0.3rem 0 0.5rem; font-size: 0.82rem; }
  .hidden-kinds .label { color: ${css.grey3}; }
  .hidden-kinds .chip { display: inline-flex; align-items: center; gap: 0.2rem; background: ${css.grey6}; color: ${css.grey1}; border: none; font: inherit; border-radius: 1rem; padding: 0.1rem 0.5rem; cursor: pointer; }
  .hidden-kinds .chip:hover { background: ${css.grey5}; }
  .hidden-kinds .link { color: ${css.primaryColor}; background: none; border: none; font: inherit; cursor: pointer; margin-left: 0.2rem; }
  a.dl { color: ${css.primaryColor}; cursor: pointer; }
  .link-btn { background: none; border: none; color: ${css.primaryColor}; cursor: pointer; padding: 0; font-size: 0.82rem; }
  /* Copy-the-estimate-across arrow: quiet until pointed at, so a whole column of them doesn't compete
     with the values either side. */
  .link-btn.promote { color: ${css.grey4}; font-size: 1rem; line-height: 1; }
  .link-btn.promote:hover { color: ${css.primaryColor}; }
  .fin-table th.promote-col, .fin-table td.promote-col { width: 1.6rem; text-align: center; padding: 0.25rem 0; }
  .sep { color: ${css.grey5}; margin: 0 0.35rem; }
  .onsite-contact { font-size: 0.9rem; margin: 0.25rem 0 1rem; }
`;

// Attention Now stays a summary: past this many rows it competes with the tabs below it.
const ATTENTION_ROWS = 6;

const WideModalStyled = styled(ModalStyled)`
  .modal-window { width: 58rem; max-width: 100%; }
  .bulk-section { margin-top: 1.25rem; padding-top: 1rem; border-top: 1px solid ${css.grey6}; }
  .bulk-section .bulk-title { font-weight: 600; color: ${css.grey2}; margin-bottom: 0.5rem; }
  .type-row { display: flex; gap: 0.5rem; align-items: center; padding: 0.15rem 0; cursor: pointer; }
  /* Two columns of inputs: the form runs long once every logo has a field, and a single column pushed
     the buttons off-screen. Collapses to one column on a narrow viewport. */
  .add-fields { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 0.75rem 1.25rem; align-items: start; }
  .add-fields > .span2 { grid-column: 1 / -1; }
  ${css.mobileMedia} { .add-fields { grid-template-columns: minmax(0, 1fr); } }
  .add-fields .input { display: block; width: 100%; }
  .add-fields .input input { width: 100%; }
  .meta-row { color: ${css.grey3}; font-size: 0.9rem; margin: 0.1rem 0; }
  /* .fin-table is scoped to the page block for the same reason — carried in for the parsed-model review. */
  .fin-table { width: 100%; border-collapse: collapse; font-size: 0.85rem; }
  .fin-table th, .fin-table td { text-align: left; padding: 0.25rem 0.5rem; border-bottom: 1px solid ${css.grey6}; }
  .fin-table tbody tr:hover { background: ${css.grey8}; }
  .fin-table th.num, .fin-table td.num { text-align: right; }
  /* A row the reviewer unticked stays legible but visibly out of the write. */
  .parsed-model tr.unpicked td { opacity: 0.45; }
  /* Beats the .meta-row rule above, whose vertical-only margin would butt the badge against the name. */
  .parsed-model .meta-row { display: inline; margin-left: 0.4rem; }
  .attachment-row { display: flex; gap: 0.5rem; align-items: center; padding: 0.2rem 0; }
  .attachment-row .attachment-label { font-weight: 600; }
  .attachment-row .doc-badge { cursor: pointer; }
  .attachment-uploader { margin-top: 0.75rem; max-width: 18rem; }
  .alloc-cell { display: inline-flex; align-items: center; gap: 0.4rem; }
  .alloc-cell .alloc-fill { cursor: pointer; }
  .alloc-cell .alloc-fill:hover svg { opacity: 0.75; }
  .modal-buttons { display: flex; gap: 1rem; justify-content: flex-end; margin-top: 1rem; }
`;

// How Sources and Uses are ordered wherever they are listed: by description. The allocation modal shows
// the same Uses as the Uses table and has to agree with it — reading the two side by side is the whole
// job — so both sort through this rather than each choosing for itself. Without it the modal inherited
// the publication's order (paidDate, then createdAt) and the two lists disagreed.
const byCashEventLabel = (a: { label?: string }, b: { label?: string }) => (a.label || '').localeCompare(b.label || '');

// The five kinds of Budget record the consolidated modal shows.
type BudgetRecordKind = 'fundingSource' | 'cashEvent' | 'changeOrder' | 'purchaseOrder' | 'invoice';

// Field names as they appear in a history diff. One map over all five kinds: the names do not collide,
// and a field with no entry falls back to its raw name rather than disappearing.
const budgetFieldLabel: { [field: string]: string } = {
  label: strings.Description(), title: strings.Description(), milestoneKey: strings.Milestone(),
  estimatedAmount: strings.Estimated(), actualAmount: strings.Actual(), value: strings.Value(), drawShare: strings.ShareOfTotalSolarCost(), devFeeShare: strings.ShareOfDevFee(),
  invoicedDate: strings.Invoiced(), paidDate: strings.Paid(), reference: strings.Reference(),
  payee: strings.Payee(), status: strings.Status(), rationale: strings.Rationale(),
  fundingSourceId: strings.FundingSources(), orderDate: strings.OrderDate(),
  equipmentType: strings.EquipmentType(), serials: strings.Serials(), supplierStakeholderId: strings.Supplier(),
  note: strings.Note(), party: strings.Party(), type: strings.Type(),
};

/** A history diff's value, formatted by what the field holds — money as money, dates as dates. */
const budgetChangeValue = (field: string, v?: string | number | boolean | Date): string => {
  if (v == null || v === '') return '—';
  if (field === 'estimatedAmount' || field === 'actualAmount' || field === 'value') {
    return typeof v === 'number' ? money(v, true) : String(v);
  }
  if (v instanceof Date) return formatCalendarDate(v);   // paid / invoiced / order dates are calendar days
  return typeof v === 'number' ? v.toLocaleString() : String(v);
};

// Every Budget record opens here — funding source, cash source/use, change order, purchase order or
// invoice. It replaced a row of links per table, each opening a modal of its own.
const BudgetRecordModalStyled = styled(ModalStyled)`
  .modal-window { width: 66rem; max-width: 96vw; }
  .br-layout { display: flex; align-items: stretch; gap: 1.25rem; min-height: 18rem; }
  .br-main { flex: 1 1 auto; min-width: 0; }
  /* Absolutely positioned contents, so the history's own length never stretches the row — the record's
     detail sets the height and the feed scrolls inside it. Same arrangement as the task card's panel. */
  .br-side { position: relative; flex: 0 0 20rem; border-left: 1px solid ${css.grey6}; }
  .br-side .br-side-inner { position: absolute; inset: 0 0 0 1rem; display: flex; flex-direction: column; }
  .br-side .br-scroll { flex: 1 1 0; min-height: 0; overflow-y: auto; padding-right: 0.25rem; }
  .br-title { color: ${css.grey3}; font-size: 0.72rem; font-weight: 600; text-transform: uppercase; letter-spacing: 0.06em; margin: 0 0 0.35rem; }
  .br-main .br-title { margin-top: 1.1rem; }
  .br-main .br-title:first-child { margin-top: 0; }
  .br-fields { display: grid; grid-template-columns: max-content minmax(0, 1fr); gap: 0.25rem 1.25rem; font-size: 0.88rem; }
  .br-fields .k { color: ${css.grey3}; }
  .br-fields .v { color: ${css.grey1}; overflow-wrap: anywhere; }
  .br-actions { display: flex; flex-wrap: wrap; align-items: center; gap: 0.5rem; }
  .br-empty { color: ${css.grey4}; font-size: 0.85rem; }
  .br-related-row { display: flex; align-items: baseline; gap: 0.75rem; padding: 0.2rem 0; border-bottom: 1px solid ${css.grey8}; font-size: 0.88rem; }
  .br-related-row:last-child { border-bottom: none; }
  .br-related-row .amount { margin-left: auto; color: ${css.grey2}; font-variant-numeric: tabular-nums; }
  .attachment-row { display: flex; gap: 0.5rem; align-items: center; padding: 0.2rem 0; }
  .attachment-row .attachment-label { font-weight: 600; }
  .attachment-row .doc-badge { cursor: pointer; }
  .attachment-uploader { margin-top: 0.5rem; max-width: 18rem; }
  .transition { font-size: 0.85rem; padding: 0.25rem 0; border-top: 1px solid ${css.grey6}; }
  .transition:first-child { border-top: none; }
  .transition .when { color: ${css.grey4}; font-size: 0.78rem; }
  .br-change { display: flex; align-items: baseline; flex-wrap: wrap; gap: 0 0.35rem; font-size: 0.78rem; }
  .br-change .field { color: ${css.grey3}; }
  .br-change .from { color: ${css.grey4}; }
  .br-change .arrow { color: ${css.grey5}; }
  .br-change .to { color: ${css.grey1}; font-weight: 600; }
  .link-btn { background: none; border: none; color: ${css.primaryColor}; cursor: pointer; padding: 0; font-size: 0.85rem; }
  a.dl { color: ${css.primaryColor}; cursor: pointer; }
  .sep { color: ${css.grey5}; }
  .meta-row { color: ${css.grey3}; font-size: 0.9rem; margin: 0.1rem 0; }
  ${css.mobileMedia} {
    .modal-window { width: 100%; }
    .br-layout { flex-direction: column; min-height: 0; }
    .br-side { position: static; flex: none; border-left: none; border-top: 1px solid ${css.grey6}; padding-top: 0.75rem; }
    .br-side .br-side-inner { position: static; }
    .br-side .br-scroll { max-height: 16rem; }
  }
`;

// The full task card in a modal: the card styles ride in via taskCardStyles, plus copies of the page's
// small shared rules the card leans on (the .badge pill now lives on the base ModalStyled).
const TaskCardModalStyled = styled(ModalStyled)`
  .modal-window { width: 92rem; max-width: 96vw; }
  ${taskCardStyles}
  /* Two columns: the card, and the task's slice of History pinned beside it. The panel's contents are
     absolutely positioned so their own length never stretches the row — the card sets the height and the
     panel scrolls inside it, which is the point of the split. The floor stops a one-line task collapsing
     the panel to nothing. The feed's styles are re-declared here because a portaled modal never sees the
     page wrapper's scoped rules. */
  .task-modal-layout { display: flex; align-items: stretch; gap: 1.25rem; min-height: 18rem; }
  .task-modal-main { flex: 1 1 auto; min-width: 0; }
  .task-history { position: relative; flex: 0 0 20rem; border-left: 1px solid ${css.grey6}; }
  .task-history .history-inner { position: absolute; inset: 0 0 0 1rem; display: flex; flex-direction: column; }
  .task-history .history-title { font-weight: 600; color: ${css.grey2}; margin-bottom: 0.4rem; }
  .task-history .history-scroll { flex: 1 1 0; min-height: 0; overflow-y: auto; padding-right: 0.25rem; }
  .task-history .transition { font-size: 0.85rem; padding: 0.25rem 0; border-top: 1px solid ${css.grey6}; }
  .task-history .transition:first-child { border-top: none; }
  .task-history .when { color: ${css.grey4}; font-size: 0.78rem; }
  ${css.mobileMedia} {
    .modal-window { width: 100%; }
    .task-modal-layout { flex-direction: column; min-height: 0; }
    .task-history { position: static; flex: none; border-left: none; border-top: 1px solid ${css.grey6}; padding-top: 0.75rem; }
    .task-history .history-inner { position: static; }
    .task-history .history-scroll { max-height: 16rem; }
  }
  .meta-row { color: ${css.grey3}; font-size: 0.9rem; margin: 0.1rem 0; }
  a.dl { color: ${css.primaryColor}; cursor: pointer; }
  .link-btn { background: none; border: none; color: ${css.primaryColor}; cursor: pointer; padding: 0; font-size: 0.82rem; }
  .link-btn.promote { color: ${css.grey4}; font-size: 1rem; line-height: 1; }
  .link-btn.promote:hover { color: ${css.primaryColor}; }
  .sep { color: ${css.grey5}; margin: 0 0.35rem; }
`;

// Was a LOCAL-components reader, paired with a local-midnight writer below — its own convention, against
// the UTC-midnight one every other date in the module uses and 100% of the stored data is in. West of UTC
// the picker therefore opened on the day BEFORE the stored one, and saving without touching it wrote that
// day back: a task date walking backwards one edit at a time. Both ends now read and write UTC.
const dateInputValue = calendarDateInputValue;

const coStatusLabel: { [k in ProjectManagementChangeOrder['status']]: string } = {
  submitted: strings.Submitted(), underReview: strings.UnderReview(), accepted: strings.Accepted(), rejected: strings.Rejected(),
};

/** Colored status pill. */
const Badge = (props: { status: string; label: string }) => (
  <span className="badge" style={{ background: statusColor[props.status] || css.grey5 }}>{props.label}</span>
);

/**
 * The arrow sitting between the Estimated and Actual columns: one click copies the estimate across.
 * Renders nothing unless there is an estimate to copy and the actual is still empty — but the CELL it
 * lives in is always emitted by the caller, because both hosts (a table row, and a `display: contents`
 * grid row) count on a fixed number of children per row.
 */
const PromoteToActual = (props: { estimated: boolean; actual: boolean; onClick: () => void }) => (
  !props.estimated || props.actual ? null : (
    <button
      className="link-btn promote"
      data-tooltip-id="pm-task-tip"
      data-tooltip-content={strings.PromoteToActual()}
      onClick={props.onClick}
    >
      →
    </button>
  )
);

interface PromptField { key: string; label: string; type?: 'text' | 'select' | 'account' | 'date' | 'combo' | 'phone' | 'currency'; options?: { value: string; label: string }[]; default?: string; max?: string; required?: boolean }
interface FieldsPrompt { title: string; fields: PromptField[]; submit: (values: Record<string, string>) => void; submitText?: string }

const ComboInputStyled = styled.div`
  position: relative;
  input { width: 100%; }
`;

// The options list is portaled to document.body so the modal's `overflow: auto` can't clip it; it is
// placed over the input from a viewport rect (position: fixed) and re-placed on scroll/resize.
const ComboOptionsStyled = styled.div`
  position: fixed;
  z-index: 1000;
  background: #FFF;
  color: ${css.grey2};
  border: ${css.inputBorder};
  border-radius: ${css.inputBorderRadius};
  max-height: 14rem;
  overflow: auto;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.18);
  .option {
    padding: 0.7rem 0.5rem;
    cursor: pointer;
    &:hover { color: ${css.primaryColor}; background: ${css.grey8}; }
  }
`;

/** A searchable picklist that also accepts free-form entry — styled like RichSelect's options panel. The
 *  options render in a portal (not clipped by a modal's overflow), positioned over the input. */
const ComboInput = (props: { value?: string; options: { value: string; label: string }[]; onChange: (v: string) => void }) => {
  const [open, setOpen] = useState(false);
  const [rect, setRect] = useState<{ top: number; left: number; width: number } | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const place = () => {
    const r = ref.current?.getBoundingClientRect();
    if (r) setRect({ top: r.bottom, left: r.left, width: r.width });
  };
  useEffect(() => {
    // Close on a click outside BOTH the input and the (portaled) menu — the menu lives on document.body,
    // so it is not inside `ref` and has to be excluded explicitly.
    const closer = (e: MouseEvent) => {
      const t = e.target as Node;
      if (!ref.current?.contains(t) && !menuRef.current?.contains(t)) setOpen(false);
    };
    window.addEventListener('click', closer);
    return () => window.removeEventListener('click', closer);
  }, []);
  useEffect(() => {
    if (!open) return;
    place();
    // `true` (capture) also catches scrolling inside the modal body, so the menu tracks the input.
    const onMove = () => place();
    window.addEventListener('scroll', onMove, true);
    window.addEventListener('resize', onMove);
    return () => {
      window.removeEventListener('scroll', onMove, true);
      window.removeEventListener('resize', onMove);
    };
  }, [open]);
  const search = props.value?.trim().toLowerCase();
  const filtered = search ? props.options.filter((o) => o.label.toLowerCase().includes(search)) : props.options;
  return (
    <ComboInputStyled ref={ref}>
      <Input value={props.value} onChange={(v) => { props.onChange(v || ''); setOpen(true); }} onFocus={() => { setOpen(true); place(); }} />
      {
        open && filtered.length > 0 && rect && createPortal(
          <ComboOptionsStyled ref={menuRef} style={{ top: rect.top, left: rect.left, width: rect.width }}>
            {filtered.map((o) => <div className="option" key={o.value} onClick={() => { props.onChange(o.value); setOpen(false); }}>{o.label}</div>)}
          </ComboOptionsStyled>,
          document.body,
        )
      }
    </ComboInputStyled>
  );
};

// Carries the document-card styles into the (portaled) modal, since the page's scoped classes don't reach it.
const DocModalStyled = styled(ModalStyled)`
  .modal-window { width: 34rem; max-width: 100%; }
  .doc .doc-head { display: flex; gap: 0.5rem; align-items: baseline; }
  .doc .doc-label { font-weight: 600; }
  .doc .req { font-size: 0.75rem; color: ${css.grey4}; }
  .doc .current { font-size: 0.85rem; margin: 0.3rem 0; }
  .doc .reject-reason { font-size: 0.82rem; color: ${css.red5}; margin: 0.2rem 0; overflow-wrap: anywhere; }
  .doc .versions { margin: 0.3rem 0 0 0.5rem; border-left: 2px solid ${css.grey6}; padding-left: 0.6rem; }
  .doc .version { font-size: 0.82rem; display: flex; gap: 0.5rem; align-items: center; padding: 0.15rem 0; flex-wrap: wrap; }
  .doc .actions { display: flex; gap: 0.4rem; margin-top: 0.3rem; flex-wrap: wrap; }
  .doc .uploader { margin-top: 0.5rem; max-width: 18rem; }
  a.dl { color: ${css.primaryColor}; cursor: pointer; }
  .link-btn { background: none; border: none; color: ${css.primaryColor}; cursor: pointer; padding: 0; font-size: 0.82rem; }
  .meta-row { color: ${css.grey3}; font-size: 0.9rem; margin: 0.1rem 0; }
`;

/* The status dialog is two fields tall; it borrows the wide modal's field styling at a narrower width. */
const StatusModalStyled = styled(WideModalStyled)`
  .modal-window { width: 30rem; }
  .status-note-input { display: block; width: 100%; min-height: 6rem; resize: vertical; }
  .hint { color: ${css.grey3}; font-size: 0.8rem; margin-top: 0.25rem; }
`;

const DrawSplitModalStyled = styled(WideModalStyled)`
  .modal-window { width: 34rem; }
  .hint { color: ${css.grey3}; font-size: 0.85rem; margin: 0 0 0.9rem; }
  .split-grid { display: grid; grid-template-columns: 1fr 7rem 9rem; gap: 0.5rem 0.75rem; align-items: center; }
  .split-grid .hdr { font-size: 0.75rem; color: ${css.grey4}; text-transform: uppercase; letter-spacing: 0.04em; }
  .split-grid .num { text-align: right; color: ${css.grey3}; font-size: 0.85rem; font-variant-numeric: tabular-nums; }
  .split-grid input { width: 100%; text-align: right; }
  .total { margin-top: 0.75rem; font-size: 0.85rem; color: ${css.grey3}; }
  .total.bad { color: ${css.red2}; }
`;

const HistoryModalStyled = styled(ModalStyled)`
  .modal-window { width: 40rem; max-width: 100%; }
  .empty { color: ${css.grey4}; font-size: 0.9rem; padding: 1rem 0; text-align: center; }
  .entry { border-left: 2px solid ${css.grey6}; padding: 0 0 0.9rem 0.9rem; margin-left: 0.4rem; position: relative; }
  .entry::before { content: ''; position: absolute; left: -0.36rem; top: 0.15rem; width: 0.6rem; height: 0.6rem; border-radius: 50%; background: ${css.primaryColor}; }
  .entry:last-child { padding-bottom: 0; }
  .entry .when { color: ${css.grey3}; font-size: 0.82rem; margin-bottom: 0.35rem; }
  .change { display: flex; align-items: baseline; gap: 0.5rem; font-size: 0.9rem; padding: 0.1rem 0; }
  .change .field { color: ${css.grey2}; font-weight: 600; min-width: 6rem; }
  .change .from { color: ${css.grey4}; }
  .change .arrow { color: ${css.grey5}; }
  .change .to { color: ${css.grey1}; font-weight: 600; }
  .modal-buttons { display: flex; justify-content: flex-end; margin-top: 1.25rem; }
`;

// The QuickBooks-suggestions table lives in a portaled modal, so it re-declares the .fin-table / .link-btn
// styles the Budget tab scopes to its own page wrapper (a portal never sees those).
const SuggestionsModalStyled = styled(ModalStyled)`
  .modal-window { width: 44rem; max-width: 100%; }
  .fin-table { width: 100%; border-collapse: collapse; font-size: 0.85rem; }
  .fin-table th, .fin-table td { text-align: left; padding: 0.25rem 0.5rem; border-bottom: 1px solid ${css.grey6}; }
  .fin-table tbody tr:hover { background: ${css.grey8}; }
  .fin-table th.num, .fin-table td.num { text-align: right; }
  .link-btn { background: none; border: none; color: ${css.primaryColor}; cursor: pointer; padding: 0; font-size: 0.82rem; }
  .link-btn:disabled { color: ${css.grey5}; cursor: default; }
  .sep { color: ${css.grey5}; margin: 0 0.3rem; }
  .empty { color: ${css.grey4}; font-size: 0.9rem; padding: 1rem 0; text-align: center; }
`;

/**
 * Says that the LLM is reading this document, or that it could not. Reading starts at upload and runs
 * through text extraction and the model call, which together take long enough that without this the
 * upload looks finished while the work is still going.
 */
const AiReadIndicator = ({ doc }: { doc?: { aiRead?: ProjectManagementAiRead } }) => {
  if (projectManagementIsReadingWithAi(doc)) {
    return (
      <span className="ai-read" data-tooltip-id="pm-task-tip" data-tooltip-content={strings.ReadingWithAiHint()}>
        <span className="dots"><i /><i /><i /></span>
        {strings.ReadingWithAi()}
      </span>
    );
  }
  if (doc?.aiRead?.status === 'failed') {
    return (
      <span className="ai-read failed" data-tooltip-id="pm-task-tip" data-tooltip-content={doc.aiRead.error || strings.AiReadFailed()}>
        <Icon icon="warning" size={12} />
        {strings.AiReadFailed()}
      </span>
    );
  }
  return null;
};

/** Localized label for an event kind — used by the History feed's per-type mute filter. */
const eventKindLabel = (kind: ProjectManagementProjectEvent['kind']): string => {
  switch (kind) {
    case 'stageTransition': return strings.Stage();
    case 'milestone': return strings.Milestones();
    case 'parameter': return strings.Parameters();
    case 'cashEvent': return strings.CashEvents();
    case 'changeOrder': return strings.ChangeOrders();
    case 'fundingSource': return strings.FundingSources();
    case 'stakeholder': return strings.Stakeholders();
    case 'document': return strings.Documents();
    case 'task': return strings.Tasks();
    case 'purchaseOrder': return strings.PurchaseOrders();
    case 'inventory': return strings.Inventory();
    case 'link': return strings.Links();
    default: return strings.Project();
  }
};

/**
 * Timeline of one row's edit history — a projection of the unified project-event feed filtered on the
 * row (kind + ref.id). The caller names the fields and formats the values, so a parameter's date value
 * and a cash event's dollar amount each render in their own terms.
 */
const EntityHistoryModal = (props: {
  projectId: string;
  kind: ProjectManagementProjectEvent['kind'];
  refId: string;
  title: string;
  fieldLabel: Record<string, string>;
  fmtChange: (field: string, v?: string | number | boolean | Date) => string;
  onClose: () => void;
}) => {
  const { projectId, kind, refId, title, fieldLabel, fmtChange, onClose } = props;
  const entries = useTracker(() => ProjectManagementProjectEvents.find(
    { projectManagementProjectId: projectId, kind, 'ref.id': refId, active: { $ne: false } },
    { sort: { createdAt: -1 } },
  ).fetch(), [kind, refId]);
  return (
    <Modal styled={HistoryModalStyled} title={`${title} — ${strings.History()}`} onClose={onClose}>
      {
        !entries.length ? <div className="empty">{strings.NoHistory()}</div>
          : entries.map((e) => (
            <div className="entry" key={e._id}>
              <div className="when">{e.createdByName ? `${e.createdByName} · ` : ''}{e.createdAt && formatDateTime(e.createdAt)}</div>
              {
                // 'updated' events carry field diffs; 'created'/'deleted' just show the action.
                (e.changes?.length ? e.changes : [{ field: eventActionLabel(e.action) }]).map((c, i) => (
                  <div className="change" key={i}>
                    <span className="field">{fieldLabel[c.field] || c.field}</span>
                    {'to' in c &&
                      <>
                        <span className="from">{fmtChange(c.field, (c as any).from)}</span>
                        <span className="arrow">→</span>
                        <span className="to">{fmtChange(c.field, (c as any).to)}</span>
                      </>
                    }
                  </div>
                ))
              }
            </div>
          ))
      }
      <div className="modal-buttons">
        <Button className="secondary" onClick={onClose}>{strings.Close()}</Button>
      </div>
    </Modal>
  );
};

/** A parameter's history: value fields format by the parameter's type/unit (so date params drop the time). */
const ParameterHistoryModal = (props: { parameter: ProjectManagementParameter; onClose: () => void }) => {
  const { parameter, onClose } = props;
  const fmtChange = (field: string, v?: string | number | boolean | Date) => {
    if (v == null || v === '') return '—';
    if (field === 'estimatedValue' || field === 'actualValue') return formatValue(v, parameter.unit, parameter.type);
    if (typeof v === 'boolean') return v ? strings.Yes() : strings.No();
    if (v instanceof Date) return v.toLocaleString();
    return typeof v === 'number' ? v.toLocaleString() : String(v);
  };
  return (
    <EntityHistoryModal
      projectId={parameter.projectManagementProjectId} kind="parameter" refId={parameter._id} title={parameter.name}
      fieldLabel={{ name: strings.Name(), type: strings.Type(), unit: strings.Unit(), estimatedValue: strings.Estimated(), actualValue: strings.Actual() }}
      fmtChange={fmtChange} onClose={onClose}
    />
  );
};

/** Add/edit a parameter: the value type drives which widget the estimated/actual fields use.
    The Task select assigns / reassigns / clears which task owns the parameter; `taskId` seeds it when
    creating from a task row's Details block. */
const ParameterModal = (props: { parameter?: ProjectManagementParameter; projectId: string; taskId?: string; taskOptions: { value: string, label: string }[]; onClose: () => void }) => {
  const { parameter, projectId, taskId, taskOptions, onClose } = props;
  const [task, setTask] = useState(parameter?.taskId || taskId || '');
  const [name, setName] = useState(parameter?.name || '');
  const [unit, setUnit] = useState(parameter?.unit || '');
  const [type, setType] = useState<ProjectManagementParameterType>(parameter?.type || 'number');
  const [category, setCategory] = useState<ProjectManagementParameterCategory | ''>(parameter?.category || '');
  const [estimated, setEstimated] = useState<any>(parameter?.estimatedValue);
  const [actual, setActual] = useState<any>(parameter?.actualValue);
  // Editable, so a parameter can be switched between the two shapes by hand. New ones default to the
  // single-value shape, which is what the great majority of parameters are.
  const [tracksActual, setTracksActual] = useState<boolean>(projectManagementTracksActual(parameter));
  const [notes, setNotes] = useState(parameter?.notes || '');
  // Switching type clears the values — a number isn't a valid date, etc.
  const changeType = (t: ProjectManagementParameterType) => { setType(t); setEstimated(undefined); setActual(undefined); };

  // Widgets + coercion are shared with the bulk grid (ProjectManagementCellEditor).
  const valueInput = (value: any, onChange: (v: any) => void) => projectManagementValueInput(type, value, onChange);
  const toStored = (raw: any) => projectManagementToStored(type, raw);
  const numeric = projectManagementParameterIsNumeric(type);
  const save = toastOnError(async () => {
    if (!name.trim()) { toast.info(strings.Name()); return; }
    await projectManagementSaveParameter.call({
      _id: parameter?._id, projectManagementProjectId: projectId, name: name.trim(), type, category: category || undefined,
      unit: numeric ? (unit.trim() || undefined) : undefined,
      estimatedValue: toStored(estimated) ?? null,
      // Clearing the actual alongside the flag: a stale actual left on a single-value parameter would be
      // invisible in the UI but still returned by the export and the bulk grid.
      actualValue: tracksActual ? (toStored(actual) ?? null) : null,  // null clears
      tracksActual,
      notes: notes.trim(),  // '' clears it server-side
      source: parameter ? parameter.source : 'manual',
      taskId: task,  // '' clears the link server-side
    });
    onClose();
  });
  return (
    <Modal styled={WideModalStyled} title={parameter ? parameter.name : strings.Parameters()} onClose={onClose}>
      <div className="add-fields">
        <div><div className="meta-row">{strings.Name()}</div><Input value={name} onChange={(v) => setName(v || '')} /></div>
        <div><div className="meta-row">{strings.Type()}</div><Select value={type} options={projectManagementParameterTypes.map((t) => ({ value: t, label: projectManagementParameterTypeLabel(t) }))} onChange={(v) => changeType(v as ProjectManagementParameterType)} /></div>
        <div><div className="meta-row">{strings.Category()}</div><Select value={category} options={[{ value: '', label: '—' }, ...projectManagementParameterCategories.map((c) => ({ value: c, label: projectManagementParameterCategoryLabel(c) }))]} onChange={(v) => setCategory(v as ProjectManagementParameterCategory | '')} /></div>
        <div><div className="meta-row">{strings.TaskWithActualRequiredHint()}</div><Select value={task} options={taskOptions} onChange={(v) => setTask(v || '')} /></div>
        {numeric && <div><div className="meta-row">{strings.Unit()}</div><ComboInput value={unit} options={projectManagementParameterUnits.map((u) => ({ value: u, label: u }))} onChange={(v) => setUnit(v)} /></div>}
        {tracksActual && <div><div className="meta-row">{strings.Actual()}</div>{valueInput(actual, setActual)}</div>}
        <div><div className="meta-row">{tracksActual ? strings.Estimated() : strings.Value()}</div>{valueInput(estimated, setEstimated)}</div>
        <div><div className="meta-row">{strings.NotesStatus()}</div><ComboInput value={notes} options={[strings.StatusScheduled(), strings.StatusSubmitted(), strings.StatusApproved(), strings.StatusCompleted(), strings.StatusExecuted()].map((s) => ({ value: s, label: s }))} onChange={(v) => setNotes(v)} /></div>
        <div><div className="meta-row">{strings.TracksActual()}</div><Checkbox checked={tracksActual} onChange={(v) => setTracksActual(v)} /></div>
      </div>
      <div className="modal-buttons">
        <Button className="secondary" onClick={onClose}>{strings.Cancel()}</Button>
        <Button className="primary" onClick={() => save()}>{strings.Save()}</Button>
      </div>
    </Modal>
  );
};

/** Reusable modal that collects a set of fields and hands the values to a submit callback (replaces native prompts). */
const FieldsModal = (props: { prompt: FieldsPrompt; onClose: () => void }) => {
  const { prompt, onClose } = props;
  const [values, setValues] = useState<Record<string, string>>(() => Object.fromEntries(prompt.fields.map((f) => [f.key, f.default ?? ''])));
  return (
    <Modal styled={WideModalStyled} title={prompt.title} onClose={onClose}>
      <div className="add-fields">
        {
          prompt.fields.map((f) => (
            <div key={f.key}>
              <div className="meta-row">{f.label}</div>
              {
                f.type === 'account'
                  ? <AccountPicker value={values[f.key] || undefined} onChange={(id) => setValues({ ...values, [f.key]: id || '' })} />
                  : f.type === 'select'
                    ? <Select value={values[f.key]} options={f.options || []} onChange={(v) => setValues({ ...values, [f.key]: v })} />
                    : f.type === 'date'
                      ? <DateInput value={values[f.key]} max={f.max} onChange={(v) => setValues({ ...values, [f.key]: v || '' })} />
                      : f.type === 'combo'
                        ? <ComboInput value={values[f.key]} options={f.options || []} onChange={(v) => setValues({ ...values, [f.key]: v })} />
                        : f.type === 'phone'
                          ? <PhoneInput value={values[f.key]} onChange={(v) => setValues({ ...values, [f.key]: v || '' })} />
                          : f.type === 'currency'
                            ? <CurrencyInput value={values[f.key] ? Number(values[f.key]) : undefined} onChange={(n) => setValues({ ...values, [f.key]: n != null ? String(n) : '' })} />
                            : <Input value={values[f.key]} onChange={(v) => setValues({ ...values, [f.key]: v || '' })} />
              }
            </div>
          ))
        }
      </div>
      <div className="modal-buttons">
        <Button className="secondary" onClick={onClose}>{strings.Cancel()}</Button>
        <Button
          className="primary"
          disabled={prompt.fields.some((f) => f.required && !(values[f.key] || '').trim())}
          onClick={() => { prompt.submit(values); onClose(); }}
        >
          {prompt.submitText || strings.Save()}
        </Button>
      </div>
    </Modal>
  );
};

/**
 * Review-before-apply for the financial model parse: shows every value read out of the workbook next to
 * what the project holds today, and writes only the ticked rows. Closing without accepting writes
 * nothing at all — the parse leaves no state behind, so re-running it simply re-reads the file.
 */
const ParsedModelModal = (props: {
  rows: ProjectManagementParsedModelRow[];
  onAccept: (rows: ProjectManagementParsedModelRow[]) => void;
  onClose: () => void;
}) => {
  const { rows, onAccept, onClose } = props;
  // Everything is ticked to begin with: the common case is accepting the parse wholesale, and the
  // per-row ticks are there to drop the occasional figure that read wrong.
  const [picked, setPicked] = useState<Set<string>>(() => new Set(rows.map((r) => r.name)));
  const toggle = (name: string) => setPicked((prev) => {
    const next = new Set(prev);
    if (next.has(name)) next.delete(name);
    else next.add(name);
    return next;
  });
  const allPicked = picked.size === rows.length;
  return (
    <Modal styled={WideModalStyled} title={strings.ReviewModelValues()} onClose={onClose}>
      <table className="fin-table parsed-model">
        <thead>
          <tr>
            <th>
              <Checkbox
                checked={allPicked}
                onChange={() => setPicked(allPicked ? new Set() : new Set(rows.map((r) => r.name)))}
              />
            </th>
            <th>{strings.Name()}</th>
            <th className="num">{strings.CurrentEstimate()}</th>
            <th className="num">{strings.ModelValue()}</th>
          </tr>
        </thead>
        <tbody>
          {
            rows.map((r) => (
              <tr key={r.name} className={picked.has(r.name) ? undefined : 'unpicked'}>
                <td><Checkbox checked={picked.has(r.name)} onChange={() => toggle(r.name)} /></td>
                <td>{r.name}{!r.exists && <span className="meta-row">{strings.New()}</span>}{r.cashEvent && <span className="meta-row">{strings.Use()}</span>}{r.fundingSource && <span className="meta-row">{strings.Source()}</span>}</td>
                <td className="num">{r.exists ? formatValue(r.currentEstimatedValue, r.unit, r.type) : '—'}</td>
                <td className="num">{formatValue(r.value, r.unit, r.type)}</td>
              </tr>
            ))
          }
        </tbody>
      </table>
      <div className="modal-buttons">
        <Button className="secondary" onClick={onClose}>{strings.Cancel()}</Button>
        <Button
          className="primary"
          disabled={!picked.size}
          onClick={() => onAccept(rows.filter((r) => picked.has(r.name)))}
        >
          {strings.Accept()}
        </Button>
      </div>
    </Modal>
  );
};

/** Super-user: split an invoice's amount across the project's money-out cash 'uses'. These allocations are
 *  what the QuickBooks Tier-0 sweep reads to auto-assign a bill (matched by invoice number) to the uses. */
const InvoiceAllocationModal = (props: {
  invoice: ProjectManagementProjectInvoice;
  uses: { _id: string; label?: string; milestoneKey?: string }[];
  raised: ProjectManagementChangeOrder[];   // change orders already raised from THIS invoice
  onClose: () => void;
}) => {
  const { invoice, uses, raised, onClose } = props;
  const [amounts, setAmounts] = useState<Record<string, string>>(() => {
    const m: Record<string, string> = {};
    (invoice.allocations || []).forEach((a) => { m[a.cashEventId] = String(a.amount); });
    return m;
  });
  // The one row that is not a Use. It raises a change order rather than allocating to something that
  // already exists, so it has no cashEventId to key on and starts empty every time the modal opens —
  // a second visit raises a second change order rather than editing the first.
  const [changeOrder, setChangeOrder] = useState<number>();
  // A change order already raised from this invoice counts here only while it is still PENDING. Once
  // accepted it has posted a Use of its own, which is in the list above with the invoice's allocation
  // against it — counting the change order too would bill the same money twice. A rejected one is dead.
  const pendingRaised = raised.filter((co) => co.status === 'submitted' || co.status === 'underReview');
  const raisedTotal = pendingRaised.reduce((n, co) => n + (co.value || 0), 0);
  const total = Object.values(amounts).reduce((s, v) => s + (Number(v) || 0), 0) + (changeOrder || 0) + raisedTotal;
  const mismatch = invoice.amount != null && Math.abs(total - invoice.amount) > 0.01;
  /**
   * What the invoice still has unassigned once every row EXCEPT this one is counted — so clicking it on a
   * row that already holds a figure replaces that figure rather than adding to it.
   * @param ownAmount - what this row currently holds, which does not count against itself
   */
  const remainder = (ownAmount: number) => Math.max(0, roundNumber((invoice.amount || 0) - (total - ownAmount), 2));
  // Nothing to apportion without an invoice total, so the control would be dead.
  const canFill = invoice.amount != null;
  const fillIcon = (onClick: () => void) => (
    canFill && <Icon className="alloc-fill" icon="addCircle" size={18} fill={css.primaryColor} tip={strings.UseRemainder()} onClick={onClick} />
  );
  const save = toastOnError(async () => {
    const allocations = Object.entries(amounts).filter(([, v]) => Number(v) > 0).map(([cashEventId, v]) => ({ cashEventId, amount: Number(v) }));
    // Allocations first, deliberately: if the change order then fails, a retry re-sets the same
    // allocations harmlessly, where the other order would raise a second change order.
    await projectManagementSetInvoiceAllocations.call({ _id: invoice._id, allocations });
    if (changeOrder && changeOrder > 0) {
      await projectManagementSaveChangeOrder.call({
        projectManagementProjectId: invoice.projectManagementProjectId,
        title: `${strings.ChangeOrder()} — ${invoice.label}`,
        value: changeOrder,
        invoiceId: invoice._id,
      });
    }
    onClose();
  });
  return (
    <Modal styled={WideModalStyled} title={`${strings.Allocate()} — ${invoice.label}`} onClose={onClose}>
      <div className="meta-row">{strings.InvoiceAmount()}: {invoice.amount != null ? money(invoice.amount, true) : '—'}</div>
      <table className="fin-table">
        <thead><tr><th>{strings.Payment()}</th><th className="num">{strings.Amount()}</th></tr></thead>
        <tbody>
          {uses.map((u) => (
            <tr key={u._id}>
              <td>{u.label || u.milestoneKey || '—'}</td>
              <td className="num">
                <span className="alloc-cell">
                  <CurrencyInput value={amounts[u._id] ? Number(amounts[u._id]) : undefined} onChange={(n) => setAmounts((m) => ({ ...m, [u._id]: n != null ? String(n) : '' }))} />
                  {fillIcon(() => setAmounts((m) => ({ ...m, [u._id]: String(remainder(Number(m[u._id]) || 0)) })))}
                </span>
              </td>
            </tr>
          ))}
          {/* What this invoice has already raised, so re-allocating it shows the whole picture rather than
              an empty row and no trace. Read-only — a raised change order is edited from its own record,
              and an accepted one's money is in the Use above instead. */}
          {
            raised.map((co) => (
              <tr key={co._id}>
                <td>
                  {co.title}
                  <div className="meta-row"><Badge status={co.status} label={coStatusLabel[co.status]} /></div>
                </td>
                <td className="num">
                  {cents(co.value)}
                  {co.status === 'accepted' && <div className="meta-row">{strings.Allocated()}</div>}
                </td>
              </tr>
            ))
          }
          {/* Always last, and always present: the rest of this list is whatever Uses the project has, but a
              change order is what you raise when none of them fits. */}
          <tr>
            <td data-tooltip-id="pm-task-tip" data-tooltip-content={strings.NewChangeOrderFromInvoiceHint()}>
              {strings.ChangeOrder()}
              <div className="meta-row">{strings.New()}</div>
            </td>
            <td className="num">
              <span className="alloc-cell">
                <CurrencyInput value={changeOrder} onChange={(n) => setChangeOrder(n ?? undefined)} />
                {fillIcon(() => setChangeOrder(remainder(changeOrder || 0)))}
              </span>
            </td>
          </tr>
        </tbody>
      </table>
      <div className="meta-row">{strings.Total()}: {money(total, true)}{mismatch ? ` ⚠ ${strings.AllocationMismatch()}` : ''}</div>
      <div className="modal-buttons">
        <Button className="secondary" onClick={onClose}>{strings.Cancel()}</Button>
        <Button className="primary" onClick={() => save()}>{strings.Save()}</Button>
      </div>
    </Modal>
  );
};

/** Lists a budget record's documents — real ProjectManagementDocuments with version/approval state —
 *  and accepts new uploads. The badge opens the standard document card (versions, approve, revert). */
/** Add/edit a stakeholder: account picker first (auto-fills the name, still editable), then type/org. A
 *  membership carries no roles — any stakeholder is assignable to any task; identity lives on the profile. */
const StakeholderModal = (props: { projectId: string; stakeholder?: ProjectManagementStakeholder; profile?: ProjectManagementStakeholderProfile; onClose: () => void }) => {
  const { projectId, stakeholder, profile, onClose } = props;
  const [accountId, setAccountId] = useState<string | undefined>(profile?.accountId);
  const [name, setName] = useState(profile?.name || '');
  const [kind, setKind] = useState<'internal' | 'external'>(profile?.kind || 'internal');
  const notes = stakeholder?.notes || '';  // per-project membership note; field hidden, value preserved on save

  const pickAccount = (id?: string, account?: Account) => {
    setAccountId(id);
    if (!account) return;
    setName(accountDisplayName(account)); // auto-fill; user can still edit the name below
    setKind('internal'); // an org account is internal staff (still editable below)
  };
  const save = toastOnError(async () => {
    if (!name.trim()) { toast.info(strings.Name()); return; }
    await projectManagementSaveStakeholder.call({
      _id: stakeholder?._id,
      projectManagementProjectId: projectId,
      // Edit → target this membership's profile (updates its identity); create → find-or-create a profile by identity.
      profileId: stakeholder ? profile?._id : undefined,
      profile: { name: name.trim(), kind, accountId },
      notes,
    });
    onClose();
  });

  return (
    <Modal styled={WideModalStyled} title={strings.Stakeholders()} onClose={onClose}>
      <div className="add-fields">
        <div><div className="meta-row">{strings.Account()}</div><AccountPicker value={accountId} onChange={pickAccount} /></div>
        <div><div className="meta-row">{strings.Name()}</div><Input value={name} onChange={(v) => setName(v || '')} /></div>
      </div>
      {
        profile?._id &&
        <div className="bulk-section">
          <div className="meta-row bulk-title">{strings.ManageAcrossProjects()}</div>
          <PartyBulkManager profileId={profile._id} mode="compact" />
        </div>
      }
      <div className="modal-buttons">
        <Button className="secondary" onClick={onClose}>{strings.Cancel()}</Button>
        <Button className="primary" onClick={() => save()}>{strings.Save()}</Button>
      </div>
    </Modal>
  );
};

/** Edit a project's descriptive fields (address re-geocodes; lifecycle and stage are managed elsewhere). */
/** Edit the project spine. Solar / BESS inputs are hidden once the 'PV System Size' / 'BESS Capacity (kWh)'
 *  parameters hold a value — the parameters are the size of record then, and the banner reads them first. */
const EditProjectModal = (props: { project: ProjectManagementProject; hideSolar: boolean; hideStorage: boolean; onClose: () => void }) => {
  const { project, hideSolar, hideStorage, onClose } = props;
  const portfolios = useTracker(() => ProjectManagementPortfolios.find({ active: { $ne: false } }, { sort: { name: 1 } }).fetch(), []);
  const owners = useTracker(() => ProjectManagementOwners.find({ active: { $ne: false } }, { sort: { name: 1 } }).fetch(), []);
  const epcs = useTracker(() => ProjectManagementEpcs.find({ active: { $ne: false } }, { sort: { name: 1 } }).fetch(), []);
  const utilities = useTracker(() => ProjectManagementUtilities.find({ active: { $ne: false } }, { sort: { name: 1 } }).fetch(), []);
  const [portfolioId, setPortfolioId] = useState(project.portfolioId || '');
  const [form, setForm] = useState({
    name: project.name || '',
    onsiteContactName: project.onsiteContactName || '',
    onsiteEmail: project.onsiteEmail || '',
    onsitePhone: project.onsitePhone || '',
    clientId: project.clientId || '',
    sgipId: project.sgipId || '',
    capacityKwDc: project.capacityKwDc != null ? String(project.capacityKwDc) : '',
    storageKwh: project.storageKwh != null ? String(project.storageKwh) : '',
    externalId: project.externalId || '',
  });
  const [address, setAddress] = useState<{ address?: string; lat?: number; lng?: number }>({ address: project.address, lat: project.lat, lng: project.lng });
  const [ownerId, setOwnerId] = useState(project.ownerId);
  const [epcId, setEpcId] = useState(project.epcId);
  const [logo, setLogo] = useState<FileMeta | undefined>(project.logo);
  const [utilityId, setUtilityId] = useState(project.utilityId);
  const set = (key: keyof typeof form) => (v?: string) => setForm({ ...form, [key]: v || '' });
  // A cleared field goes over as null — the server unsets it. undefined would be dropped by DDP and the old
  // value silently kept, which read as "my change did not save".
  const text = (v?: string) => (v && v.trim() ? v.trim() : null);
  const num = (v?: string) => (v && v.trim() ? Number(v) : null);
  const save = toastOnError(async () => {
    if (!form.name.trim()) { toast.info(strings.Name()); return; }
    await projectManagementUpdateProject.call({
      projectManagementProjectId: project._id,
      name: form.name.trim(),
      address: text(address.address),
      // Coordinates only when the picker supplied them; a changed address without them is re-geocoded server-side.
      ...(address.address ? (address.lat != null && address.lng != null ? { lat: address.lat, lng: address.lng } : {}) : { lat: null, lng: null }),
      onsiteContactName: text(form.onsiteContactName),
      onsiteEmail: text(form.onsiteEmail),
      onsitePhone: text(form.onsitePhone),
      clientId: text(form.clientId),
      portfolioId: portfolioId || null,
      utilityId: utilityId || null,
      sgipId: text(form.sgipId),
      ownerId: ownerId || null,
      epcId: epcId || null,
      ...(logo !== project.logo ? { logo: logo ?? null } : {}),
      ...(hideSolar ? {} : { capacityKwDc: num(form.capacityKwDc) }),
      ...(hideStorage ? {} : { storageKwh: num(form.storageKwh) }),
      externalId: text(form.externalId),
    });
    onClose();
  });
  return (
    <Modal styled={WideModalStyled} title={strings.EditProject()} onClose={onClose}>
      <div className="add-fields">
        <div><div className="meta-row">{strings.Name()}</div><Input value={form.name} onChange={set('name')} /></div>
        <div><div className="meta-row">{strings.ProjectLogo()}</div><ImageInput value={logo} accept="image/*" onChange={(f) => setLogo(f)} /></div>
        <div className="span2">
          <div className="meta-row">{strings.Address()}</div>
          <AddressInput inline
            value={address.address ? ({ formatted: address.address, location: (address.lat != null && address.lng != null) ? { lat: address.lat, lng: address.lng } : undefined } as any) : undefined}
            onChange={(a) => setAddress({ address: a?.formatted, lat: a?.location?.lat, lng: a?.location?.lng })} />
        </div>
        <div>
          <div className="meta-row">{strings.Owner()}</div>
          <PartyPicker modelClass={projectManagementOwnerModelClass} parties={owners} value={ownerId} onChange={setOwnerId} />
        </div>
        <div>
          <div className="meta-row">{strings.Epc()}</div>
          <PartyPicker modelClass={projectManagementEpcModelClass} parties={epcs} value={epcId} onChange={setEpcId} />
        </div>
        <div>
          <div className="meta-row">{strings.Portfolio()}</div>
          <Select value={portfolioId} options={[{ value: '', label: '—' }, ...portfolios.map((p) => ({ value: p._id, label: p.name }))]} onChange={(v) => setPortfolioId(v)} />
        </div>
        <div><div className="meta-row">{strings.OnsiteContactName()}</div><Input value={form.onsiteContactName} onChange={set('onsiteContactName')} /></div>
        <div><div className="meta-row">{strings.OnsiteEmail()}</div><Input value={form.onsiteEmail} onChange={set('onsiteEmail')} /></div>
        <div><div className="meta-row">{strings.OnsitePhone()}</div><PhoneInput value={form.onsitePhone} onChange={set('onsitePhone')} /></div>
        <div><div className="meta-row">{strings.ClientId()}</div><Input value={form.clientId} onChange={set('clientId')} /></div>
        <div>
          <div className="meta-row">{strings.UtilityCompany()}</div>
          <PartyPicker modelClass={projectManagementUtilityModelClass} parties={utilities} value={utilityId} onChange={setUtilityId} />
        </div>
        <div><div className="meta-row">{strings.SgipId()}</div><Input value={form.sgipId} onChange={set('sgipId')} /></div>
        {!hideSolar && <div><div className="meta-row">{strings.CapacityKwDc()}</div><Input value={form.capacityKwDc} onChange={set('capacityKwDc')} /></div>}
        {!hideStorage && <div><div className="meta-row">{strings.StorageKwh()}</div><Input value={form.storageKwh} onChange={set('storageKwh')} /></div>}
        <div><div className="meta-row">{projectManagementExternalIdLabel(project.lifecycleKey)}</div><Input value={form.externalId} onChange={set('externalId')} /></div>
      </div>
      <div className="modal-buttons">
        <Button className="secondary" onClick={onClose}>{strings.Cancel()}</Button>
        <Button className="primary" onClick={() => save()}>{strings.Save()}</Button>
      </div>
    </Modal>
  );
};

/** Sets the hand-set project status. The exception statuses require a reason — the button waits for one
 *  and the server enforces it too. */
const ProjectStatusModal = (props: { project: ProjectManagementProject; onClose: () => void }) => {
  const { project, onClose } = props;
  const [status, setStatus] = useState<ProjectManagementProjectStatus>(projectManagementProjectStatus(project));
  const [note, setNote] = useState(project.statusNote || '');
  // The same predicate the method applies, so Save is never disabled on a status the server would take.
  const needsNote = projectManagementStatusNeedsReason(status) && !note.trim();
  const save = toastOnError(async () => {
    await projectManagementSetProjectStatus.call({ projectManagementProjectId: project._id, status, note: note.trim() || undefined });
    toast.success(strings.StatusUpdated());
    onClose();
  });
  return (
    <Modal styled={StatusModalStyled} title={strings.ChangeStatus()} onClose={onClose}>
      <div className="add-fields">
        <div className="span2">
          <div className="meta-row">{strings.ProjectStatus()}</div>
          <Select value={status} options={projectManagementProjectStatuses.map((s) => ({ value: s, label: projectStatusLabel(s) }))} onChange={(v) => setStatus(v as ProjectManagementProjectStatus)} />
        </div>
        <div className="span2">
          <div className="meta-row">{strings.StatusReason()}</div>
          <TextArea className="status-note-input" value={note} placeholder={strings.StatusReasonPlaceholder()} onChange={setNote} />
          {needsNote && <div className="hint">{strings.StatusReasonRequired()}</div>}
        </div>
      </div>
      <div className="modal-buttons">
        <Button className="secondary" onClick={onClose}>{strings.Cancel()}</Button>
        <Button className="primary" disabled={needsNote} onClick={() => save()}>{strings.Save()}</Button>
      </div>
    </Modal>
  );
};

/** The project's work-order split: one percentage per draw, saved together so they always total 100%.
 *  The right-hand column previews what each draw's estimate becomes at the current Total Solar Cost. */
const DrawSplitModal = (props: { projectId: string; draws: ProjectManagementCashEvent[]; totalSolarCost?: number; onClose: () => void }) => {
  const { projectId, draws, totalSolarCost, onClose } = props;
  const keys = Object.keys(projectManagementSolarCostDrawSplit);
  const drawOf = (key: string) => draws.find((d) => d.milestoneKey === key);
  const [percents, setPercents] = useState<{ [key: string]: string }>(() => Object.fromEntries(keys.map((k) => {
    const draw = drawOf(k);
    const share = draw ? projectManagementDrawShare(draw) : projectManagementSolarCostDrawSplit[k];
    return [k, String(roundNumber((share ?? 0) * 100, 2))];
  })));
  const value = (k: string) => Number(percents[k]);
  const valid = keys.every((k) => (percents[k] || '').trim() !== '' && Number.isFinite(value(k)) && value(k) >= 0 && value(k) <= 100);
  const total = roundNumber(keys.reduce((sum, k) => sum + (Number.isFinite(value(k)) ? value(k) : 0), 0), 2);
  const totals100 = Math.abs(total - 100) < 0.005;
  const save = toastOnError(async () => {
    await projectManagementSetDrawSplit.call({ projectManagementProjectId: projectId, shares: Object.fromEntries(keys.map((k) => [k, roundNumber(value(k) / 100, 4)])) });
    toast.success(strings.WorkOrderSplitSaved());
    onClose();
  });
  return (
    <Modal styled={DrawSplitModalStyled} title={strings.WorkOrderSplit()} onClose={onClose}>
      <p className="hint">{strings.WorkOrderSplitHint()}</p>
      <div className="split-grid">
        <span className="hdr">{strings.Description()}</span>
        <span className="hdr num">{strings.ShareOfTotalSolarCost()}</span>
        <span className="hdr num">{strings.Estimated()}</span>
        {
          keys.map((k) => (
            <React.Fragment key={k}>
              <span>{drawOf(k)?.label || k}</span>
              <Input type="number" value={percents[k]} onChange={(v) => setPercents((prev) => ({ ...prev, [k]: v || '' }))} />
              <span className="num">{totalSolarCost != null && Number.isFinite(value(k)) ? cents(roundNumber((totalSolarCost * value(k)) / 100, 2)) : '—'}</span>
            </React.Fragment>
          ))
        }
      </div>
      <div className={`total${totals100 ? '' : ' bad'}`}>{strings.SharesTotal(total)}{!totals100 && ` · ${strings.DrawSharesMustTotal()}`}</div>
      <div className="modal-buttons">
        <Button className="secondary" onClick={onClose}>{strings.Cancel()}</Button>
        <Button className="primary" disabled={!valid || !totals100} onClick={() => save()}>{strings.Save()}</Button>
      </div>
    </Modal>
  );
};

/** The project's dev-fee split: the share of the dev fee each work-order draw releases, saved together so
 *  they always total 100%. The right-hand column previews each draw's dev fee at the current modeled fee. */
const DevFeeSplitModal = (props: { projectId: string; draws: ProjectManagementCashEvent[]; devFee?: number; onClose: () => void }) => {
  const { projectId, draws, devFee, onClose } = props;
  const keys = Object.keys(projectManagementDevFeeReleaseSplit);
  const drawOf = (key: string) => draws.find((d) => d.milestoneKey === key);
  const [percents, setPercents] = useState<{ [key: string]: string }>(() => Object.fromEntries(keys.map((k) => {
    const draw = drawOf(k);
    const share = draw ? projectManagementDevFeeShare(draw) : projectManagementDevFeeReleaseSplit[k];
    return [k, String(roundNumber((share ?? 0) * 100, 2))];
  })));
  const value = (k: string) => Number(percents[k]);
  const valid = keys.every((k) => (percents[k] || '').trim() !== '' && Number.isFinite(value(k)) && value(k) >= 0 && value(k) <= 100);
  const total = roundNumber(keys.reduce((sum, k) => sum + (Number.isFinite(value(k)) ? value(k) : 0), 0), 2);
  const totals100 = Math.abs(total - 100) < 0.005;
  const save = toastOnError(async () => {
    await projectManagementSetDevFeeSplit.call({ projectManagementProjectId: projectId, shares: Object.fromEntries(keys.map((k) => [k, roundNumber(value(k) / 100, 4)])) });
    toast.success(strings.DevFeeSplitSaved());
    onClose();
  });
  return (
    <Modal styled={DrawSplitModalStyled} title={strings.DevFeeSplit()} onClose={onClose}>
      <p className="hint">{strings.DevFeeSplitHint()}</p>
      <div className="split-grid">
        <span className="hdr">{strings.Description()}</span>
        <span className="hdr num">{strings.ShareOfDevFee()}</span>
        <span className="hdr num">{strings.Estimated()}</span>
        {
          keys.map((k) => (
            <React.Fragment key={k}>
              <span>{drawOf(k)?.label || k}</span>
              <Input type="number" value={percents[k]} onChange={(v) => setPercents((prev) => ({ ...prev, [k]: v || '' }))} />
              <span className="num">{devFee != null && Number.isFinite(value(k)) ? cents(roundNumber((devFee * value(k)) / 100, 2)) : '—'}</span>
            </React.Fragment>
          ))
        }
      </div>
      <div className={`total${totals100 ? '' : ' bad'}`}>{strings.SharesTotal(total)}{!totals100 && ` · ${strings.DrawSharesMustTotal()}`}</div>
      <div className="modal-buttons">
        <Button className="secondary" onClick={onClose}>{strings.Cancel()}</Button>
        <Button className="primary" disabled={!valid || !totals100} onClick={() => save()}>{strings.Save()}</Button>
      </div>
    </Modal>
  );
};

/** The reviewer's decision on a reviewed milestone. A deficiency needs a note saying what has to be fixed;
 *  the note goes to History either way, since the real artefact is a running log rather than one field. */
const MilestoneReviewModal = (props: { milestone: ProjectManagementMilestone; onClose: () => void }) => {
  const { milestone, onClose } = props;
  const [status, setStatus] = useState<ProjectManagementMilestoneReviewStatus>('approved');
  const [note, setNote] = useState('');
  const needsNote = status === 'deficiency' && !note.trim();
  const save = toastOnError(async () => {
    await projectManagementSetMilestoneReview.call({ milestoneId: milestone._id, status, note: note.trim() || undefined });
    toast.success(strings.StatusUpdated());
    onClose();
  });
  return (
    <Modal styled={StatusModalStyled} title={strings.MilestoneReview()} onClose={onClose}>
      <div className="add-fields">
        <div className="span2">
          <div className="meta-row">{milestone.label}</div>
          <Select
            value={status}
            options={projectManagementMilestoneReviewStatuses.map((st) => ({ value: st, label: projectManagementMilestoneReviewLabel(st) }))}
            onChange={(v) => setStatus(v as ProjectManagementMilestoneReviewStatus)}
          />
        </div>
        <div className="span2">
          <div className="meta-row">{strings.ReviewNote()}</div>
          <TextArea className="status-note-input" value={note} placeholder={strings.DeficiencyNotePlaceholder()} onChange={setNote} />
          {needsNote && <div className="hint">{strings.DeficiencyNeedsNote()}</div>}
        </div>
      </div>
      <div className="modal-buttons">
        <Button className="secondary" onClick={onClose}>{strings.Cancel()}</Button>
        <Button className="primary" disabled={needsNote} onClick={() => save()}>{strings.Save()}</Button>
      </div>
    </Modal>
  );
};

/** Project detail view: lifecycle/stage, tasks, document management, transition history. */
export const ProjectManagementProjectPage = () => {
  const { _id } = useParams();
  const navigate = useNavigate();
  const user = useUser();
  const account = useFindOne(() => Accounts.find({ orgId: user?.orgId, userId: user?._id }), [user?._id]);
  const canAccess = projectManagementCanAccess(user, account);
  // Mirrors the server tiers (projectManagementVisibility): super users are admins; PM-role accounts are
  // members (external stakeholders, e.g. EPCs) — Delivery-only surface, and the server publishes/accepts
  // only their own slice regardless of what renders here.
  const isAdmin = projectManagementIsAdmin(user, account);
  const canSetOptional = projectManagementCanSetOptional(user, account);
  // Adding a task is the create-a-project tier (super users, or admin + projectManagement + luminia), not
  // the admin tier: the same people who may scaffold a project's tasks may add one to it. Editing and
  // deleting an existing task stay super-user-only, since those rows may be the template's.
  const canAddTask = projectManagementCanCreateProject(user, account);
  // Renaming and removing: the same tier, but only for a task somebody ADDED. A task the lifecycle template
  // scaffolded keeps its own key, which the template and the tracker import match on, so those stay
  // super-user-only. Twins the server's taskReshapeGuard.
  const canReshapeTask = (t: ProjectManagementTask) =>
    !!user?.isSuperUser || (canAddTask && !!t.templateKey?.startsWith('custom_'));
  // Finance viewers (super users, or accounting + PM) see the Budget tab and the money banner.
  const canSeeFinances = projectManagementCanSeeFinances(user, account);
  // Deciding a document version needs the pmDocumentApprover grant on top of the admin tier (or super
  // user) — narrower than the admin tier alone, which an org admin holds. Invoice approval stays su-only.
  const canApproveDocuments = projectManagementCanApproveDocuments(user, account);
  const canApproveInvoices = projectManagementCanApproveInvoices(user, account);
  // Luminia's own margin, and the funding behind it. The Budget tab is open to accounting and
  // invoiceApprover; what Luminia keeps is not.
  const canSeeFees = projectManagementCanSeeFees(user, account);
  const canSeeFundingSources = projectManagementCanSeeFundingSources(user, account);
  const [showEdit, setShowEdit] = useState(false);
  const [showStatus, setShowStatus] = useState(false);
  const [showDrawSplit, setShowDrawSplit] = useState(false);
  const [showDevFeeSplit, setShowDevFeeSplit] = useState(false);
  const [reviewMilestoneId, setReviewMilestoneId] = useState<string>();
  const [showDelete, setShowDelete] = useState(false);
  const [confirm, setConfirm] = useState<{ body: string; run: () => void; title?: string; confirmText?: string }>();
  const [allocating, setAllocating] = useState<ProjectManagementProjectInvoice>();  // invoice being allocated across cash 'uses'
  const [reviewing, setReviewing] = useState<ProjectManagementProjectInvoice>();    // invoice being reviewed for approve/reject
  const [expanded, setExpanded] = useState<{ [docId: string]: boolean }>({});
  const [showAddDoc, setShowAddDoc] = useState(false);
  // Parsed model rows awaiting review. Transient by design: nothing is persisted until Accept.
  const [parsedModel, setParsedModel] = useState<ProjectManagementParsedModelRow[]>();
  const [newDoc, setNewDoc] = useState<{ docType: string; label: string }>({ docType: '', label: '' });
  const [fieldsPrompt, setFieldsPrompt] = useState<FieldsPrompt>();
  // The project's chat. Opened on demand — the conversation is created by the method on first open, so
  // a project that nobody has messaged about never gets a row.
  const [chatConversationId, setChatConversationId] = useState<string>();
  const [chatOpen, setChatOpen] = useState(false);
  const [cashSuggestionChoice, setCashSuggestionChoice] = useState<{ [id: string]: string }>({});
  const [qbSyncing, setQbSyncing] = useState(false);
  const [suggestionsModal, setSuggestionsModal] = useState(false);
  useSubscribe(Publications.unreadMessageReceipts);
  // The project's conversation is published with the project detail, so we know its id on load (without
  // creating one) — this is what lets the Messages button badge unread messages before the chat is opened.
  const projectConversationId = useTracker(() => Conversations.findOne({ projectManagementProjectId: _id })?._id, [_id]);
  /** Opens the project's chat, creating the conversation on first use. */
  const openChat = toastOnError(async () => {
    if (!_id) return;
    const conversation = chatConversationId || projectConversationId || (await projectManagementConversation.call(_id))._id;
    setChatConversationId(conversation);
    setChatOpen(true);
  });
  const chatUnread = useTracker(() => {
    const cid = chatConversationId || projectConversationId;
    return cid ? MessageReceipts.find({ conversationId: cid, readAt: { $exists: false } }).count() : 0;
  }, [chatConversationId, projectConversationId]);
  // Arriving from a message email or the bell (…?chat=1) lands in the chat, not on the project page.
  useEffect(() => {
    if (!_id) return;
    if (queryStringToObject().chat) openChat();
  }, [_id]);
  const [showStakeholder, setShowStakeholder] = useState<{ stakeholder?: ProjectManagementStakeholder }>();
  const [historyParam, setHistoryParam] = useState<ProjectManagementParameter>();
  const [paramModal, setParamModal] = useState<{ parameter?: ProjectManagementParameter, taskId?: string }>();
  const [docModalId, setDocModalId] = useState<string>();  // Tasks tab: doc whose card is open in a modal
  const [parameterSearch, setParameterSearch] = useState('');
  const [milestoneSearch, setMilestoneSearch] = useState('');
  const [milestonesCompact, setMilestonesCompact] = useState<boolean>(() => typeof localStorage !== 'undefined' && localStorage.getItem('pm.project.milestonesCompact') === '1');
  /** Toggle the milestone timeline between full and compact views (persisted). */
  const toggleMilestonesCompact = () => {
    const next = !milestonesCompact;
    setMilestonesCompact(next);
    try { localStorage.setItem('pm.project.milestonesCompact', next ? '1' : '0'); }
    catch (_) { /* private mode */ }
  };
  // Attention Now collapses to a single summary line by default — expanded it competes with the tabs
  // below it. Persisted per browser, like the milestone timeline's compact mode.
  const [attentionOpen, setAttentionOpen] = useState<boolean>(() => typeof localStorage !== 'undefined' && localStorage.getItem('pm.project.attentionOpen') === '1');
  /** Expand or collapse the Attention Now card (persisted). */
  const toggleAttention = () => {
    const next = !attentionOpen;
    setAttentionOpen(next);
    try { localStorage.setItem('pm.project.attentionOpen', next ? '1' : '0'); }
    catch (_) { /* private mode */ }
  };
  // Documents tab tree: which Milestone / Task groups the user has collapsed, persisted per browser.
  // Everything starts expanded — the tab was one flat list, so defaulting to collapsed would hide rows
  // people are used to seeing.
  const [collapsedDocGroups, setCollapsedDocGroups] = useState<Set<string>>(() => {
    try { return new Set<string>(JSON.parse(localStorage.getItem('pm.project.docTreeCollapsed') || '[]')); }
    catch (_) { return new Set<string>(); }
  });
  /** Expand or collapse one group of the Documents tree (persisted). */
  const toggleDocGroup = (key: string) => setCollapsedDocGroups((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    try { localStorage.setItem('pm.project.docTreeCollapsed', JSON.stringify([...next])); }
    catch (_) { /* private mode — the choice just won't persist */ }
    return next;
  });
  const [taskSearch, setTaskSearch] = useState('');
  const [taskMilestoneFilter, setTaskMilestoneFilter] = useState('');
  // Wizard strip: the selected bucket (undefined → first bucket with an open task), and the task whose
  // full card is open in the modal.
  const [taskBucket, setTaskBucket] = useState<string>();
  const [taskCardId, setTaskCardId] = useState<string>();
  // Notes posted in this session, shown until the publication echoes them (see submitTaskNote).
  const [postedNotes, setPostedNotes] = useState<ProjectManagementTaskNote[]>([]);
  // The feed's own state: a per-task composer draft, and the note currently being edited in place.
  const [noteComposer, setNoteComposer] = useState<Record<string, string>>({});
  const [editingNoteId, setEditingNoteId] = useState<string>();
  const [viewerFile, setViewerFile] = useState<{ file: FileMeta, title?: string, downloadName?: string }>();
  // An archive of photographs opens as a gallery rather than downloading — see DriveGalleryModal.
  const [galleryVersion, setGalleryVersion] = useState<{ versionId: string, title: string }>();
  const [editingNoteBody, setEditingNoteBody] = useState('');
  const [budgetMilestoneFilter, setBudgetMilestoneFilter] = useState('');
  // The Budget record whose consolidated modal is open — the one modal every Budget table's rows lead to.
  const [budgetRecord, setBudgetRecord] = useState<{ kind: BudgetRecordKind; id: string }>();
  /**
   * A change order's value, red with a tooltip when it is over the lifecycle's IC threshold. The colour
   * IS the flag — it used to carry a "Requires IC Approval" line of its own, which said in a sentence what
   * a red figure says at a glance. Rejected ones are never flagged: nobody needs to approve them.
   * @param co - the change order
   */
  const changeOrderValue = (co: ProjectManagementChangeOrder) => {
    const pct = lifecycle?.changeOrderApprovalPct;
    const needsIc = co.status !== 'rejected' && projectManagementChangeOrderNeedsIc(co.value, pct, cashEvents);
    if (!needsIc) return cents(co.value);
    const threshold = projectManagementChangeOrderIcThreshold(pct, cashEvents);
    return (
      <span
        className="needs-ic"
        data-tooltip-id="pm-task-tip"
        data-tooltip-content={strings.RequiresIcApprovalHint(pct as number, cents(threshold))}
      >
        {cents(co.value)}
      </span>
    );
  };
  const [allDocsSearch, setAllDocsSearch] = useState('');
  const [contentHits, setContentHits] = useState<{ [documentId: string]: string }>({});
  // Content search: debounce, then ask the server which docs' extracted text contains the query.
  useEffect(() => {
    const query = allDocsSearch.trim();
    if (!_id || query.length < 2) {
      setContentHits({});
      return undefined;
    }
    const timer = setTimeout(async () => {
      try {
        const hits = await projectManagementSearchDocuments.call({ projectManagementProjectId: _id, query });
        setContentHits(Object.fromEntries(hits.map((h) => [h.documentId, h.snippet])));
      }
      catch (_) { /* content search is best-effort; title filtering still applies */ }
    }, 350);
    return () => clearTimeout(timer);
  }, [allDocsSearch, _id]);
  const [stakeholderSearch, setStakeholderSearch] = useState('');
  const [hiddenEventKinds, setHiddenEventKinds] = useState<Set<string>>(new Set());  // History feed: kinds the user has muted
  const [activeTab, setActiveTab] = useState<string>(() => {
    const stored = (typeof localStorage !== 'undefined' && localStorage.getItem('pm.project.tab')) || 'delivery';
    return stored === 'milestones' || stored === 'work' ? 'delivery' : stored;  // migrate the now-merged tab keys
  });
  const selectTab = (t: string) => {
    setActiveTab(t);
    try { localStorage.setItem('pm.project.tab', t); }
    catch (_) { /* private mode */ }
  };
  // Sub-tab within the merged Delivery tab (Milestones / Tasks / Documents / Stakeholders).
  // Reactive: subscribe to the project's data and compose the detail bundle from Minimongo, so edits by
  // anyone (this user or a teammate) reflect live without an explicit reload.
  const loading = useSubscribe(Publications.projectManagementProjectDetail, _id);
  const detail = useTracker<ProjectManagementProjectDetail | undefined>(() => {
    if (!_id) return undefined;
    const project = ProjectManagementProjects.findOne(_id);
    if (!project) return undefined;
    const byProject = { projectManagementProjectId: _id };
    const documents: ProjectManagementDocumentWithVersions[] = ProjectManagementDocuments.find(byProject, { sort: { stage: 1 } }).fetch().map((doc) => {
      const versions = ProjectManagementDocumentVersions.find({ projectManagementDocumentId: doc._id }, { sort: { versionNumber: -1 } }).fetch();
      return { ...doc, versions, currentVersion: versions.find((v) => v._id === doc.currentVersionId) };
    });
    const inventorySites = InventorySites.find({ projectManagementProjectId: _id }, { sort: { siteId: 1 } }).fetch();
    const inventorySiteIds = inventorySites.map((site) => site.siteId);
    return {
      project,
      events: ProjectManagementProjectEvents.find(byProject, { sort: { createdAt: -1 } }).fetch(),
      tasks: ProjectManagementTasks.find(byProject, { sort: { stage: 1 } }).fetch(),
      documents,
      milestones: ProjectManagementMilestones.find(byProject, { sort: { order: 1 } }).fetch(),
      fundingSources: ProjectManagementFundingSources.find(byProject, { sort: { createdAt: 1 } }).fetch(),
      cashEvents: ProjectManagementCashEvents.find(byProject, { sort: { paidDate: 1, createdAt: 1 } }).fetch(),
      parameters: ProjectManagementParameters.find(byProject, { sort: { name: 1 } }).fetch(),
      stakeholders: ProjectManagementStakeholders.find(byProject, { sort: { createdAt: 1 } }).fetch(),
      stakeholderProfiles: ProjectManagementStakeholderProfiles.find({}, { sort: { name: 1 } }).fetch(),
      changeOrders: ProjectManagementChangeOrders.find(byProject, { sort: { createdAt: 1 } }).fetch(),
      purchaseOrders: ProjectManagementPurchaseOrders.find(byProject, { sort: { orderDate: 1, createdAt: 1 } }).fetch(),
      inventorySites: inventorySites,
      inventoryPallets: InventoryPallets.find({ assignedSiteId: { $in: inventorySiteIds } }, { sort: { palletNumber: 1 } }).fetch(),
      inventoryDeliveries: InventoryDeliveries.find({ siteId: { $in: inventorySiteIds } }, { sort: { createdAt: -1 } }).fetch(),
      contacts: ProjectManagementContacts.find(byProject, { sort: { name: 1 } }).fetch(),
      links: ProjectManagementLinks.find(byProject, { sort: { label: 1 } }).fetch(),
      cashSuggestions: ProjectManagementCashSuggestions.find(byProject).fetch(),
      invoices: ProjectManagementProjectInvoices.find(byProject, { sort: { createdAt: -1 } }).fetch(),
      taskNotes: ProjectManagementTaskNotes.find(byProject, { sort: { createdAt: -1 } }).fetch(),
    };
  }, [_id]);

  // Proposed AI suggestions for this project's parameters (admin pub only). Review-before-apply: these
  // are shown against the parameter they'd fill and are never written until accepted.
  const suggestions = useTracker<ProjectManagementParameterSuggestion[]>(
    () => ProjectManagementParameterSuggestions.find({ projectManagementProjectId: _id, status: 'proposed' }).fetch(), [_id],
  );
  // Send-a-link consumption capture status (admin pub only; members never receive these).
  const pmUtilityAccount = useTracker(() => UtilityAccounts.findOne({ projectManagementProjectId: _id }), [_id]);
  const pmUtilityMeterCount = useTracker(() => (pmUtilityAccount ? ConsumptionMeters.find({ utilityAccountId: pmUtilityAccount._id }).count() : 0), [pmUtilityAccount?._id]);
  const portfolio = useTracker(() => {
    const pid = detail?.project?.portfolioId;
    return pid ? ProjectManagementPortfolios.findOne(pid) : undefined;
  }, [detail?.project?.portfolioId]);
  const portfolioName = portfolio?.name;
  // Owner / EPC resolve from the org registries, so a rename or a freshly-found logo shows up here with
  // no per-project write, and the background logo lookup repaints the header when it lands. Read via
  // `detail?.` and declared with the other hooks: below the early returns these ran conditionally,
  // which is a hook-order violation ("rendered more hooks than during the previous render").
  const projectOwner = useTracker(() => {
    const ownerId = detail?.project?.ownerId;
    return ownerId ? ProjectManagementOwners.findOne(ownerId) : undefined;
  }, [detail?.project?.ownerId]);
  const projectEpc = useTracker(() => {
    const epcId = detail?.project?.epcId;
    return epcId ? ProjectManagementEpcs.findOne(epcId) : undefined;
  }, [detail?.project?.epcId]);
  const projectUtility = useTracker(() => {
    const utilityId = detail?.project?.utilityId;
    return utilityId ? ProjectManagementUtilities.findOne(utilityId) : undefined;
  }, [detail?.project?.utilityId]);

  // Attention Now: what is late, then what lands next, across open tasks and incomplete milestones.
  // Reads through `detail?.` and sits with the other hooks — below the early returns it would run only
  // once detail had loaded, which is a hook-order violation.
  const attention = useMemo(() => {
    const now = Date.now();
    const days = (d: Date) => Math.round((now - new Date(d).getTime()) / 86400000);
    // Tasks are already publication-scoped to the caller's own. Milestones are members' read-only context
    // (they can't act on them), so a member's Attention Now lists only their own tasks; admins get both.
    const rows = [
      ...(detail?.tasks || [])
        .filter((t) => t.status !== 'done' && t.estimatedDate)
        .map((t) => ({ kind: 'task' as const, id: t._id, label: t.label, role: t.role, date: t.estimatedDate as Date, overdue: days(t.estimatedDate as Date) })),
      ...(isAdmin ? (detail?.milestones || [])
        .filter((m) => !m.actualDate && m.plannedDate)
        .map((m) => ({ kind: 'milestone' as const, id: m._id, label: m.label, role: m.ownerRole, date: m.plannedDate as Date, overdue: days(m.plannedDate as Date) })) : []),
    ];
    // Most overdue first, then the nearest upcoming — the order you would work them in.
    rows.sort((a, b) => b.overdue - a.overdue);
    // The overdue count is of EVERYTHING late, not just the rows that survive the cap — a summary
    // saying "3 overdue" while the list shows six items would be lying about the smaller number.
    return { rows: rows.slice(0, ATTENTION_ROWS), overdue: rows.filter((r) => r.overdue > 0).length, total: rows.length };
  }, [detail?.tasks, detail?.milestones, isAdmin]);

  if (!canAccess) return <NotPermittedPage />;
  if (loading()) return <ProjectManagementProjectPageStyled><LoadingFlex /></ProjectManagementProjectPageStyled>;
  if (!detail) return <ProjectManagementProjectPageStyled><PageBody>{strings.CouldNotLoadProject()}</PageBody></ProjectManagementProjectPageStyled>;

  const { project, tasks, documents, events, milestones, fundingSources, cashEvents, parameters, stakeholders, stakeholderProfiles, changeOrders, purchaseOrders, inventorySites, inventoryPallets, inventoryDeliveries, contacts, links, cashSuggestions, invoices, taskNotes } = detail;

  /** One-line headline for a timeline event, rendered from its structured fields (stage moves show from→to). */
  const renderEventHeadline = (e: ProjectManagementProjectEvent) => {
    if (e.kind === 'stageTransition') {
      const ch = e.changes?.[0];
      const from = ch?.from != null ? (lifecycle ? projectManagementStageLabel(lifecycle, String(ch.from)) : String(ch.from)) : '';
      const to = ch?.to != null ? (lifecycle ? projectManagementStageLabel(lifecycle, String(ch.to)) : String(ch.to)) : '';
      const note = e.meta?.note;
      return <>{from ? `${from} → ` : ''}<b>{to}</b>{note ? ` — ${note}` : ''}</>;
    }
    if (e.kind === 'project' && e.action === 'statusChanged') {
      const st = e.changes?.find((c) => c.field === 'status');
      const note = e.changes?.find((c) => c.field === 'statusNote')?.to;
      const label = (v: unknown) => (v ? projectStatusLabel(v as ProjectManagementProjectStatus) : '');
      return <>{strings.Status()}: {st?.from != null ? `${label(st.from)} → ` : ''}<b>{label(st?.to)}</b>{note ? ` — ${note}` : ''}</>;
    }
    if (e.kind === 'project') return <b>{e.label || strings.ProjectCreated()}</b>;
    // Cash events tack on the amount for quick scanning of the ledger's history.
    const amount = e.kind === 'cashEvent' ? (e.meta?.actualAmount ?? e.meta?.estimatedAmount) : undefined;
    // A review decision carries the reviewer's note; History is where the deficiency log actually lives.
    return <><b>{e.label || ''}</b> {eventActionLabel(e.action)}{typeof amount === 'number' ? ` · ${money(amount)}` : ''}{e.meta?.note ? ` — ${e.meta.note}` : ''}</>;
  };
  const lifecycle = getProjectManagementLifecycle(project.lifecycleKey);
  const financeModel = projectManagementFinanceModel(lifecycle);
  // The Sources figure comes from wherever this vertical keeps it. RIC's sources are drawn against, so the
  // ledger is the figure; residential's are received on their own schedule, so until the money arrives the
  // ledger holds nothing and the sources themselves are the estimate. On the Uses side a Use paid out in
  // installments counts once: residential's dealer payments are its EPC Cost, not money on top of it.
  const fin = projectManagementFinancialSummary(
    projectManagementUsesLedger(cashEvents, lifecycle?.finance),
    projectManagementSourcesFigure(lifecycle) === 'fundingSources'
      ? { inEstimated: projectManagementFundingSourcesEstimate(fundingSources) }
      : undefined,
  );
  const coSummary = projectManagementChangeOrderSummary(changeOrders);
  // Luminia Fees (Dev fees + Tenant Billing) and the "Total fees" stat come from the shared helper, so this
  // page and the pipeline's portfolio-wide Total Fees card compute from one formula.
  const totalFees = projectManagementTotalFees(parameters, cashEvents);
  // The vertical's own Fees figure. RIC's is what Luminia makes, worked out from the fee parameters;
  // residential's is the fund's SGIP financing cost and fee, two of the model's Uses. A vertical with
  // neither shows no tile rather than a convincing $0.
  const feeUseCodes = projectManagementFeeUseCodes(lifecycle);
  const bannerFees = feeUseCodes
    ? projectManagementUseFees(cashEvents, feeUseCodes)
    : financeModel === 'ric' ? projectManagementCompleteProjectFees(totalFees, project, cashEvents) : undefined;
  const { devFeesEstimated, devFeesNet, tenantBillingEstimated, ancillary, waterfall, draws } = totalFees;
  // Age runs from the day the PPA was SIGNED, not from the db record: the record's createdAt is really
  // "when we last re-imported", which made every project the same couple of days old. The signature date is
  // the 'PPA Executed' parameter on the Contracts → PPA / Lease task — the contract's own execution date.
  // NOT the ppaExecuted milestone's actualDate, which is stamped with new Date() the moment someone marks
  // the milestone done, so a back-entered project read as 0 days old.
  const ppaExecutedParameter = parameters.find((x) => x.name === 'PPA Executed');
  const ppaSignedDate = ppaExecutedParameter
    && (projectManagementTracksActual(ppaExecutedParameter) ? ppaExecutedParameter.actualValue : ppaExecutedParameter.estimatedValue);
  const ppaSignedTime = ppaSignedDate ? new Date(ppaSignedDate as string | Date).getTime() : NaN;
  const projectAgeDays = Number.isFinite(ppaSignedTime)
    ? Math.floor((Date.now() - ppaSignedTime) / 86400000)
    : undefined;
  const ptoSource = projectManagementPtoEstimate(lifecycle);
  const ptoDate = ptoSource.from === 'milestone'
    ? milestones.find((m) => m.key === ptoSource.key)?.plannedDate
    : tasks.find((t) => t.templateKey === ptoSource.key)?.estimatedDate;
  // Same rounding as the Attention card, so the two never disagree about what is late.
  const ptoLate = ptoDate ? Math.round((Date.now() - new Date(ptoDate).getTime()) / 86400000) : undefined;
  // Sizes for the banner, by precedence: the parameter's ACTUAL, else its ESTIMATE, else the spine field the
  // tracker filled. Zero or a non-number counts as no value, so a solar-only site does not read "BESS 0.00 kWh".
  const sizeOf = (v: unknown) => { const n = typeof v === 'number' ? v : typeof v === 'string' && v.trim() !== '' ? Number(v) : NaN; return Number.isFinite(n) && n > 0 ? n : undefined; };
  const paramSize = (name: string) => {
    const p = parameters.find((x) => x.name === name);
    return sizeOf(p?.actualValue) ?? sizeOf(p?.estimatedValue);
  };
  const solarKwParam = paramSize('PV System Size');
  const bessKwhParam = paramSize('BESS Capacity (kWh)');
  const solarKw = solarKwParam ?? sizeOf(project.capacityKwDc);
  const bessKwh = bessKwhParam ?? sizeOf(project.storageKwh);
  const twoDp = (n: number) => n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const bannerMetrics = [
    {
      dot: projectManagementMetricDot(0),
      // The label names what the project actually has: Solar for capacity, BESS for storage, both when
      // both. Storage rides along in the value rather than taking a fifth box.
      label: [solarKw != null && strings.Solar(), bessKwh != null && strings.Bess()].filter(Boolean).join(' · ') || strings.Capacity(),
      // A non-breaking space keeps each figure with its unit; the tile wraps at the separator instead.
      value: [solarKw != null && `${twoDp(solarKw)}\u00a0kW`, bessKwh != null && `${twoDp(bessKwh)}\u00a0kWh`].filter(Boolean).join(' · ') || '—',
      sub: '',
    },
    // Sources / Uses show the estimate (the partner's Project Payment) with what has been invoiced beneath.
    // Super users and invoice approvers only, as the fees tile beside them (2026-10-20): the banner leads the
    // page, and the project's cash is not something accounting reads on the way past. The Budget tab's own
    // Sources and Uses tables are unchanged — that is where accounting works the ledger.
    ...(canSeeFees ? [
      { dot: projectManagementMetricDot(1), label: strings.CashSources(), value: money(fin.inEstimated), sub: `${strings.Invoiced()}: ${money(fin.inActual)}` },
      { dot: projectManagementMetricDot(2), label: strings.CashUses(), value: money(fin.outEstimated), sub: `${strings.Invoiced()}: ${money(fin.outActual)}` },
    ] : []),
    // The Luminia fee belongs to the RIC money model; a vertical without one shows no tile rather than $0.
    // projectManagementTotalFees: estimated dev fee + tenant billing, tranche-released beneath.
    ...(bannerFees && canSeeFees ? [{ dot: projectManagementMetricDot(3), label: strings.Fees(), value: money(bannerFees.estimated), sub: `${strings.Actual()}: ${money(bannerFees.actual)}` }] : []),
    {
      dot: projectManagementMetricDot(4), label: strings.EstimatedPto(),
      value: ptoDate ? new Date(ptoDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' }) : '—',
      sub: ptoLate == null ? strings.NotScheduled() : ptoLate > 0 ? strings.DaysLate(ptoLate) : strings.OnSchedule(),
    },
  ];
  // Member banner: capacity, THEIR task completion, and the estimated PTO only. `tasks` is already scoped to
  // the member's own; and since they don't own the PTO task, its date rides in on the 'substantial'
  // (Substantial Completion / PTO) milestone plannedDate — the same est-dates source.
  const memberDoneTasks = tasks.filter((t) => t.status === 'done').length;
  const memberTaskPct = tasks.length ? Math.round((memberDoneTasks / tasks.length) * 100) : 0;
  const memberPtoDate = ptoDate || milestones.find((m) => m.key === 'substantial')?.plannedDate;
  const memberPtoLate = memberPtoDate ? Math.round((Date.now() - new Date(memberPtoDate).getTime()) / 86400000) : undefined;
  const memberBannerMetrics = [
    bannerMetrics[0],
    {
      dot: projectManagementMetricDot(1), label: strings.TaskCompletion(),
      value: `${memberTaskPct}%`, sub: `${memberDoneTasks} / ${tasks.length} ${strings.Tasks()}`,
    },
    {
      dot: projectManagementMetricDot(2), label: strings.EstimatedPto(),
      value: memberPtoDate ? new Date(memberPtoDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' }) : '—',
      sub: memberPtoLate == null ? strings.NotScheduled() : memberPtoLate > 0 ? strings.DaysLate(memberPtoLate) : strings.OnSchedule(),
    },
  ];
  // Identity (name/kind/email) lives on the org-level StakeholderProfile now; a membership points at one.
  const profileById = new Map(stakeholderProfiles.map((p) => [p._id, p]));
  const profileOfStakeholder = (s?: ProjectManagementStakeholder) => (s ? profileById.get(s.profileId) : undefined);
  const stakeholderName = (id?: string) => profileOfStakeholder(stakeholders.find((s) => s._id === id))?.name;
  const stakeholderOptions = [{ value: '', label: '—' }, ...stakeholders.map((s) => ({ value: s._id, label: profileOfStakeholder(s)?.name || '—' }))];
  const projectTabs = isAdmin ? [
    { key: 'parameters', label: strings.ProjectDetails() },
    // Budget is for finance viewers (super users, or accounting + PM) — build cost, Luminia's fee, and the
    // cash ledger are not every PM admin's business (same rule as the metrics banner).
    ...(canSeeFinances ? [{ key: 'finances', label: strings.Budget() }] : []),
    { key: 'delivery', label: strings.Tasks() },
    { key: 'allDocuments', label: strings.Documents() },
    { key: 'schedule', label: strings.Schedule() },
    { key: 'contacts', label: strings.Contacts() },
    { key: 'history', label: strings.History() },
    // The Stakeholders tab is hidden (2026-09-22). Its panel below is left intact for when it returns; a
    // stored 'stakeholders' selection falls back to the first tab, like any key no longer offered.
  ] : canSeeFinances ? [
    // Finance viewer who is not an admin (accounting + PM): may open any org project, but sees only the
    // Budget tab for now.
    { key: 'finances', label: strings.Budget() },
  ] : [
    { key: 'delivery', label: strings.Tasks() },
    // Members get the calendar too, over their own tasks only — the milestone layer is withheld below.
    { key: 'schedule', label: strings.Schedule() },
    // EPC invoices: free-form upload/manage; super users see them in the Budget tab.
    { key: 'invoices', label: strings.Invoices() },
    // Members get a Documents tab too, scoped to the docs their own tasks deliver (visibleDocuments).
    { key: 'allDocuments', label: strings.Documents() },
  ];
  // Effective tab selection — clamped to what this user may actually see, so a stored key that is no
  // longer offered (a member's 'finances', an org admin's 'stakeholders', a key retired later) falls back
  // to the user's first tab rather than leaving the content column blank.
  const tab = projectTabs.some((t) => t.key === activeTab) ? activeTab : projectTabs[0].key;

  /**
   * Toggle a task's done/open status.
   * @param taskId - task to update
   * @param done - whether the task is checked
   */
  const toggleTask = toastOnError(async (taskId: string, done: boolean) => {
    await projectManagementSetTaskStatus.call({ taskId, status: done ? 'done' : 'open' });
  });
  // Excusing a requirement, or putting it back, re-evaluates the task server-side — so a card can complete
  // or reopen on the click, which is the point of the flag.
  const setParameterOptional = toastOnError(async (parameterId: string, optional: boolean) => {
    await projectManagementSetParameterOptional.call({ parameterId, optional });
  });
  const setDocumentOptional = toastOnError(async (documentId: string, optional: boolean) => {
    await projectManagementSetDocumentOptional.call({ documentId, optional });
  });
  /**
   * Upload a new version of a document.
   * @param doc - document to version
   * @param file - uploaded file
   */
  // Upload a document for a task directly from the Tasks tab — lands it in the docType slot (creating it if
  // missing); approval then flows through the normal review + the task's auto-complete.
  const uploadTaskDoc = toastOnError(async (docType: string, file: any) => {
    await projectManagementBatchUploadDocument.call({ projectManagementProjectId: project._id, docType, file });
    toast.success(strings.Done());
  });
  // Project invoices — free-form CRUD. Members upload/manage their own; super users also see them (Budget tab).
  // Upload prompts for the vendor's invoice number (= the QuickBooks Bill no. / DocNumber) and amount.
  const uploadInvoice = async (file: any) => {
    // Best-effort AI read of the invoice to pre-fill vendor + number + amount; the user confirms/edits before saving.
    const tid = toast.loading(strings.ReadingInvoice());
    let parsed: { vendor?: string, invoiceNumber?: string, amount?: number } = {};
    try { parsed = await projectManagementParseInvoice.call({ file }) || {}; }
    catch (_) { /* best-effort — fall back to manual entry */ }
    finally { toast.dismiss(tid); }
    setFieldsPrompt({
    title: strings.UploadInvoice(),
    fields: [
      { key: 'label', label: strings.Name(), default: file?.name || file?.filename || '' },
      { key: 'vendor', label: strings.Vendor(), default: parsed.vendor || '' },
      { key: 'invoiceNumber', label: strings.InvoiceNumber(), default: parsed.invoiceNumber || '' },
      { key: 'amount', label: strings.Amount(), type: 'currency', default: parsed.amount != null ? String(parsed.amount) : '' },
      { key: 'memo', label: strings.Memo(), default: '' },
    ],
    submit: toastOnError(async (v) => {
      await projectManagementCreateInvoice.call({
        projectManagementProjectId: project._id, file, label: v.label || file?.name || 'Invoice',
        vendor: v.vendor?.trim() || undefined,
        invoiceNumber: v.invoiceNumber?.trim() || undefined, amount: v.amount ? Number(v.amount) : undefined,
        memo: v.memo?.trim() || undefined,
      });
      toast.success(strings.Done());
    }),
    });
  };
  const editInvoice = (invoice: ProjectManagementProjectInvoice) => {
    const su = !!user?.isSuperUser;  // only a super user edits the Luminia (QB-matching) number
    setFieldsPrompt({
      title: strings.EditInvoice(),
      fields: [
        { key: 'label', label: strings.Name(), default: invoice.label },
        { key: 'vendor', label: strings.Vendor(), default: invoice.vendor },
        { key: 'invoiceNumber', label: strings.InvoiceNumber(), default: invoice.invoiceNumber },
        ...(su ? [{ key: 'luminiaInvoiceNumber', label: strings.LuminiaInvoiceNumber(), default: invoice.luminiaInvoiceNumber }] : []),
        { key: 'amount', label: strings.Amount(), type: 'currency' as const, default: invoice.amount != null ? String(invoice.amount) : '' },
        { key: 'memo', label: strings.Memo(), default: invoice.memo },
      ],
      submit: toastOnError(async (v) => {
        await projectManagementUpdateInvoice.call({
          _id: invoice._id, label: v.label || invoice.label, vendor: v.vendor ?? '', invoiceNumber: v.invoiceNumber ?? '',
          ...(su ? { luminiaInvoiceNumber: v.luminiaInvoiceNumber ?? '' } : {}),
          amount: v.amount ? Number(v.amount) : null, memo: v.memo ?? '',
        });
      }),
    });
  };
  const deleteInvoice = (id: string) => promptDelete(() => projectManagementDeleteInvoice.call({ _id: id }));
  // A correction, not a rejection: 'reject' soft-deletes the invoice, this only clears the approval. Behind
  // a confirm and off the review modal, which exists for a FIRST decision with the file on screen.
  const unapproveInvoice = (id: string) => setConfirm({
    title: strings.Unapprove(),
    body: strings.UnapproveInvoiceConfirm(),
    confirmText: strings.Unapprove(),
    run: toastOnError(() => projectManagementDecideInvoice.call({ _id: id, decision: 'unapprove' })),
  });
  const canEditInvoice = (inv: ProjectManagementProjectInvoice) => !!user?.isSuperUser || inv.createdByUserId === user?._id;
  // filter() returns a fresh array, so sorting it in place leaves the shared cashEvents list alone.
  const outUses = cashEvents.filter((c) => c.direction === 'out').sort(byCashEventLabel);
  const invoiceAllocationSummary = (inv: ProjectManagementProjectInvoice) =>
    (inv.allocations || []).map((a) => { const u = outUses.find((c) => c._id === a.cashEventId); return `${u?.label || u?.milestoneKey || '?'} ${cents(a.amount)}`; }).join(' · ');
  // Members see their own number; admins see the Luminia (QB-matching) number, annotated with the EPC's
  // original in parentheses when the two differ.
  const invoiceNumberDisplay = (inv: ProjectManagementProjectInvoice) => projectManagementInvoiceNumberLabel(inv, isAdmin);
  // Paid / Billed / — all come from the QuickBooks reconciliation fields, not from any status the app sets,
  // so each one says which it is and where it came from.
  const invoiceStatusHint = (inv: ProjectManagementProjectInvoice) =>
    (inv.paidAt ? strings.InvoicePaidHint() : inv.qbBillId ? strings.InvoiceBilledHint() : strings.InvoiceNotBilledHint());
  /** The project's invoices: an upload control + a table. Members see name/number/amount and manage their own;
   *  super users (Budget tab) also allocate each invoice across cash 'uses' and see its billed/paid status. */
  // --- One modal per Budget record. Every table below opens it by row click, and it carries what used to
  //     be a trailing column of links spread over four separate modals: the record's detail, its
  //     edit/delete and any decision its kind allows, its attachments, and its history. Edit and delete
  //     still raise the shared fields form and the delete confirm on top — those are the app's forms, not
  //     per-row modals. ---
  const renderBudgetRecordModal = () => {
    if (!budgetRecord) return null;
    const close = () => setBudgetRecord(undefined);
    const { kind, id } = budgetRecord;
    const sep = <span className="sep">|</span>;
    const dateOf = (d?: Date) => formatCalendarDate(d) || '—';
    let title = '';
    let rows: { k: string, v: React.ReactNode }[] = [];
    let actions: React.ReactNode = null;
    const related: { title: string, rows: React.ReactNode[] }[] = [];
    let attachmentKind: ProjectManagementBudgetAttachmentKind | undefined;
    let eventKind: ProjectManagementEventKind | undefined;
    // One row of the Related section: what it is, what it is worth here, and a way through to it.
    const relatedRow = (key: string, label: string, amount: number | undefined, go: () => void) => (
      <div className="br-related-row" key={key}>
        <button className="link-btn" onClick={go}>{label}</button>
        <span className="amount">{amount != null ? cents(amount) : '—'}</span>
      </div>
    );

    if (kind === 'fundingSource') {
      const s = fundingSources.find((x) => x._id === id);
      if (!s) return null;
      const totals = projectManagementFundingSourceTotals(s, cashEvents);
      title = projectManagementFundingSourceTypeLabel(s.type);
      rows = [
        { k: strings.Type(), v: projectManagementFundingSourceTypeLabel(s.type) },
        { k: strings.Party(), v: stakeholderName(s.partyStakeholderId) || s.party || '—' },
        { k: strings.Estimated(), v: cents(totals.estimated) },
        { k: strings.Actual(), v: cents(totals.actual) },
      ];
      actions = isAdmin && <>
        <button className="link-btn" onClick={() => editFundingSource(s)}>{strings.Edit()}</button>
        {sep}
        <button className="link-btn" onClick={() => { close(); delFundingSource(s._id); }}>{strings.Delete()}</button>
      </>;
      attachmentKind = 'fundingSource';
      eventKind = 'fundingSource';
    }
    else if (kind === 'cashEvent') {
      const c = cashEvents.find((x) => x._id === id);
      if (!c) return null;
      // Variance waits for settlement, exactly as the table reads it.
      const variance = (c.paidDate && c.actualAmount != null && c.estimatedAmount != null) ? c.actualAmount - c.estimatedAmount : undefined;
      title = c.label || milestoneLabelFor(c.milestoneKey);
      rows = [
        { k: strings.Description(), v: c.label || '—' },
        { k: strings.Direction(), v: c.direction === 'in' ? strings.CashSources() : strings.CashUses() },
        { k: strings.Milestone(), v: milestoneLabelFor(c.milestoneKey) || '—' },
        ...(projectManagementDrawShare(c) != null ? [{ k: strings.ShareOfTotalSolarCost(), v: `${roundNumber((projectManagementDrawShare(c) as number) * 100, 2)}%` }] : []),
        { k: strings.Estimated(), v: cents(c.estimatedAmount) },
        { k: strings.Actual(), v: cents(c.actualAmount) },
        { k: strings.Variance(), v: variance != null ? cents(variance) : '—' },
        { k: strings.Invoiced(), v: dateOf(c.invoicedDate) },
        { k: strings.Paid(), v: dateOf(c.paidDate) },
        { k: strings.Reference(), v: c.reference || '—' },
        { k: strings.Payee(), v: c.payee || '—' },
      ];
      actions = isAdmin && <>
        <button className="link-btn" onClick={() => editCashEvent(c.direction, c)}>{strings.Edit()}</button>
        {sep}
        <button className="link-btn" onClick={() => { close(); delCashEvent(c._id); }}>{strings.Delete()}</button>
      </>;
      // The Use↔Invoice link lives in invoice.allocations; this is simply that read in reverse. The amount
      // shown is what the invoice put against THIS Use, not the invoice's own total — one invoice routinely
      // spans several Uses.
      const paying = invoices
        .map((inv) => ({ inv, alloc: (inv.allocations || []).find((a) => a.cashEventId === c._id) }))
        .filter((x) => x.alloc);
      if (paying.length) {
        related.push({
          title: strings.Invoices(),
          rows: paying.map(({ inv, alloc }) => relatedRow(inv._id, inv.label, alloc!.amount, () => setBudgetRecord({ kind: 'invoice', id: inv._id }))),
        });
      }
      attachmentKind = 'cashEvent';
      eventKind = 'cashEvent';
    }
    else if (kind === 'changeOrder') {
      const co = changeOrders.find((x) => x._id === id);
      if (!co) return null;
      title = co.title;
      rows = [
        { k: strings.Description(), v: co.title },
        { k: strings.Rationale(), v: co.rationale || '—' },
        { k: strings.Value(), v: changeOrderValue(co) },
        ...(canSeeFundingSources ? [{ k: strings.FundingSources(), v: co.fundingSourceId ? (fundingSourceOptions.find((o) => o.value === co.fundingSourceId)?.label || '—') : '—' }] : []),
        { k: strings.Status(), v: <Badge status={co.status} label={coStatusLabel[co.status]} /> },
        ...(co.decisionNote ? [{ k: strings.Note(), v: `${coStatusLabel[co.status]}: ${co.decisionNote}` }] : []),
      ];
      actions = isAdmin && <>
        {co.status === 'submitted' && <><button className="link-btn" onClick={() => setChangeOrderStatus(co, 'underReview')}>{strings.Review()}</button>{sep}</>}
        {co.status === 'underReview' && <><button className="link-btn" onClick={() => setChangeOrderStatus(co, 'accepted')}>{strings.Accept()}</button>{sep}</>}
        {co.status === 'underReview' && <><button className="link-btn" onClick={() => setChangeOrderStatus(co, 'rejected')}>{strings.Reject()}</button>{sep}</>}
        {(co.status === 'accepted' || co.status === 'rejected') && <><button className="link-btn" onClick={() => setChangeOrderStatus(co, 'underReview')}>{strings.Review()}</button>{sep}</>}
        <button className="link-btn" onClick={() => editChangeOrder(co)}>{strings.Edit()}</button>
        {sep}
        <button className="link-btn" onClick={() => { close(); delChangeOrder(co._id); }}>{strings.Delete()}</button>
      </>;
      // Provenance, not payment: the invoice this change order was raised from. Once accepted it is PAID
      // through the cash event below, which that invoice allocates to like any other Use.
      const raisedFrom = co.invoiceId ? invoices.find((inv) => inv._id === co.invoiceId) : undefined;
      if (raisedFrom) {
        related.push({
          title: strings.RaisedFromInvoice(),
          rows: [relatedRow(raisedFrom._id, raisedFrom.label, raisedFrom.amount, () => setBudgetRecord({ kind: 'invoice', id: raisedFrom._id }))],
        });
      }
      attachmentKind = 'changeOrder';
      eventKind = 'changeOrder';
    }
    else if (kind === 'purchaseOrder') {
      const po = purchaseOrders.find((x) => x._id === id);
      if (!po) return null;
      title = po.reference || strings.PurchaseOrders();
      rows = [
        { k: strings.Reference(), v: po.reference || '—' },
        { k: strings.OrderDate(), v: dateOf(po.orderDate) },
        { k: strings.EquipmentType(), v: po.equipmentType || '—' },
        { k: strings.Serials(), v: po.serials || '—' },
        { k: strings.Source(), v: po.source === 'inventory' ? strings.Inventory() : strings.External() },
        { k: strings.Supplier(), v: stakeholderName(po.supplierStakeholderId) || '—' },
        { k: strings.Value(), v: cents(po.value) },
        { k: strings.Note(), v: po.note || '—' },
      ];
      actions = isAdmin && <>
        <button className="link-btn" onClick={() => editPurchaseOrder(po)}>{strings.Edit()}</button>
        {sep}
        <button className="link-btn" onClick={() => { close(); delPurchaseOrder(po._id); }}>{strings.Delete()}</button>
      </>;
      attachmentKind = 'purchaseOrder';
      eventKind = 'purchaseOrder';
    }
    else {
      const inv = invoices.find((x) => x._id === id);
      if (!inv) return null;
      title = inv.label;
      rows = [
        { k: strings.Name(), v: inv.label },
        { k: strings.Vendor(), v: inv.vendor || '—' },
        { k: isAdmin ? strings.LuminiaInvoiceNumber() : strings.InvoiceNumber(), v: invoiceNumberDisplay(inv) },
        { k: strings.Amount(), v: inv.amount != null ? cents(inv.amount) : '—' },
        { k: strings.UploadedBy(), v: `${inv.createdByName || '—'}${inv.createdAt ? ` · ${new Date(inv.createdAt).toLocaleDateString()}` : ''}` },
        ...(isAdmin ? [{ k: strings.Allocated(), v: invoiceAllocationSummary(inv) || '—' }] : []),
        // Reconciliation against QuickBooks, which reads like an approval state and is not one — hence the
        // hint on each of the three. The sync restates it every run, so Paid can go back to Billed.
        ...(isAdmin ? [{
          k: strings.Status(),
          v: (
            <span data-tooltip-id="pm-task-tip" data-tooltip-content={invoiceStatusHint(inv)}>
              {inv.paidAt ? <Badge status="approved" label={strings.Paid()} /> : inv.qbBillId ? <Badge status="pending" label={strings.Billed()} /> : '—'}
            </span>
          ),
        }] : []),
        ...(canSeeFinances ? [{
          k: strings.Approved(),
          v: inv.approvedAt ? `${inv.approvedByName || '—'} · ${new Date(inv.approvedAt).toLocaleDateString()}` : strings.NotYetApproved(),
        }] : []),
      ];
      actions = <>
        {
          inv.file && (
            fileIsViewable(inv.file)
              ? <button className="link-btn dl" onClick={() => setViewerFile({ file: inv.file!, title: inv.label })}>{strings.View()}</button>
              : <a className="dl" href={fileHref(inv.file)} target="_blank" rel="noreferrer">{strings.Download()}</a>
          )
        }
        {isAdmin && !inv.paidAt && <>{inv.file && sep}<button className="link-btn" onClick={() => setAllocating(inv)}>{strings.Allocate()}</button></>}
        {canApproveInvoices && !inv.approvedAt && <>{sep}<button className="link-btn" onClick={() => setReviewing(inv)}>{strings.Approve()}</button></>}
        {canApproveInvoices && inv.approvedAt && <>{sep}<button className="link-btn" onClick={() => { close(); unapproveInvoice(inv._id); }}>{strings.Unapprove()}</button></>}
        {canEditInvoice(inv) && <>
          {sep}
          <button className="link-btn" onClick={() => editInvoice(inv)}>{strings.Edit()}</button>
          {sep}
          <button className="link-btn" onClick={() => { close(); deleteInvoice(inv._id); }}>{strings.Delete()}</button>
        </>}
      </>;
      eventKind = 'invoice';
      // Both directions of the invoice's reach: the Uses it allocates to, and any change order it raised.
      const allocated = (inv.allocations || [])
        .map((a) => ({ a, use: cashEvents.find((c) => c._id === a.cashEventId) }))
        .filter((x) => x.use);
      const raised = changeOrders.filter((co) => co.invoiceId === inv._id);
      if (allocated.length) {
        related.push({
          title: strings.Allocated(),
          rows: allocated.map(({ a, use }) => relatedRow(use!._id, use!.label || milestoneLabelFor(use!.milestoneKey), a.amount, () => setBudgetRecord({ kind: 'cashEvent', id: use!._id }))),
        });
      }
      // Separate heading, deliberately: a change order raised here is not allocated money until it is
      // accepted, and listing it under Allocated would say it was. Its status rides along to say so.
      if (raised.length) {
        related.push({
          title: strings.ChangeOrders(),
          rows: raised.map((co) => relatedRow(co._id, `${co.title} (${coStatusLabel[co.status]})`, co.value, () => setBudgetRecord({ kind: 'changeOrder', id: co._id }))),
        });
      }
      // Invoices carry their own file rather than budget attachments, and post no project events — so
      // neither section applies to them.
    }

    const attachments = attachmentKind ? budgetDocsFor(attachmentKind, id) : [];
    const history = eventKind ? events.filter((e) => e.kind === eventKind && e.ref?.id === id) : [];
    return (
      <Modal styled={BudgetRecordModalStyled} title={title} onClose={close}>
        <div className="br-layout">
          <div className="br-main">
            <div className="br-title">{strings.Details()}</div>
            <div className="br-fields">
              {rows.map((r) => <React.Fragment key={r.k}><span className="k">{r.k}</span><span className="v">{r.v}</span></React.Fragment>)}
            </div>
            {actions && <><div className="br-title">{strings.Actions()}</div><div className="br-actions">{actions}</div></>}
            {related.map((g) => <React.Fragment key={g.title}><div className="br-title">{g.title}</div>{g.rows}</React.Fragment>)}
            {
              attachmentKind &&
              <>
                <div className="br-title">{strings.Documents()}{attachments.length ? ` (${attachments.length})` : ''}</div>
                {
                  attachments.length ? attachments.map((d) => (
                    <div className="attachment-row" key={d._id}>
                      <Icon icon="docs" />
                      <span className="attachment-label">{d.label}</span>
                      <span className="doc-badge" data-tooltip-id="pm-task-tip" data-tooltip-content={strings.Manage()} onClick={() => setDocModalId(d._id)}><Badge status={d.status} label={statusLabel(d.status)} /></span>
                      {
                        d.currentVersion && versionOpenLink(d, d.currentVersion, d.label)
                      }
                      <span className="meta-row">v{d.versions.length}</span>
                    </div>
                  )) : <div className="br-empty">—</div>
                }
                {
                  isAdmin &&
                  <div className="attachment-uploader">
                    <FileInput
                      placeholder={strings.AddAttachment()}
                      onChange={toastOnError(async (file: FileMeta) => { await projectManagementAddBudgetAttachment.call({ kind: attachmentKind as ProjectManagementBudgetAttachmentKind, id, file }); })}
                    />
                  </div>
                }
              </>
            }
          </div>
          {
            // Invoices post no project events, so they get the detail column to themselves.
            eventKind &&
            <aside className="br-side">
              <div className="br-side-inner">
                <div className="br-title">{strings.History()}</div>
                <div className="br-scroll">
                  {
                    history.map((e) => (
                      <div className="transition" key={e._id}>
                        {renderEventHeadline(e)}
                        {e.changes?.map((c, i) => (
                          <div className="br-change" key={i}>
                            <span className="field">{budgetFieldLabel[c.field] || c.field}</span>
                            <span className="from">{budgetChangeValue(c.field, c.from)}</span>
                            <span className="arrow">→</span>
                            <span className="to">{budgetChangeValue(c.field, c.to)}</span>
                          </div>
                        ))}
                        <div className="when">{e.createdByName ? `${e.createdByName} · ` : ''}{e.createdAt && formatDateTime(e.createdAt)}</div>
                      </div>
                    ))
                  }
                  {!history.length && <div className="br-empty">{strings.NoHistory()}</div>}
                </div>
              </div>
            </aside>
          }
        </div>
      </Modal>
    );
  };
  const renderInvoices = () => (
    <>
      <div className="panel-tools"><span data-tooltip-id="pm-task-tip" data-tooltip-content={strings.UploadNewInvoice()}><FileInput placeholder={strings.UploadInvoice()} accept={user?.isSuperUser ? undefined : 'application/pdf'} maxSize={maxInvoiceUploadBytes} onChange={(file) => uploadInvoice(file)} /></span></div>
      {
        !invoices.length
          ? <div className="meta-row">{strings.NoInvoices()}</div>
          : <table className="fin-table">
            <thead><tr>
              <th>{strings.Name()}</th>
              <th>{strings.Vendor()}</th>
              <th data-tooltip-id="pm-task-tip" data-tooltip-content={strings.InvoiceNumberBrianHint()}>{isAdmin ? strings.LuminiaInvoiceNumber() : strings.InvoiceNumber()}</th>
              <th className="num">{strings.Amount()}</th>
              <th>{strings.UploadedBy()}</th>
              {isAdmin && <th data-tooltip-id="pm-task-tip" data-tooltip-content={strings.AllocateBrianHint()}>{strings.Allocated()}</th>}
              {isAdmin && <th>{strings.Status()}</th>}
              {canSeeFinances && <th>{strings.Approved()}</th>}
            </tr></thead>
            <tbody>
              {
                invoices.map((inv) => (
                  <tr className="br-row" key={inv._id} onClick={() => setBudgetRecord({ kind: 'invoice', id: inv._id })}>
                    <td>{inv.label}</td>
                    <td>{inv.vendor || '—'}</td>
                    <td>{invoiceNumberDisplay(inv)}</td>
                    <td className="num">{inv.amount != null ? cents(inv.amount) : '—'}</td>
                    <td className="invoice-uploader">{inv.createdByName || '—'}{inv.createdAt ? ` · ${new Date(inv.createdAt).toLocaleDateString()}` : ''}</td>
                    {isAdmin && <td>{invoiceAllocationSummary(inv) || '—'}</td>}
                    {isAdmin && <td>{inv.paidAt ? <Badge status="approved" label={strings.Paid()} /> : inv.qbBillId ? <Badge status="pending" label={strings.Billed()} /> : '—'}</td>}
                    {/* Finance viewers see approval status; the Approve action itself is in the record's
                        modal, which is where accounting's "no" is also decided. */}
                    {canSeeFinances && <td className={inv.approvedAt ? 'invoice-uploader' : undefined}>{
                      inv.approvedAt ? `${inv.approvedByName || '—'} · ${new Date(inv.approvedAt).toLocaleDateString()}` : strings.NotYetApproved()
                    }</td>}
                  </tr>
                ))
              }
            </tbody>
          </table>
      }
    </>
  );
  const uploadVersion = (doc: ProjectManagementDocumentWithVersions, file: any) => setFieldsPrompt({
    title: strings.UploadNewVersion(),
    fields: [{ key: 'changeNote', label: strings.ChangeNote() }],
    submit: toastOnError(async (v) => {
      await projectManagementAddDocumentVersion.call({ projectManagementDocumentId: doc._id, file, changeNote: v.changeNote || undefined });
    }),
  });
  /** Approve, reject, or revert-to-pending a document version (reject opens a modal for the reason). */
  // Returns true only when the call went through — toastOnError swallows the error and returns undefined,
  // which is what lets the approve path below close the document modal on success and leave it open on a
  // failure the user still needs to see.
  const doApproval = toastOnError(async (version: ProjectManagementDocumentVersion, status: 'approved' | 'rejected' | 'pending', rejectedReason?: string) => {
    await projectManagementSetVersionApproval.call({ versionId: version._id, status, rejectedReason });
    return true;
  });
  const setApproval = (version: ProjectManagementDocumentVersion, status: 'approved' | 'rejected' | 'pending') => {
    // Approve and revert-to-pending each ask for confirmation first; reject asks for a reason.
    if (status === 'approved') {
      // Approving finishes with the document, so its modal goes too and focus lands back on the task card
      // that opened it. A no-op on the Documents tab, where this same card renders without a modal.
      return setConfirm({
        title: strings.Approve(), body: strings.ApproveDocConfirm(), confirmText: strings.Approve(),
        run: async () => { if (await doApproval(version, 'approved')) setDocModalId(undefined); },
      });
    }
    if (status === 'pending') {
      return setConfirm({ title: strings.RevertToPending(), body: strings.RevertToPendingConfirm(), confirmText: strings.RevertToPending(), run: () => doApproval(version, 'pending') });
    }
    setFieldsPrompt({
      title: strings.Reject(),
      fields: [{ key: 'reason', label: strings.Reason(), required: true }],
      submitText: strings.Reject(),
      submit: (v) => doApproval(version, 'rejected', v.reason.trim()),
    });
  };
  /**
   * Make an older version the current one.
   * @param doc - document to revert
   * @param version - version to restore
   */
  const revert = toastOnError(async (doc: ProjectManagementDocumentWithVersions, version: ProjectManagementDocumentVersion) => {
    await projectManagementRevertVersion.call({ projectManagementDocumentId: doc._id, versionId: version._id });
  });
  /** The full document card (status, current version, approve/reject, version history, upload) — shared by the
   *  Documents sub-tab and the doc modal opened from a task's status badge. */
  const renderDocCard = (doc: ProjectManagementDocumentWithVersions) => (
    <div className="doc" key={doc._id}>
      <div className="doc-head">
        <span className="doc-label">{doc.label}</span>
        <Badge status={doc.status} label={statusLabel(doc.status)} />
        <AiReadIndicator doc={doc} />
      </div>
      {
        doc.requirement &&
        <div className="req">{doc.requirement === 'required' ? strings.Required() : strings.Conditional()}{doc.condition ? ` — ${doc.condition}` : ''}</div>
      }
      {
        doc.currentVersion ?
          <div className="current">
            {strings.CurrentVersion()}: v{doc.currentVersion.versionNumber} · {
              versionOpenLink(doc, doc.currentVersion, doc.label)
            }
          </div>
          :
          <div className="current meta-row">—</div>
      }
      {
        doc.currentVersion?.status === 'rejected' && doc.currentVersion.rejectedReason &&
        <div className="reject-reason">{strings.RejectedBecause(doc.currentVersion.rejectedReason)}</div>
      }
      <div className="actions">
        {
          canApproveDocuments && doc.currentVersion && doc.currentVersion.status === 'pending' &&
          <>
            <Button className="primary compact" onClick={() => setApproval(doc.currentVersion!, 'approved')}>{strings.Approve()}</Button>
            <Button className="secondary compact" onClick={() => setApproval(doc.currentVersion!, 'rejected')}>{strings.Reject()}</Button>
          </>
        }
        {
          // Undo an approve/reject (e.g. a fat-fingered approval) — back to pending without a re-upload.
          canApproveDocuments && doc.currentVersion && (doc.currentVersion.status === 'approved' || doc.currentVersion.status === 'rejected') &&
          <Button className="secondary compact" onClick={() => setApproval(doc.currentVersion!, 'pending')}>{strings.RevertToPending()}</Button>
        }
        {
          doc.versions.length > 0 &&
          <button className="link-btn" onClick={() => setExpanded({ ...expanded, [doc._id]: !expanded[doc._id] })}>
            {strings.VersionHistory()} ({doc.versions.length})
          </button>
        }
      </div>
      {
        expanded[doc._id] &&
        <div className="versions">
          {
            doc.versions.map((v) => (
              <div className="version" key={v._id}>
                <span>v{v.versionNumber}</span>
                <span className="when">{v.createdAt && formatDateTime(v.createdAt)}</span>
                <Badge status={v.status} label={statusLabel(v.status)} />
                {
                  versionOpenLink(doc, v, `${doc.label} · v${v.versionNumber}`, `v${v.versionNumber}`)
                }
                {v.changeNote && <span className="when">“{v.changeNote}”</span>}
                {v.rejectedReason && <span className="reject-reason">{strings.RejectedBecause(v.rejectedReason)}</span>}
                {canApproveDocuments && doc.currentVersionId !== v._id && <button className="link-btn" onClick={() => revert(doc, v)}>{strings.Revert()}</button>}
              </div>
            ))
          }
        </div>
      }
      <div className="uploader">
        <FileInput placeholder={strings.UploadNewVersion()} onChange={(file) => uploadVersion(doc, file)} />
      </div>
    </div>
  );
  /** Edit a milestone's planned or actual date via the date prompt (clearing the field unsets it). */
  const editMilestoneDate = (m: ProjectManagementMilestone, field: 'plannedDate' | 'actualDate') => setFieldsPrompt({
    title: m.label,
    fields: [{
      key: 'date', label: field === 'plannedDate' ? strings.Estimated() : strings.Actual(), type: 'date',
      default: m[field] ? new Date(m[field] as Date).toISOString().slice(0, 10) : '',
    }],
    submit: toastOnError(async (v) => {
      await projectManagementSetMilestoneDate.call({ milestoneId: m._id, field, date: v.date ? new Date(v.date) : null });
    }),
  });
  /**
   * Both of a task's reported dates in one modal, so the milestone ceiling and the check against it live
   * in a single place. The pickers carry a `max` of the milestone's corresponding date, and the values are
   * re-checked before the call because FieldsModal closes as it submits — a server rejection would
   * otherwise toast over a dismissed modal, taking the typed dates with it.
   */
  const editTaskDates = (t: ProjectManagementTask, milestone?: ProjectManagementMilestone) => setFieldsPrompt({
    title: t.label,
    fields: [
      { key: 'estimatedDate', label: strings.Estimated(), type: 'date', default: dateInputValue(t.estimatedDate), max: dateInputValue(milestone?.plannedDate) || undefined },
      { key: 'actualDate', label: strings.Actual(), type: 'date', default: dateInputValue(t.actualDate), max: dateInputValue(milestone?.actualDate) || undefined },
    ],
    submit: toastOnError(async (v) => {
      // Midnight UTC, which is how parameter dates ARE stored (1,506 of 1,506) and how the picker above
      // now reads them back. The local-midnight parse this replaced moved the date a day on every save.
      const parse = (s?: string) => parseCalendarDate(s) || null;
      const next = { estimatedDate: parse(v.estimatedDate), actualDate: parse(v.actualDate) };
      if (projectManagementTaskDateAfterMilestone(next.estimatedDate, milestone?.plannedDate)
        || projectManagementTaskDateAfterMilestone(next.actualDate, milestone?.actualDate)) {
        throw new Error(strings.TaskDateAfterMilestone());
      }
      for (const field of ['estimatedDate', 'actualDate'] as const) {
        const was = t[field] ? projectManagementDayStart(t[field] as Date) : undefined;
        const now = next[field] ? projectManagementDayStart(next[field] as Date) : undefined;
        if (was !== now) await projectManagementSetTaskDate.call({ taskId: t._id, field, date: next[field] });
      }
    }),
  });

  /**
   * Jump from a calendar pip to the task itself: switch to Tasks, drop the filters that could hide it and
   * search its label, expanding its bucket if the user had collapsed it.
   * @param taskId - task the calendar cell was showing
   */
  const openTaskFromSchedule = (taskId: string) => {
    const t = tasks.find((x) => x._id === taskId);
    if (!t) return;
    setTaskBucket(t.role || 'unassigned');
    setTaskCardId(taskId);
    selectTab('delivery');
  };

  /** Add (no argument) or edit a milestone via the fields modal. */
  const editMilestone = (m?: ProjectManagementMilestone) => setFieldsPrompt({
    title: m ? m.label : strings.AddMilestone(),
    fields: [
      { key: 'label', label: strings.Name(), default: m?.label },
      { key: 'phase', label: strings.Phase(), type: 'select', default: m?.phase || lifecycle?.phases[0]?.key || '', options: (lifecycle?.phases || []).map((p) => ({ value: p.key, label: p.label })) },
      { key: 'ownerRole', label: strings.Role(), type: 'select', default: m?.ownerRole || '', options: [{ value: '', label: '—' }, ...(lifecycle?.roles || []).map((r) => ({ value: r.key, label: r.label }))] },
      { key: 'plannedDate', label: strings.Estimated(), type: 'date', default: m?.plannedDate ? new Date(m.plannedDate).toISOString().slice(0, 10) : '' },
    ],
    submit: toastOnError(async (v) => {
      await projectManagementSaveMilestone.call({
        _id: m?._id, projectManagementProjectId: project._id, label: v.label || '',
        phase: v.phase || undefined, ownerRole: v.ownerRole || undefined, plannedDate: v.plannedDate ? new Date(v.plannedDate) : null,
      });
    }),
  });
  /** Delete a milestone. Tasks under it must first be moved to other milestones (required selects in the modal). */
  const removeMilestone = (m: ProjectManagementMilestone) => {
    const under = tasks.filter((t) => t.milestoneId === m._id);
    const others = milestones.filter((x) => x._id !== m._id);
    if (!under.length) return promptDelete(() => projectManagementDeleteMilestone.call({ milestoneId: m._id, taskMoves: [] }));
    if (!others.length) return toast.error(strings.NoOtherMilestoneForTasks());
    setFieldsPrompt({
      title: `${m.label} — ${strings.MoveTasksThenDelete()}`,
      fields: under.map((t) => ({
        key: t._id, label: t.label, type: 'select' as const, default: '',
        options: [{ value: '', label: '—' }, ...others.map((o) => ({ value: o._id, label: o.label }))],
      })),
      submit: toastOnError(async (v) => {
        if (under.some((t) => !v[t._id])) throw new Error(strings.SelectMilestoneForEveryTask());
        await projectManagementDeleteMilestone.call({ milestoneId: m._id, taskMoves: under.map((t) => ({ taskId: t._id, milestoneId: v[t._id] })) });
      }),
    });
  };
  /** Add (no argument) or edit a task via the fields modal. Milestone association is required. */
  const editTask = (t?: ProjectManagementTask) => setFieldsPrompt({
    title: t ? t.label : strings.AddTask(),
    fields: [
      { key: 'label', label: strings.Name(), default: t?.label },
      { key: 'description', label: strings.Description(), default: t?.description },
      { key: 'role', label: strings.Role(), type: 'select', default: t?.role || (lifecycle?.roles || [])[0]?.key || '', options: (lifecycle?.roles || []).map((r) => ({ value: r.key, label: r.label })) },
      { key: 'milestoneId', label: strings.Milestone(), type: 'select', default: t?.milestoneId || '', options: [{ value: '', label: '—' }, ...milestones.map((m) => ({ value: m._id, label: m.label }))] },
    ],
    submit: toastOnError(async (v) => {
      if (!v.milestoneId) throw new Error(strings.MilestoneRequired());
      await projectManagementSaveTask.call({
        _id: t?._id, projectManagementProjectId: project._id, label: v.label || '', description: v.description || '', role: v.role || '', milestoneId: v.milestoneId,
      });
    }),
  });
  /** Accept (write the estimate) or dismiss AI suggestions. Acceptance re-checks the blank rule server-side. */
  const decideSuggestions = toastOnError(async (ids: string[], accept: boolean) => {
    if (!ids.length) return;
    const res = await projectManagementDecideSuggestions.call({ suggestionIds: ids, accept });
    if (res.skipped) toast.info(`${res.accepted + res.rejected} ${strings.Done()} · ${res.skipped} ${strings.Skipped()}`);
    else toast.success(strings.Done());
  });
  /** Re-read a document with the LLM (admin-triggered; the upload path does this automatically). */
  const parseDocumentWithAi = toastOnError(async (documentId: string) => {
    const res = await projectManagementLlmParseDocument.call({ projectManagementDocumentId: documentId });
    toast.success(res.suggested ? `${res.suggested} ${strings.AiSuggested()}` : strings.NoAiSuggestions());
  });
  // Ctrl+Enter (Cmd+Enter on a Mac) submits, the convention for a multi-line composer in a work tool —
  // GitHub, Jira, Linear. Plain Enter stays a newline, which is what a notes field is for.
  const isSubmitChord = (e: React.KeyboardEvent) => (e.ctrlKey || e.metaKey) && e.key === 'Enter';
  /** Post the composer's text as a new note on the task, then clear the composer. The note is shown in
   *  the feed from the method's return, not from the publication round-trip: the feed re-renders on
   *  submit however long the subscription takes to echo the row (the echo replaces the local copy by id). */
  const submitTaskNote = toastOnError(async (t: ProjectManagementTask) => {
    const body = (noteComposer[t._id] || '').trim();
    if (!body) return;
    const _id = await projectManagementSaveTaskNote.call({ projectManagementTaskId: t._id, body });
    const createdByName = [account?.firstName, account?.lastName].filter(Boolean).join(' ') || user?.username || '';
    setPostedNotes((l) => [{ _id, projectManagementProjectId: t.projectManagementProjectId, projectManagementTaskId: t._id, body, active: true, createdAt: new Date(), createdByUserId: user?._id, createdByName }, ...l]);
    setNoteComposer((d) => { const next = { ...d }; delete next[t._id]; return next; });
  });
  /** Save an in-place edit of an existing note. */
  const saveTaskNoteEdit = toastOnError(async () => {
    const body = editingNoteBody.trim();
    if (!editingNoteId || !body) return;
    await projectManagementSaveTaskNote.call({ _id: editingNoteId, body });
    setEditingNoteId(undefined);
    setEditingNoteBody('');
  });
  const removeTaskNote = (note: ProjectManagementTaskNote) =>
    promptDelete(() => projectManagementDeleteTaskNote.call({ id: note._id }));
  /** Delete a task (completed included). Parameters unlink; the task's doc slots return to the Documents tab. */
  const removeTask = (t: ProjectManagementTask) => promptDelete(() => projectManagementDeleteTask.call({ taskId: t._id }));
  /** Add (no doc) or rename a document slot on a task. */
  const editTaskDoc = (t: ProjectManagementTask, doc?: ProjectManagementDocumentWithVersions) => setFieldsPrompt({
    title: doc ? doc.label : `${t.label} — ${strings.AddDocument()}`,
    fields: [{ key: 'label', label: strings.Name(), default: doc?.label }],
    submit: toastOnError(async (v) => {
      await projectManagementSaveTaskDocument.call({ taskId: t._id, documentId: doc?._id, label: v.label || '' });
    }),
  });
  /** Remove a document slot from a task (uploads survive as detached project documents). */
  const removeTaskDoc = (t: ProjectManagementTask, doc: ProjectManagementDocumentWithVersions) => promptDelete(() => projectManagementDeleteTaskDocument.call({ taskId: t._id, documentId: doc._id }));
  /** Send-a-link consumption capture: mint (or reuse) the project's utility-intake link and copy it. */
  const copyPmUtilityLink = toastOnError(async () => {
    const link = await projectManagementMakeUtilityAccountLink.call({ projectManagementProjectId: project._id });
    copyToClipboard(link);
    toast.success(strings.CopiedToClipboard());
  });
  /** Add or edit a funding source (debt / tax equity / loan / advance) via the fields modal. */
  const editFundingSource = (fs?: ProjectManagementFundingSource) => setFieldsPrompt({
    title: fs ? projectManagementFundingSourceTypeLabel(fs.type) : strings.AddFundingSource(),
    fields: [
      { key: 'type', label: strings.Type(), type: 'select', default: fs?.type || 'debt', options: projectManagementFundingSourceTypes.map((t) => ({ value: t, label: projectManagementFundingSourceTypeLabel(t) })) },
      { key: 'partyStakeholderId', label: strings.Stakeholder(), type: 'select', default: fs?.partyStakeholderId || '', options: stakeholderOptions },
      { key: 'party', label: strings.Party(), default: fs?.party },
      { key: 'estimatedAmount', label: strings.Estimated(), default: fs?.estimatedAmount != null ? String(fs.estimatedAmount) : '' },
    ],
    submit: toastOnError(async (v) => {
      await projectManagementSaveFundingSource.call({ _id: fs?._id, projectManagementProjectId: project._id, type: v.type as ProjectManagementFundingSourceType, partyStakeholderId: v.partyStakeholderId || undefined, party: v.party || undefined, estimatedAmount: Number(v.estimatedAmount) || undefined });
    }),
  });
  /** Opens a confirmation modal; the deletion runs (and reloads) only on confirm. */
  const promptDelete = (run: () => Promise<any>) => setConfirm({ body: strings.DeleteItemConfirm(), run: toastOnError(run) });
  const delFundingSource = (id: string) => promptDelete(() => projectManagementDeleteFundingSource.call({ id }));

  /**
   * Add or edit a cash event (money-in or money-out) via quick prompts.
   * @param direction - 'in' for a funder draw, 'out' for an EPC/installer payment
   * @param existing - when set, edit this event instead of creating one
   */
  const editCashEvent = (direction: 'in' | 'out', existing?: ProjectManagementCashEvent) => setFieldsPrompt({
    title: direction === 'in' ? strings.CashSource() : strings.CashUse(),
    fields: [
      { key: 'label', label: strings.Description(), default: existing?.label },
      { key: 'milestoneId', label: strings.Milestone(), type: 'select', default: existing?.milestoneId || '', options: [{ value: '', label: '—' }, ...milestones.map((m) => ({ value: m._id, label: m.label }))] },
      ...(direction === 'in' ? [{ key: 'fundingSourceId', label: strings.FundingSources(), type: 'select' as const, default: existing?.fundingSourceId || '', options: fundingSourceOptions }] : []),
      ...(direction === 'out' ? [
        { key: 'payeeStakeholderId', label: strings.Payee(), type: 'select' as const, default: existing?.payeeStakeholderId || '', options: stakeholderOptions },
        { key: 'payee', label: strings.Party(), default: existing?.payee },
      ] : []),
      { key: 'estimatedAmount', label: strings.Estimated(), default: existing?.estimatedAmount != null ? String(existing.estimatedAmount) : '' },
      { key: 'actualAmount', label: strings.Actual(), default: existing?.actualAmount != null ? String(existing.actualAmount) : '' },
      { key: 'invoicedDate', label: strings.Invoiced(), type: 'date', default: existing?.invoicedDate ? new Date(existing.invoicedDate).toISOString().slice(0, 10) : '' },
      { key: 'paidDate', label: strings.Paid(), type: 'date', default: existing?.paidDate ? new Date(existing.paidDate).toISOString().slice(0, 10) : '' },
      { key: 'reference', label: strings.Reference(), default: existing?.reference },
    ],
    submit: toastOnError(async (v) => {
      // Rejected here as well as on the server, so a typo earns a sentence rather than a Match error.
      const estimated = parseTypedAmount(v.estimatedAmount);
      const actual = parseTypedAmount(v.actualAmount);
      if (!estimated.ok || !actual.ok) {
        toast.error(strings.AmountMustBeANumber());
        return;
      }
      await projectManagementSaveCashEvent.call({
        _id: existing?._id, projectManagementProjectId: project._id, direction,
        // milestoneKey is the payment code (m0..m6 / lntp / …), set by the template/import and not editable
        // here — preserve it so an edit does not wipe it; the select edits milestoneId (the owning milestone).
        label: v.label || undefined, milestoneKey: existing?.milestoneKey, milestoneId: v.milestoneId || undefined,
        fundingSourceId: v.fundingSourceId || undefined,
        payeeStakeholderId: v.payeeStakeholderId || undefined, payee: v.payee || undefined,
        estimatedAmount: estimated.value, actualAmount: actual.value,
        invoicedDate: v.invoicedDate ? new Date(v.invoicedDate) : undefined,
        paidDate: v.paidDate ? new Date(v.paidDate) : undefined,
        reference: v.reference || undefined,
        origin: existing?.origin || 'manual',
      });
    }),
  });
  const delCashEvent = (id: string) => promptDelete(() => projectManagementDeleteCashEvent.call({ id }));

  /** One cash-event table for a single direction — Sources ('in', party = funding source) or Uses ('out',
   *  party = payee). Split so the ledger reads as distinct source/use sub-ledgers rather than one mixed list. */
  /** Name for a cash line's milestone. Resolves through the milestone so the column shows what the
   *  Milestones panel shows (and tracks renames); falls back to the upper-cased key for cash-only keys
   *  like equipmentWo / itcSale, which have no milestone. */
  const milestoneLabelFor = (key?: string) => {
    if (!key) return '—';
    return milestones.find((m) => m.key === key)?.label || key.toUpperCase();
  };
  const applySuggestion = toastOnError(async (sug: ProjectManagementCashSuggestion, milestoneKey?: string) => {
    await quickbooksApplyCashSuggestion.call({ suggestionId: sug._id, milestoneKey });
    toast.success(strings.Saved());
  });
  const dismissSuggestion = toastOnError(async (sug: ProjectManagementCashSuggestion) => {
    await quickbooksDismissCashSuggestion.call({ suggestionId: sug._id });
  });
  // Super-user only: run the QuickBooks Bill -> Use outflow sync against the org's stored connection. It
  // scans org-wide, so any new Tier-3 suggestions for THIS project then appear in the table below.
  const syncQuickbooksBills = toastOnError(async () => {
    setQbSyncing(true);
    try {
      const r = await quickbooksOutflowSync.call({ projectId: project._id });
      if (!r?.ok) { toast.info(r?.message || strings.QuickbooksSyncFailed()); return; }
      toast.success(`${r.tier3} ${strings.QuickbooksSuggestionsAdded()}`);
    }
    finally {
      setQbSyncing(false);
    }
  });
  // Super-user only: run the QuickBooks Invoice/Payment -> Source inflow sync on demand — the same sweep
  // the hourly cron runs (45-day window, org-wide), so a payment just posted in QuickBooks lands on the
  // WO draws' actuals now rather than at :20 past the hour.
  const syncQuickbooksInvoices = toastOnError(async () => {
    setQbSyncing(true);
    try {
      const r = await quickbooksCashSync.call({});
      if (!r?.ok) { toast.info(r?.message || strings.QuickbooksSyncFailed()); return; }
      toast.success(strings.QuickbooksSourcesUpdated(r.updated || 0, r.unchanged || 0));
    }
    finally {
      setQbSyncing(false);
    }
  });
  // Every 'out' milestone the project has a Use for — the options a Tier 3 (unassigned) bill can be
  // placed against.
  const useMilestoneOptions = [
    { value: '', label: strings.AssignMilestone() },
    ...cashEvents.filter((c) => c.direction === 'out').map((c) => ({ value: c.milestoneKey || '', label: c.label || milestoneLabelFor(c.milestoneKey) })),
  ];
  const pendingSuggestions = (cashSuggestions || []).filter((sg) => sg.status === 'pending');
  /** The pending QuickBooks Bill->Use suggestions, shown in a modal opened from the Uses-header badge.
   *  Each row's milestone dropdown starts empty (Tier 3); Confirm writes the SELECTED Use. */
  const renderSuggestionsModal = () => {
    if (!suggestionsModal) return null;
    return (
      <Modal styled={SuggestionsModalStyled} title={strings.QuickbooksSuggestions()} onClose={() => setSuggestionsModal(false)}>
        {
          !pendingSuggestions.length
            ? <div className="empty">{strings.NoPendingSuggestions()}</div>
            : (
              <table className="fin-table">
                <thead><tr><th>{strings.Vendor()}</th><th>{strings.Reference()}</th><th className="num">{strings.Amount()}</th><th className="num">{strings.Paid()}</th><th>{strings.Milestone()}</th><th></th></tr></thead>
                <tbody>
                  {
                    pendingSuggestions.map((sg) => {
                      const chosen = cashSuggestionChoice[sg._id] ?? sg.milestoneKey ?? '';
                      return (
                        <tr key={sg._id}>
                          <td>{sg.vendor || '—'}</td>
                          <td>{sg.docNumber || '—'}</td>
                          <td className="num">{cents(sg.amount)}</td>
                          <td className="num">{cents(sg.paidAmount ?? 0)}</td>
                          <td>
                            <Select value={chosen} options={useMilestoneOptions} onChange={(v) => setCashSuggestionChoice((prev) => ({ ...prev, [sg._id]: v }))} />
                          </td>
                          <td className="num">
                            <button className="link-btn" disabled={!chosen} onClick={() => chosen && applySuggestion(sg, chosen)}>{strings.Confirm()}</button>
                            <span className="sep">|</span>
                            <button className="link-btn" onClick={() => dismissSuggestion(sg)}>{strings.Dismiss()}</button>
                          </td>
                        </tr>
                      );
                    })
                  }
                </tbody>
              </table>
            )
        }
      </Modal>
    );
  };
  // The three work-order draws, when this project has them (RIC's payment placeholders seed them).
  const workOrderDraws = cashEvents.filter((c) => c.direction === 'in' && projectManagementDrawShare(c) != null);
  const totalSolarCostParam = parameters.find((p) => p.name === projectManagementTotalSolarCostParameter && p.active !== false);
  const totalSolarCostValue = typeof totalSolarCostParam?.estimatedValue === 'number' ? totalSolarCostParam.estimatedValue : undefined;
  const renderCashTable = (direction: 'in' | 'out') => {
    // filter() returns a fresh array, so sorting it in place doesn't mutate the shared cashEvents list.
    const rows = cashEvents
      .filter((c) => c.direction === direction)
      .filter((c) => !budgetMilestoneFilter || c.milestoneId === budgetMilestoneFilter)
      .sort(byCashEventLabel);
    // The executed EPC contract is what priced these Uses, so the Budget opens it from here rather than
    // sending the reader to the Documents tab: its current version, whatever its review status. Until one
    // is uploaded the button stays, disabled, with the reason — the slot exists on every RIC project.
    const epcContract = documents.find((d) => d.docType === projectManagementEpcContractDocType && d.currentVersion);
    const openEpcContract = () => {
      if (!epcContract?.currentVersion) return;
      const { file } = epcContract.currentVersion;
      const name = projectManagementDocumentFileName(project, epcContract.label, file);
      if (fileIsViewable(file)) setViewerFile({ file, title: epcContract.label || strings.ViewEpcContract(), downloadName: name });
      else window.open(fileHref(file, name), '_blank', 'noopener');
    };
    return (
      <>
        <div className="fin-section-title">
          {direction === 'in' ? strings.CashSources() : strings.CashUses()}
          {isAdmin && <Button className="secondary compact" onClick={() => editCashEvent(direction)}>{strings.Add()}</Button>}
          {direction === 'out' && (
            // A disabled button swallows mouse events, so the tooltip anchors on a wrapper the hover reaches.
            <span className="disabled-tip" {...(epcContract ? {} : { 'data-tooltip-id': 'pm-task-tip', 'data-tooltip-content': strings.EpcContractNotUploaded() })}>
              <Button className="secondary compact" disabled={!epcContract} onClick={openEpcContract}>{strings.ViewEpcContract()}</Button>
            </span>
          )}
          {direction === 'in' && isAdmin && workOrderDraws.length === Object.keys(projectManagementSolarCostDrawSplit).length &&
            <Button className="secondary compact" onClick={() => setShowDrawSplit(true)}>{strings.WorkOrderSplit()}</Button>}
          {direction === 'in' && user?.isSuperUser && <Button className="secondary compact" onClick={() => syncQuickbooksInvoices()} disabled={qbSyncing}>{qbSyncing ? strings.Sending() : strings.SyncQuickbooksInvoices()}</Button>}
          {direction === 'out' && user?.isSuperUser && <Button className="secondary compact" onClick={() => syncQuickbooksBills()} disabled={qbSyncing}>{qbSyncing ? strings.Sending() : strings.SyncQuickbooksBills()}</Button>}
          {direction === 'out' && pendingSuggestions.length > 0 && (
            <span className="qb-suggestions-badge" onClick={() => setSuggestionsModal(true)} data-tooltip-id="pm-task-tip" data-tooltip-content={strings.QuickbooksSuggestions()}>{pendingSuggestions.length}</span>
          )}
        </div>
        <table className="fin-table">
          <thead><tr><th>{strings.Description()}</th><th className="num">{strings.Estimated()}</th><th className="num">{strings.Actual()}</th><th className="num">{strings.Variance()}</th><th>{strings.Invoiced()}</th><th>{strings.Paid()}</th></tr></thead>
          <tbody>
            {
              rows.map((c) => {
                // Variance waits for settlement: QuickBooks books what has been received so far (total less
                // balance), so until the paid date lands the actual is a running figure, not a final one.
                const variance = (c.paidDate && c.actualAmount != null && c.estimatedAmount != null) ? c.actualAmount - c.estimatedAmount : undefined;
                return (
                  <tr className="br-row" key={c._id} onClick={() => setBudgetRecord({ kind: 'cashEvent', id: c._id })}>
                    <td>
                      {c.label || '—'}
                      {projectManagementDrawShare(c) != null && <span className="draw-share">{roundNumber((projectManagementDrawShare(c) as number) * 100, 2)}%</span>}
                    </td>
                    <td className="num">{cents(c.estimatedAmount)}</td>
                    <td className="num">{cents(c.actualAmount)}</td>
                    <td className="num">{variance != null ? cents(variance) : '—'}</td>
                    <td>{formatCalendarDate(c.invoicedDate) || '—'}</td>
                    <td>{formatCalendarDate(c.paidDate) || '—'}</td>
                  </tr>
                );
              })
            }
            {!rows.length && <tr><td className="meta-row">—</td></tr>}
          </tbody>
        </table>
        {direction === 'out' && renderSuggestionsModal()}
      </>
    );
  };

  /** Open the parameter modal (empty to add, with a parameter to edit). */
  const openParam = (mv?: ProjectManagementParameter) => setParamModal({ parameter: mv });
  const delParameter = (id: string) => promptDelete(() => projectManagementDeleteParameter.call({ id }));
  /**
   * Detach a detail from its task WITHOUT deleting it: the record and its values survive, it simply
   * stops gating that task's completion. Every field is echoed back because applyParameterSave writes
   * the whole document — a partial payload would blank what it omits. '' is the unlink sentinel.
   * @param pm - the parameter to unlink
   */
  const unlinkParameter = (pm: ProjectManagementParameter) => setConfirm({
    title: strings.RemoveFromTask(),
    body: strings.RemoveFromTaskConfirm(),
    confirmText: strings.Remove(),
    run: toastOnError(() => projectManagementSaveParameter.call({
      _id: pm._id, projectManagementProjectId: pm.projectManagementProjectId, name: pm.name, type: pm.type,
      category: pm.category, unit: pm.unit, estimatedValue: pm.estimatedValue, actualValue: pm.actualValue,
      source: pm.source, taskId: '',
    })),
  });
  /**
   * Copy a detail's estimate into its actual. Same whole-document echo as unlinkParameter (including the
   * existing taskId, so the link survives) — only actualValue differs.
   * @param pm - the parameter whose estimate is being promoted
   */
  const promoteParameter = toastOnError(async (pm: ProjectManagementParameter) => {
    await projectManagementSaveParameter.call({
      _id: pm._id, projectManagementProjectId: pm.projectManagementProjectId, name: pm.name, type: pm.type,
      category: pm.category, unit: pm.unit, estimatedValue: pm.estimatedValue, actualValue: pm.estimatedValue,
      source: pm.source, taskId: pm.taskId,
    });
  });
  /**
   * Copy a task's estimated date into its actual. The server still checks it against the milestone's own
   * actual, so an estimate later than a completed milestone is refused rather than quietly written.
   * @param t - the task whose estimated date is being promoted
   */
  const promoteTaskDate = toastOnError(async (t: ProjectManagementTask) => {
    await projectManagementSetTaskDate.call({ taskId: t._id, field: 'actualDate', date: t.estimatedDate as Date });
  });

  /** Open the dedicated stakeholder modal (empty for create; with a stakeholder to edit). */
  const editStakeholder = (stakeholder?: ProjectManagementStakeholder) => setShowStakeholder({ stakeholder });
  const delStakeholder = (id: string) => promptDelete(() => projectManagementDeleteStakeholder.call({ id }));

  /** Add or edit a contact via the fields modal. */
  const editContact = (c?: ProjectManagementContact) => setFieldsPrompt({
    title: c ? c.name : strings.Contacts(),
    fields: [
      { key: 'name', label: strings.Name(), default: c?.name },
      { key: 'title', label: strings.Title(), default: c?.title },
      { key: 'email', label: strings.Email(), default: c?.email },
      { key: 'phone', label: strings.Phone(), type: 'phone', default: c?.phone },
    ],
    submit: toastOnError(async (v) => {
      if (!v.name?.trim()) { toast.info(strings.Name()); return; }
      await projectManagementSaveContact.call({
        _id: c?._id, projectManagementProjectId: project._id, name: v.name.trim(),
        title: v.title || undefined, email: v.email || undefined, phone: v.phone || undefined,
      });
    }),
  });
  const delContact = (id: string) => promptDelete(() => projectManagementDeleteContact.call({ id }));

  const editLink = (l?: ProjectManagementLink) => setFieldsPrompt({
    title: l ? l.label : strings.Links(),
    fields: [
      { key: 'label', label: strings.Name(), default: l?.label },
      { key: 'url', label: strings.Url(), default: l?.url },
    ],
    submit: toastOnError(async (v) => {
      if (!v.label?.trim() || !v.url?.trim()) { toast.info(strings.Url()); return; }
      await projectManagementSaveLink.call({ _id: l?._id, projectManagementProjectId: project._id, label: v.label.trim(), url: v.url.trim() });
    }),
  });
  const delLink = (id: string) => promptDelete(() => projectManagementDeleteLink.call({ id }));

  /** Edit the project's onsite-contact fields via the fields modal. */
  const editOnsiteContact = () => setFieldsPrompt({
    title: strings.OnsiteContact(),
    fields: [
      { key: 'onsiteContactName', label: strings.OnsiteContactName(), default: project.onsiteContactName },
      { key: 'onsiteEmail', label: strings.OnsiteEmail(), default: project.onsiteEmail },
      { key: 'onsitePhone', label: strings.OnsitePhone(), type: 'phone', default: project.onsitePhone },
    ],
    submit: toastOnError(async (v) => {
      await projectManagementUpdateProject.call({
        projectManagementProjectId: project._id,
        onsiteContactName: v.onsiteContactName || undefined,
        onsiteEmail: v.onsiteEmail || undefined,
        onsitePhone: v.onsitePhone || undefined,
      });
    }),
  });

  const fundingSourceOptions = [
    { value: '', label: '—' },
    ...fundingSources.map((s) => ({ value: s._id, label: `${projectManagementFundingSourceTypeLabel(s.type)} · ${stakeholderName(s.partyStakeholderId) || s.party || '—'}` })),
  ];
  /** Add or edit a change order (title / rationale / value / paying source) via the fields modal. */
  const editChangeOrder = (co?: ProjectManagementChangeOrder) => setFieldsPrompt({
    title: co ? co.title : strings.AddChangeOrder(),
    fields: [
      { key: 'title', label: strings.Name(), default: co?.title },
      { key: 'rationale', label: strings.Rationale(), default: co?.rationale },
      { key: 'value', label: strings.Value(), default: co?.value != null ? String(co.value) : '' },
      { key: 'fundingSourceId', label: strings.FundingSources(), type: 'select', default: co?.fundingSourceId || '', options: fundingSourceOptions },
    ],
    submit: toastOnError(async (v) => {
      if (!v.title?.trim()) { toast.info(strings.Name()); return; }
      await projectManagementSaveChangeOrder.call({
        _id: co?._id, projectManagementProjectId: project._id, title: v.title.trim(), rationale: v.rationale || undefined,
        value: Number(v.value) || undefined, fundingSourceId: v.fundingSourceId || undefined,
      });
    }),
  });
  /** Move a change order through its workflow; a rejection prompts for the reason. */
  const setChangeOrderStatus = (co: ProjectManagementChangeOrder, status: ProjectManagementChangeOrder['status']) => {
    if (status !== 'rejected') return toastOnError(async () => projectManagementSetChangeOrderStatus.call({ id: co._id, status }))();
    setFieldsPrompt({
      title: strings.Reject(),
      fields: [{ key: 'reason', label: strings.Note() }],
      submit: toastOnError(async (v) => projectManagementSetChangeOrderStatus.call({ id: co._id, status, decisionNote: v.reason || undefined })),
    });
  };
  /** A budget record's linked documents (real docs, hidden from the Documents panel). */
  const budgetDocsFor = (kind: ProjectManagementBudgetAttachmentKind, id: string) => documents.filter((d) => d.budgetRef && d.budgetRef.kind === kind && d.budgetRef.id === id);
  /** The 'Documents (n)' row action for a budget record: opens the attachments modal. */
  const delChangeOrder = (id: string) => promptDelete(() => projectManagementDeleteChangeOrder.call({ id }));

  /** Add or edit a purchase order via the fields modal; the order date is the accountability anchor. */
  const editPurchaseOrder = (po?: ProjectManagementPurchaseOrder) => setFieldsPrompt({
    title: po ? po.reference : strings.AddPurchaseOrder(),
    fields: [
      { key: 'reference', label: strings.Reference(), default: po?.reference },
      { key: 'orderDate', label: strings.OrderDate(), type: 'date', default: po?.orderDate ? new Date(po.orderDate).toISOString().slice(0, 10) : '' },
      { key: 'equipmentType', label: strings.EquipmentType(), default: po?.equipmentType },
      { key: 'serials', label: strings.Serials(), default: po?.serials },
      {
        key: 'source', label: strings.Source(), type: 'select', default: po?.source || 'external',
        options: [{ value: 'external', label: strings.External() }, { value: 'inventory', label: strings.Inventory() }],
      },
      { key: 'supplierStakeholderId', label: strings.Supplier(), type: 'select', default: po?.supplierStakeholderId || '', options: stakeholderOptions },
      { key: 'value', label: strings.Value(), default: po?.value != null ? String(po.value) : '' },
      { key: 'note', label: strings.Note(), default: po?.note },
    ],
    submit: toastOnError(async (v) => {
      if (!v.reference?.trim()) { toast.info(strings.Reference()); return; }
      await projectManagementSavePurchaseOrder.call({
        _id: po?._id, projectManagementProjectId: project._id, reference: v.reference.trim(),
        orderDate: v.orderDate ? new Date(v.orderDate) : undefined,
        equipmentType: v.equipmentType || undefined, serials: v.serials || undefined,
        source: v.source === 'inventory' ? 'inventory' : 'external',
        supplierStakeholderId: v.supplierStakeholderId || undefined,
        value: Number(v.value) || undefined, note: v.note || undefined,
      });
    }),
  });
  const delPurchaseOrder = (id: string) => promptDelete(() => projectManagementDeletePurchaseOrder.call({ id }));

  /** Soft-delete the project (cascades to its children server-side) and return to the pipeline. */
  const deleteProject = toastOnError(async () => {
    await projectManagementDeleteProject.call({ projectManagementProjectId: project._id });
    navigate('/project-management');
  });

  /** Set/clear the explicit owner override for a milestone (clear → falls back to the role default). */
  // Unused while the milestone Owner select is hidden (2026-08-03):
  // const setMilestoneOwner = toastOnError(async (milestoneId: string, stakeholderId: string | null) => {
  //   await projectManagementSetMilestoneOwner.call({ milestoneId, stakeholderId });
  // });
  /** Replace a task's assignee list. The whole list goes up; the server notifies only who is new to it. */
  const setTaskOwner = toastOnError(async (taskId: string, stakeholderIds: string[]) => {
    await projectManagementSetTaskOwner.call({ taskId, stakeholderIds });
  });

  /** The title copies a link to this page: pasted into an email it reads as the project's name, linking here. */
  const copyProjectLink = toastOnError(async () => {
    await copyLinkToClipboard(`${window.location.origin}/project-management/projects/${project._id}`, project.name);
    toast.success(strings.CopiedToClipboard());
  });

  /** Parse the model now in the slot and open the review modal. Nothing is written until Accept. */
  const parseModel = toastOnError(async () => {
    const res = await projectManagementParseModel.call({ projectManagementProjectId: project._id });
    // Deterministic-parse warnings name exactly what didn't extract (missing labels, blank cells).
    if (res.warnings?.length) toast.warn(`${strings.Warnings()}: ${res.warnings.join(' · ')}`);
    if (!res.rows.length) {
      toast.info(strings.NothingParsedFromModel());
      return;
    }
    setParsedModel(res.rows);
  });

  /** Commit the rows the reviewer ticked. */
  const acceptParsedModel = toastOnError(async (rows: ProjectManagementParsedModelRow[]) => {
    const res = await projectManagementApplyParsedModel.call({ projectManagementProjectId: project._id, rows });
    setParsedModel(undefined);
    toast.info(`${res.applied} ${strings.Parameters()}`);
  });

  /**
   * Upload into the Financial Model slot, approve it, then parse it straight into the review modal —
   * uploading a model and reading it are one action, so there is no separate Parse button to press.
   */
  const uploadFinancialModel = toastOnError(async (file: FileMeta) => {
    await projectManagementUploadFinancialModel.call({ projectManagementProjectId: project._id, file });
    toast.info(strings.FinancialModelUploaded());
    await parseModel();
  });

  /** Create a new document slot from the add-document modal. */
  const addDoc = toastOnError(async () => {
    if (!newDoc.label) {
      toast.info(strings.Name());
      return;
    }
    await projectManagementCreateDocument.call({ projectManagementProjectId: project._id, docType: newDoc.docType || 'other', label: newDoc.label });
    setShowAddDoc(false);
    setNewDoc({ docType: '', label: '' });
  });

  // Task groups are per ROLE, ordered chronologically: tasks within a group sort by their milestone's
  // ESTIMATED date, and the groups themselves order by their earliest task. Tasks with no role fall
  // into an Unassigned group; tasks with no/dangling milestone sort last within their group.
  const milestonesByDate = [...milestones].sort((a, b) => {
    const at = a.plannedDate ? new Date(a.plannedDate).getTime() : Infinity;
    const bt = b.plannedDate ? new Date(b.plannedDate).getTime() : Infinity;
    return at === bt ? (a.order ?? 0) - (b.order ?? 0) : at - bt;
  });
  const milestoneRank = new Map(milestonesByDate.map((m, i) => [m._id, i]));
  // Template array position is the authoritative task order; unknown templateKeys (legacy import_*
  // tasks) fall back to their milestone's chronological rank, after all template-ordered tasks.
  const templateRank = new Map((lifecycle?.taskTemplates || []).map((tt, i) => [tt.key, i]));
  const taskRank = (t: ProjectManagementTask) => {
    const tr = templateRank.get(t.templateKey);
    if (tr !== undefined) return tr;
    return 1000 + (milestoneRank.get(t.milestoneId) ?? 1000);
  };
  // Bucket order follows the lifecycle roles registry; roles outside it (legacy) sort by first task.
  const roleRank = new Map((lifecycle?.roles || []).map((r, i) => [r.key, i]));
  // A task's Details follow the template's seed order; anything not in the catalog keeps the
  // collection's alphabetical sort, after the seeded ones.
  const seedRank = new Map((lifecycle?.parameterSeeds || []).map((s, i) => [s.name, i]));
  const paramRank = (pm: ProjectManagementParameter) => seedRank.get(pm.name) ?? Infinity;
  const taskGroups: { key: string; label: string; tasks: ProjectManagementTask[] }[] = [...new Set(tasks.map((t) => t.role || ''))]
    .map((role) => ({
      key: role || 'unassigned',
      label: role || strings.Unassigned(),
      tasks: tasks.filter((t) => (t.role || '') === role).sort((a, b) => taskRank(a) - taskRank(b)),
    }))
    .filter((g) => g.tasks.length)
    .sort((a, b) => {
      const ra = roleRank.get(a.key) ?? 1000;
      const rb = roleRank.get(b.key) ?? 1000;
      return ra === rb ? taskRank(a.tasks[0]) - taskRank(b.tasks[0]) : ra - rb;
    });
  const paramTaskOptions = [
    { value: '', label: '—' },
    ...taskGroups.flatMap((g) => g.tasks.map((t) => ({ value: t._id, label: `${g.label} · ${t.label}` }))),
  ];
  // Parameters grouped by category, enum order then uncategorized ("Other") last. Within a group the
  // catalog's order wins (it IS the spec'd layout); anything outside it keeps the alphabetical fetch
  // order, after the catalog rows.
  const parameterQuery = parameterSearch.trim().toLowerCase();
  // What this vertical leaves off the tab. Hidden here and nowhere else: the parameters are still stored,
  // still published and still read on the Tasks tab, where the ones being left out are a task's own
  // requirements — which is why this is not a filter on the publication.
  const hidden = projectManagementProjectDetailsHidden(lifecycle);
  const parameterGroups = [...projectManagementParameterCategories, undefined]
    .filter((cat) => !cat || !hidden.hiddenCategories?.includes(cat))
    .map((cat) => ({
      cat,
      label: projectManagementParameterCategoryLabel(cat),
      items: parameters
        .filter((mv) => (mv.category || undefined) === cat && mv.name.toLowerCase().includes(parameterQuery))
        .filter((mv) => !cat || !hidden.trackedOnlyCategories?.includes(cat) || projectManagementTracksActual(mv))
        .sort((a, b) => paramRank(a) - paramRank(b)),
    }))
    .filter((g) => g.items.length);
  // Documents tab scope. Super users see the project's whole document set; everyone else sees only the
  // docTypes delivered by tasks assigned to them, resolved through their membership on THIS project — found
  // by matching the org Account to their stakeholder profile(s) (profile.accountId), then the membership
  // pointing at one. No membership means no assigned tasks, hence no documents — the empty state says so.
  const myProfileIds = new Set(account?._id ? stakeholderProfiles.filter((p) => p.accountId === account._id).map((p) => p._id) : []);
  const myStakeholder = myProfileIds.size ? stakeholders.find((s) => myProfileIds.has(s.profileId)) : undefined;
  // The client twin of the server's taskEditGuard: the admin tier may add to any task, a member only to one
  // assigned to them. Both Add buttons in the task card and the per-document Delete read this.
  const canEditTask = (t: ProjectManagementTask) =>
    isAdmin || (!!myStakeholder && !!t.ownerStakeholderIds?.includes(myStakeholder._id));
  // Rename, re-parse and remove: the admin tier on any document, a member only on one they added
  // themselves. A template slot carries no creator, so it is never a member's. Twins the server's
  // authorGuard, which is what actually refuses the call.
  const canManageTaskDoc = (t: ProjectManagementTask, doc?: ProjectManagementDocumentWithVersions) =>
    !!doc && (isAdmin || (canEditTask(t) && doc.createdByUserId === user?._id));
  // The same rule for a parameter: a member edits, re-values and unlinks the ones they typed onto their own
  // task. The lifecycle's seeded tracker parameters carry no author and stay read-only to them.
  const canManageTaskParam = (t: ProjectManagementTask, pm: ProjectManagementParameter) =>
    isAdmin || (canEditTask(t) && pm.createdByUserId === user?._id);
  // The admin tier sees every document on the project (the publication already sends them all to it) —
  // it used to be super users only, which left an org admin, and now a document approver, staring at an
  // empty tab. A member still sees just the docs their own tasks deliver.
  // Budget attachments go only to who the Budget tab shows them to. The server already withholds the rest;
  // this keeps the page honest if a row ever reaches it anyway.
  const hiddenBudgetKinds = projectManagementHiddenBudgetKinds(user, account);
  const visibleDocuments = (isAdmin ? documents
    : myStakeholder
      ? (() => {
        const mine = projectManagementStakeholderDocTypes(tasks, myStakeholder);
        return documents.filter((d) => mine.has(d.docType));
      })()
      : []).filter((d) => projectManagementBudgetRefVisible(hiddenBudgetKinds, d));


  // Documents tab: the flat list grouped Stage → Documents, where a stage is the lifecycle role that also
  // orders the Tasks panel (RIC: Development, Contracts, Interconnection, Engineering, Procurement,
  // Construction …). A document carries no role: it reaches one through the tasks requiring its slot, so a
  // slot two stages both require shows under the earlier stage. Groups holding nothing are left out.
  const docQuery = allDocsSearch.trim().toLowerCase();
  const shownDocuments = visibleDocuments.filter((d) => !docQuery || d.label.toLowerCase().includes(docQuery) || contentHits[d._id] !== undefined
    || tasks.some((t) => (t.requiredDocSlots || []).includes(d.docType) && t.label.toLowerCase().includes(docQuery)));
  // Within a stage: the lifecycle's own doc-slot order, so the list reads like the template. Anything
  // off-template (an ad-hoc upload) sorts after them, by label.
  const slotRank = new Map((lifecycle?.docSlots || []).map((sl, i) => [sl.docType, i]));
  const slotOrder = (docType?: string) => (docType && slotRank.has(docType) ? slotRank.get(docType) as number : 9999);
  const sortedDocs = (docs: typeof shownDocuments) =>
    [...docs].sort((a, b) => slotOrder(a.docType) - slotOrder(b.docType) || a.label.localeCompare(b.label));
  const roleOrder = new Map((lifecycle?.roles || []).map((r, i) => [r.key, i]));
  const roleIndex = (role?: string) => (role && roleOrder.has(role) ? roleOrder.get(role) as number : 9999);
  const stageOfDocType = new Map<string, string>();
  // Every task delivering the slot, in Tasks-panel order — the row names them beside the document.
  const tasksOfDocType = new Map<string, string[]>();
  [...tasks]
    .sort((a, b) => roleIndex(a.role) - roleIndex(b.role) || taskRank(a) - taskRank(b))
    .forEach((t) => (t.requiredDocSlots || []).forEach((dt) => {
      if (!stageOfDocType.has(dt)) stageOfDocType.set(dt, t.role || '');
      tasksOfDocType.set(dt, [...(tasksOfDocType.get(dt) || []), t.label]);
    }));
  (lifecycle?.docSlots || []).forEach((sl) => {
    if (sl.role && !stageOfDocType.has(sl.docType)) stageOfDocType.set(sl.docType, sl.role);
  });
  const taskNamesOf = (docType?: string) => (docType && tasksOfDocType.get(docType)?.join(', ')) || '';
  /**
   * What a document should be called once it is on somebody's disk: the project's Luminia id and the
   * document's own label. Not the task that asks for it — a task commonly delivers several documents, and
   * naming them all after the task leaves a download folder full of identical names.
   */
  const docFileName = (doc?: { docType?: string, label?: string }, file?: FileMeta, suffix?: string) =>
    projectManagementDocumentFileName(project, doc?.label, file, suffix);
  /**
   * How a version opens. An archive of photographs opens as a gallery, anything a browser can render
   * opens in the viewer, and everything else downloads — which is what every version did before. One
   * renderer because the same three-way choice is made in five places on this page.
   * @param doc - the document the version belongs to, for the download's filename
   * @param version - the version to open
   * @param title - what the modal is called
   * @param suffix - appended to the download's filename, for a historical version
   * @returns the control
   */
  const versionOpenLink = (
    doc: { docType?: string, label?: string } | undefined,
    version: { _id: string, file: FileMeta, driveFileId?: string },
    title: string,
    suffix?: string,
  ) => {
    const name = docFileName(doc, version.file, suffix);
    // Only an adopted version has an index to read; one filed before the drive existed still downloads.
    if (version.driveFileId && driveIsArchive(driveExtOf(version.file?.name))) {
      return (
        <button className="link-btn dl" onClick={() => setGalleryVersion({ versionId: version._id, title })}>
          {strings.ViewGallery()}
        </button>
      );
    }
    if (fileIsViewable(version.file)) {
      return (
        <button className="link-btn dl" onClick={() => setViewerFile({ file: version.file, title, downloadName: name })}>
          {strings.View()}
        </button>
      );
    }
    return (
      <a className="dl" href={fileHref(version.file, name)} target="_blank" rel="noreferrer" download={name}>
        {strings.Download()}
      </a>
    );
  };
  const documentTree = (lifecycle?.roles || [])
    .map((role) => ({ role, docs: sortedDocs(shownDocuments.filter((d) => stageOfDocType.get(d.docType) === role.key)) }))
    .filter((g) => g.docs.length);
  // Budget attachments are their own section, named after the record each one hangs off.
  const budgetDocs = sortedDocs(shownDocuments.filter((d) => d.budgetRef));
  const budgetRecordLabel = (ref: { kind: ProjectManagementBudgetAttachmentKind, id: string }) => {
    if (ref.kind === 'fundingSource') {
      const s = fundingSources.find((x) => x._id === ref.id);
      return s ? projectManagementFundingSourceTypeLabel(s.type) : '';
    }
    if (ref.kind === 'cashEvent') {
      const c = cashEvents.find((x) => x._id === ref.id);
      return c ? c.label || milestoneLabelFor(c.milestoneKey) : '';
    }
    if (ref.kind === 'changeOrder') return changeOrders.find((x) => x._id === ref.id)?.title || '';
    const po = purchaseOrders.find((x) => x._id === ref.id);
    return po ? po.reference || strings.PurchaseOrders() : '';
  };
  // Whatever no stage claims: task-less doc slots that declare no role, and anything delivered by a task
  // whose role has since left the registry.
  const unplacedDocs = sortedDocs(shownDocuments.filter((d) => !d.budgetRef && !roleOrder.has(stageOfDocType.get(d.docType) || '')));
  const reviewMilestone = milestones.find((m) => m._id === reviewMilestoneId);
  const stakeholderQuery = stakeholderSearch.trim().toLowerCase();
  const filteredStakeholders = stakeholders.filter((s) => {
    if (!stakeholderQuery) return true;
    const profile = profileOfStakeholder(s);
    return (profile?.name || '').toLowerCase().includes(stakeholderQuery)
      || (profile?.organization || '').toLowerCase().includes(stakeholderQuery);
  });

  /** The full task card — everything a task carries (details, docs, notes). Rendered in the task modal. */
  /**
   * The task's own slice of the project History: what happened to the task itself, to the parameters
   * linked to it, and to the documents filling its slots — which is everything a person means by "what
   * happened to this task". Shares renderEventHeadline with the History tab so the two can never word the
   * same event differently; the tab's per-kind mute filter is deliberately not applied, since this list is
   * already narrow. An event about a parameter or document that has since been DELETED drops out, because
   * the row that tied it to this task is what has gone.
   */
  const renderTaskHistory = (t: ProjectManagementTask) => {
    const paramIds = new Set(parameters.filter((pm) => pm.taskId === t._id).map((pm) => pm._id));
    const slots = t.requiredDocSlots || [];
    const docIds = new Set(documents.filter((d) => slots.includes(d.docType)).map((d) => d._id));
    const mine = events.filter((e) => {
      if (e.ref?.collection === 'projectManagementTasks') return e.ref.id === t._id;
      if (e.ref?.collection === 'projectManagementParameters') return paramIds.has(e.ref.id);
      if (e.ref?.collection === 'projectManagementDocuments') return docIds.has(e.ref.id);
      return false;
    });
    return (
      <aside className="task-history">
        <div className="history-inner">
          <div className="history-title">{strings.History()}</div>
          <div className="history-scroll">
            {
              mine.map((e) => (
                <div className="transition" key={e._id}>
                  {renderEventHeadline(e)}
                  <div className="when">{e.createdByName ? `${e.createdByName} · ` : ''}{e.createdAt && formatDateTime(e.createdAt)}</div>
                </div>
              ))
            }
            {!mine.length && <div className="meta-row">{strings.NoHistory()}</div>}
          </div>
        </div>
      </aside>
    );
  };

  const renderTaskCard = (t: ProjectManagementTask) => {
    const taskMilestone = milestones.find((m) => m._id === t.milestoneId);
    // How many numbered items the Details block will use, so the Documents block can carry the count on.
    // Hoisted here because the parameters are computed inside their own block below, out of reach of the
    // documents. The task's own dates row is not an item — it is a fact about the task, not work to do.
    const taskItemCount = parameters.filter((pm) => pm.taskId === t._id).length;
    // Linking a parameter to a task makes its actual a completion requirement;
    // mirrors the server rule so the checkbox can't offer a doomed click.
    // A single-value parameter is filled via its value (estimatedValue); an actual-tracking one via its
    // actualValue. Mirrors the server's taskMissingActuals so the checkbox can't offer a doomed click.
    const missingActuals = parameters
      .filter((pm) => {
        if (pm.taskId !== t._id) return false;
        const value = projectManagementTracksActual(pm) ? pm.actualValue : pm.estimatedValue;
        return value == null || value === '';
      })
      .map((pm) => pm.name);
    const blocked = t.status !== 'done' && !!missingActuals.length;
    const blockedHint = `${strings.TaskActualsRequired()}: ${missingActuals.join(', ')}`;
    const ownerOptions = stakeholders.map((s) => ({ value: s._id, label: profileOfStakeholder(s)?.name || '—' }));
    const assigneeNames = projectManagementOwnersOf(t, stakeholders).map((s) => profileOfStakeholder(s)?.name || '—');
    return (
      <div className={`task${t.status === 'done' ? ' done' : ''}`} key={t._id}>
        {
          // Only super users complete a task by toggling it. Members can't check it off — their tasks
          // complete by filling the required parameters/documents and a super user approving the docs — but
          // a done task still shows a read-only green check.
          user?.isSuperUser
            ? <input
                type="checkbox" id={`task-${t._id}`} checked={t.status === 'done'} disabled={blocked}
                data-tooltip-id="pm-task-tip" data-tooltip-content={blocked ? blockedHint : undefined}
                onChange={(e) => toggleTask(t._id, e.target.checked)}
              />
            : t.status === 'done' && <Icon className="task-done-check" icon="circleCheck" fill={css.green2} tip={strings.Done()} />
        }
        <label>
          {/* role prefix hidden 2026-08-05 — redundant now that tasks group by role */}
          {/* {t.role && <span className="role">[{t.role}] </span>} */}
          {t.label}
        </label>
        {
          t.label === 'Consumption Data' && isAdmin &&
          <span className="task-utility">
            <Icon icon="link2" tip={strings.CopyLinkToConnectUtilityAccount()} onClick={() => copyPmUtilityLink()} />
            {
              pmUtilityAccount &&
              <span className="task-utility-status">
                {(pmUtilityAccount.type || '').toUpperCase()} · {pmUtilityAccount.status}
                {pmUtilityMeterCount ? ` · ${pmUtilityMeterCount} ${strings.ConsumptionMeters()}` : ''}
                {pmUtilityAccount.pmCapturedAt ? ` · ✓ ${new Date(pmUtilityAccount.pmCapturedAt).toLocaleDateString()}` : ''}
              </span>
            }
          </span>
        }
        {
          taskMilestone &&
          <span className="task-ms" data-tooltip-id="pm-task-tip" data-tooltip-content={strings.Milestones()}>
            <span className="task-ms-dot" style={{ background: milestoneDotColor(taskMilestone) }} />
            {taskMilestone.label}
          </span>
        }
        {/* Only super users reassign work; everyone else sees who holds it, read-only. Assignment is manual
            and now plural — an empty selection reads "Unassigned", never a role-matched fallback. */}
        <span className="task-owner">
          {
            isAdmin ?
              <RichMultiSelect
                className="task-owner-select"
                placeholder={strings.Unassigned()}
                selectedValues={t.ownerStakeholderIds || []}
                options={ownerOptions}
                onSelect={(ids) => setTaskOwner(t._id, ids)}
              />
              :
              <span className="task-owner-readonly">{assigneeNames.join(', ') || strings.Unassigned()}</span>
          }
        </span>
        {
          canReshapeTask(t) &&
          <span className="task-actions">
            <button className="link-btn" onClick={() => editTask(t)}>{strings.Edit()}</button>
            <span className="sep">|</span>
            <button className="link-btn" onClick={() => removeTask(t)}>{strings.Delete()}</button>
          </span>
        }
        {/* Full-width, so it reads as a line under the header rather than another item in it. */}
        {t.description && <div className="task-description">{t.description}</div>}
        {(() => {
          const taskParams = parameters.filter((pm) => pm.taskId === t._id).sort((a, b) => paramRank(a) - paramRank(b));
          // A suggestion can only ever fill a BLANK estimate (acceptance re-checks that
          // server-side), so one left over from a parse that raced a human edit is dead —
          // hide it rather than offer a button whose only outcome is 'skipped'.
          const taskSuggestions = suggestions.filter((sg) => taskParams.some((pm) =>
            pm._id === sg.parameterId && (pm.estimatedValue == null || pm.estimatedValue === '')));
          // Dates show for anyone once set, but only an admin can author them, so a
          // member's parameter-less task still renders no DETAILS block at all.
          const showDates = isAdmin || !!t.estimatedDate || !!t.actualDate;
          const taskDate = (d?: Date) => formatCalendarDate(d) || '—';
          // Split like the Project Details tab: parameters that carry an estimate AND an actual go in one
          // grid with both columns; single-value parameters go in their own grid with just a Value column,
          // rather than sharing a header that makes every single-value row look like it is missing an actual.
          const trackedParams = taskParams.filter((pm) => projectManagementTracksActual(pm));
          const singleParams = taskParams.filter((pm) => !projectManagementTracksActual(pm));
          const del = (pm: ProjectManagementParameter) => canManageTaskParam(t, pm)
            && <button className="link-btn tp-del" data-tooltip-id="pm-task-tip" data-tooltip-content={strings.RemoveFromTask()} onClick={() => unlinkParameter(pm)}>✕</button>;
          // Read-only for anyone who cannot excuse a requirement (super users and the luminia role can) — the
          // state is still worth seeing, since it explains why a blank row is not holding the task open.
          const optionalBox = (pm: ProjectManagementParameter) => (
            <Checkbox
              checked={!!pm.optional} disabled={!canSetOptional}
              onChange={(v) => setParameterOptional(pm._id, v)}
            />
          );
          // The estimate / value cell, shared by both grids: an AI suggestion replaces a blank value.
          const valueCell = (pm: ProjectManagementParameter, suggestion?: { _id: string, value: any }) => (
            suggestion ?
              <span className="tp-suggestion" data-tooltip-id="pm-task-tip" data-tooltip-content={strings.AiSuggested()}>
                {formatValue(suggestion.value, pm.unit, pm.type)}
                <button className="link-btn ok" data-tooltip-id="pm-task-tip" data-tooltip-content={strings.Accept()} onClick={() => decideSuggestions([suggestion._id], true)}>✓</button>
                <button className="link-btn no" data-tooltip-id="pm-task-tip" data-tooltip-content={strings.Dismiss()} onClick={() => decideSuggestions([suggestion._id], false)}>✕</button>
              </span>
              : canManageTaskParam(t, pm) ?
                <button className="link-btn" onClick={() => openParam(pm)}>{formatValue(pm.estimatedValue, pm.unit, pm.type)}</button>
                :
                formatValue(pm.estimatedValue, pm.unit, pm.type)
          );
          const paramName = (pm: ProjectManagementParameter) => (canManageTaskParam(t, pm)
            ? <button className="link-btn name-btn" onClick={() => openParam(pm)}>{pm.name}</button>
            : pm.name);
          // canEditTask, not isSuperUser: a member assigned to this task needs the section to exist before
          // there is a first parameter to put in it, or the Add button has nowhere to live.
          return (!!taskParams.length || canEditTask(t) || showDates) && (
            <>
              <div className="task-params-title task-params-title-block">
                {strings.Details()}
                {canEditTask(t) && <button className="link-btn title-add" onClick={() => setParamModal({ taskId: t._id })}>{strings.Add()}</button>}
              </div>
              {
                !!taskSuggestions.length &&
                <div className="tp-banner tp-banner-block">
                  <Icon icon="documentSearch" />
                  {taskSuggestions.length} {strings.AiSuggested()}
                  <button className="link-btn" onClick={() => decideSuggestions(taskSuggestions.map((x) => x._id), true)}>{strings.AcceptAll()}</button>
                  <span className="sep">|</span>
                  <button className="link-btn" onClick={() => decideSuggestions(taskSuggestions.map((x) => x._id), false)}>{strings.DismissAll()}</button>
                </div>
              }
              {
                // Estimate/Actual grid: the task's dates plus every actual-tracking parameter.
                (!!trackedParams.length || showDates) &&
                <div className="task-params">
                  <div className="tp-head">
                    <span />
                    <span>{strings.Estimated()}</span>
                    <span />
                    <span>{strings.Actual()}</span>
                    <span>{strings.NotesStatus()}</span>
                    <span>{strings.Optional()}</span>
                    <span />
                  </div>
                  {
                    // EXACTLY five children: .task-param is a subgrid over the parent's tracks, so a sixth
                    // wraps onto a second row inside it rather than sitting in the column it looks like.
                    showDates &&
                    <div className="task-param tp-dates">
                      <span className="tp-name">{strings.TaskDates()}</span>
                      <span className={`tp-est${t.estimatedDate ? '' : ' tp-missing'}`}>
                        {
                          isAdmin ?
                            <button className="link-btn" onClick={() => editTaskDates(t, taskMilestone)}>{taskDate(t.estimatedDate)}</button>
                            : taskDate(t.estimatedDate)
                        }
                      </span>
                      <span className="tp-promote">
                        {isAdmin && <PromoteToActual estimated={!!t.estimatedDate} actual={!!t.actualDate} onClick={() => promoteTaskDate(t)} />}
                      </span>
                      <span className={`tp-act${t.actualDate ? '' : ' tp-missing'}`}>
                        {
                          isAdmin ?
                            <button className="link-btn" onClick={() => editTaskDates(t, taskMilestone)}>{taskDate(t.actualDate)}</button>
                            : taskDate(t.actualDate)
                        }
                      </span>
                      <span className="tp-notes" />
                      <span className="tp-optional" />
                      <span className="tp-tools" />
                    </div>
                  }
                  {trackedParams.map((pm, i) => {
                    const suggestion = taskSuggestions.find((sg) => sg.parameterId === pm._id);
                    const hasEstimated = pm.estimatedValue != null && pm.estimatedValue !== '';
                    const hasActual = pm.actualValue != null && pm.actualValue !== '';
                    return (
                      <div className="task-param" key={pm._id}>
                        <span className="tp-name"><span className="item-no">{i + 1}.</span>{paramName(pm)}</span>
                        <span className={`tp-est${suggestion ? ' tp-suggested' : hasEstimated ? '' : ' tp-missing'}`}>{valueCell(pm, suggestion)}</span>
                        {/* A pending suggestion has no accepted estimate to promote yet — the arrow waits for the ✓. */}
                        <span className="tp-promote">{isAdmin && !suggestion && <PromoteToActual estimated={hasEstimated} actual={hasActual} onClick={() => promoteParameter(pm)} />}</span>
                        <span className={`tp-act${hasActual ? '' : ' tp-missing'}`}>
                          {isAdmin ? <button className="link-btn" onClick={() => openParam(pm)}>{formatValue(pm.actualValue, pm.unit, pm.type)}</button> : formatValue(pm.actualValue, pm.unit, pm.type)}
                        </span>
                        <span className="tp-notes">{pm.notes || ''}</span>
                        <span className="tp-optional">{optionalBox(pm)}</span>
                        <span className="tp-tools">{del(pm)}</span>
                      </div>
                    );
                  })}
                </div>
              }
              {
                // Single-value grid: name + value, no actual column (there is nothing to track).
                !!singleParams.length &&
                <div className="task-params single-value">
                  <div className="tp-head"><span /><span>{strings.Value()}</span><span>{strings.NotesStatus()}</span><span>{strings.Optional()}</span><span /></div>
                  {singleParams.map((pm, i) => {
                    const suggestion = taskSuggestions.find((sg) => sg.parameterId === pm._id);
                    const hasValue = pm.estimatedValue != null && pm.estimatedValue !== '';
                    return (
                      <div className="task-param" key={pm._id}>
                        <span className="tp-name"><span className="item-no">{trackedParams.length + i + 1}.</span>{paramName(pm)}</span>
                        <span className={`tp-est${suggestion ? ' tp-suggested' : hasValue ? '' : ' tp-missing'}`}>{valueCell(pm, suggestion)}</span>
                        <span className="tp-notes">{pm.notes || ''}</span>
                        <span className="tp-optional">{optionalBox(pm)}</span>
                        <span className="tp-tools">{del(pm)}</span>
                      </div>
                    );
                  })}
                </div>
              }
            </>
          );
        })()}
        {
          (!!t.requiredDocSlots?.length || canEditTask(t)) &&
          <>
            <div className="task-params-title task-docs-title">
              {strings.Documents()}
              {canEditTask(t) && <button className="link-btn title-add" onClick={() => editTaskDoc(t)}>{strings.Add()}</button>}
            </div>
            <div className="task-docs">
              {/* The grid never had a header; it gets one now so the Optional column is labelled rather
                  than a bare column of checkboxes. The other tracks stay unlabelled — an icon, a name, an
                  upload pill and a status pill each say what they are. */}
              <div className="tp-head">
                <span /><span /><span /><span /><span>{strings.Optional()}</span><span />
              </div>
              {
                (t.requiredDocSlots || []).map((docType, i) => {
                  const doc = documents.find((d) => d.docType === docType);
                  return (
                    <div className="task-doc" key={docType}>
                      <Icon icon="docs" />
                      <span className="label">
                        {/* One run of numbers across the whole card: the documents carry on from the last
                            parameter, so "get to item 7" means one thing however the card is split up. */}
                        <span className="item-no">{taskItemCount + i + 1}.</span>
                        {
                          doc ?
                            <button className="link-btn name-btn" data-tooltip-id="pm-task-tip" data-tooltip-content={strings.Manage()} onClick={() => setDocModalId(doc._id)}>{doc.label}</button>
                            :
                            docType
                        }
                        <AiReadIndicator doc={doc} />
                      </span>
                      <FileInput className="task-doc-upload" placeholder={strings.Upload()} onChange={(file) => uploadTaskDoc(docType, file)} />
                      {/* A pending badge explains the wait rather than offering "Manage" — the person
                          looking at it is usually the uploader, waiting on someone else. */}
                      <span className="cell">{doc && <span className="doc-badge" data-tooltip-id="pm-task-tip" data-tooltip-content={doc.status === 'pending' ? strings.DocPendingApprovalHint() : strings.Manage()} onClick={() => setDocModalId(doc._id)}><Badge status={doc.status} label={statusLabel(doc.status)} /></span>}</span>
                      <span className="tp-optional">
                        <Checkbox
                          checked={!!doc?.optional} disabled={!doc || !canSetOptional}
                          onChange={(v) => doc && setDocumentOptional(doc._id, v)}
                        />
                      </span>
                      <span className="cell">
                        {
                          // Anything the browser can render opens here in the page; the rest still
                          // downloads through the plain link.
                          doc?.currentVersion && versionOpenLink(doc, doc.currentVersion, doc.label)
                        }
                        {
                          doc && canManageTaskDoc(t, doc) &&
                        <span className="task-doc-actions">
                          {/* Divides these from the View / Download link beside them, which is a sibling
                              of this block rather than part of it — hence the separator leading here. */}
                          {doc.currentVersion && <span className="sep">|</span>}
                          {doc.currentVersionId && <><button className="link-btn" onClick={() => parseDocumentWithAi(doc._id)}>{strings.ParseWithAi()}</button><span className="sep">|</span></>}
                          <button className="link-btn" onClick={() => editTaskDoc(t, doc)}>{strings.Edit()}</button>
                          <span className="sep">|</span>
                          <button className="link-btn" onClick={() => removeTaskDoc(t, doc)}>{strings.Delete()}</button>
                        </span>
                        }
                      </span>
                    </div>
                  );
                })
              }
            </div>
          </>
        }
        <div className="task-params-title task-notes-title">{strings.Notes()}</div>
        <div className="task-notes note-composer">
          <TextArea
            placeholder={strings.AddNote()}
            value={noteComposer[t._id] || ''}
            onChange={(v) => setNoteComposer((d) => ({ ...d, [t._id]: v }))}
            onKeyDown={(e) => { if (isSubmitChord(e)) { e.preventDefault(); submitTaskNote(t); } }}
          />
          {
            // Only once there is something to post — an always-on button on an empty box is noise.
            !!(noteComposer[t._id] || '').trim() &&
            <Button className="primary compact note-submit" tip={strings.SubmitNoteHint()} onClick={() => submitTaskNote(t)}>{strings.Submit()}</Button>
          }
        </div>
        {
          (() => {
            // Newest first, and capped in height by .note-feed so a long history scrolls inside the modal
            // rather than pushing the rest of the card off screen.
            const notes = [
              ...postedNotes.filter((n) => n.projectManagementTaskId === t._id && !taskNotes.some((x) => x._id === n._id)),
              ...taskNotes.filter((n) => n.projectManagementTaskId === t._id),
            ];
            if (!notes.length) return <div className="note-feed"><div className="meta-row">{strings.NoNotesYet()}</div></div>;
            return (
              <div className="note-feed">
                {
                  notes.map((n) => (
                    <div className="note" key={n._id}>
                      <div className="note-meta">
                        <span className="who">{n.createdByName || '—'}</span>
                        <span>{n.createdAt ? new Date(n.createdAt).toLocaleString() : ''}</span>
                        {n.updatedAt && <span>({strings.Edited()})</span>}
                        {
                          projectManagementCanEditTaskNote(user, n) && editingNoteId !== n._id &&
                          <span className="note-actions">
                            <button className="link-btn" onClick={() => { setEditingNoteId(n._id); setEditingNoteBody(n.body); }}>{strings.Edit()}</button>
                            <span className="sep">|</span>
                            <button className="link-btn" onClick={() => removeTaskNote(n)}>{strings.Delete()}</button>
                          </span>
                        }
                      </div>
                      {
                        editingNoteId === n._id ?
                          <div className="note-edit">
                            <TextArea
                              value={editingNoteBody}
                              onChange={(v) => setEditingNoteBody(v)}
                              onKeyDown={(e) => { if (isSubmitChord(e)) { e.preventDefault(); saveTaskNoteEdit(); } }}
                            />
                            <div className="note-edit-actions">
                              <Button className="primary compact" onClick={() => saveTaskNoteEdit()}>{strings.Save()}</Button>
                              <Button className="secondary compact" onClick={() => { setEditingNoteId(undefined); setEditingNoteBody(''); }}>{strings.Cancel()}</Button>
                            </div>
                          </div>
                          :
                          <div className="note-body">{n.body}</div>
                      }
                    </div>
                  ))
                }
              </div>
            );
          })()
        }
      </div>
    );
  };

  // Everyone gets the bucket wizard. A member's tasks are already publication-scoped to their own, so the
  // strip shows just the buckets they actually work — the numbered steps + per-bucket filtering below.
  // Wizard default: the first bucket still carrying an open task; an all-done project lands on the last.
  const selectedBucketKey = taskBucket && taskGroups.some((g) => g.key === taskBucket)
    ? taskBucket
    : (taskGroups.find((g) => g.tasks.some((t) => t.status !== 'done')) || taskGroups[taskGroups.length - 1])?.key;

  return (
    <ProjectManagementProjectPageStyled>
      <PageHeading
        left={
          <h1>
            {
              // The project's own logo is uploaded per site; the portfolio's is the brand mark shared by
              // every site in it, so it stands in whenever a project has none of its own.
              (project.logo || portfolio?.logo) &&
              <img className="project-logo" src={fileHref(project.logo || portfolio?.logo)} alt="" />
            }
            <span
              className="title-text"
              role="button"
              tabIndex={0}
              data-tooltip-id="pm-task-tip"
              data-tooltip-content={strings.CopyProjectLinkHint()}
              onClick={() => copyProjectLink()}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); copyProjectLink(); } }}
            >
              {project.name}
              {project.address && <span className="project-address">{project.address}</span>}
            </span>
          </h1>
        }
        right={
          <div className="heading-buttons">
            {isAdmin && <Button className="primary" onClick={() => setShowEdit(true)}>{strings.Edit()}</Button>}
            {isAdmin && <Button className="primary" onClick={() => setShowStatus(true)}>{strings.ChangeStatus()}</Button>}
            {isAdmin && <Button className="primary" onClick={() => setShowDelete(true)}>{strings.Delete()}</Button>}
            <Button className="primary chat-btn" onClick={() => openChat()}>
              {strings.Messages()}{chatUnread ? <span className="chat-badge">{chatUnread}</span> : null}
            </Button>
          </div>
        }
      />
      <PageBody>
        <div className="meta-row">
          <div className="project-meta">
            {/* Hand-set status leads: it is the one thing that colours the project everywhere else. */}
            <div className="meta-item">
              <span className="k">{strings.Status()}</span>
              <span className="v"><ProjectStatusBadge project={project} /></span>
              {projectManagementProjectStatus(project) !== 'moving' && project.statusNote && <span className="status-note">{project.statusNote}</span>}
            </div>
            {project.luminiaId && <div className="meta-item"><span className="k">{strings.LuminiaId()}</span><span className="v">{project.luminiaId}</span></div>}
            {project.externalId && <div className="meta-item"><span className="k">{projectManagementExternalIdLabel(project.lifecycleKey)}</span><span className="v">{project.externalId}</span></div>}
            {/* Next to the RIC #: one is our id for the project, the other is the client's for the site. */}
            {project.clientId && <div className="meta-item"><span className="k">{strings.StoreNumber()}</span><span className="v">{project.clientId}</span></div>}
            {portfolioName && <div className="meta-item"><span className="k">{strings.Portfolio()}</span><span className="v">{portfolioName}</span></div>}
            {projectOwner && <div className="meta-item"><span className="k">{strings.Owner()}</span><span className="v">{projectOwner.logo ? <img className="meta-logo" src={fileHref(projectOwner.logo)} alt={projectOwner.name} data-tooltip-id="pm-task-tip" data-tooltip-content={projectOwner.name} /> : projectOwner.name}</span></div>}
            {projectEpc && <div className="meta-item"><span className="k">{strings.Epc()}</span><span className="v">{projectEpc.logo ? <img className="meta-logo" src={fileHref(projectEpc.logo)} alt={projectEpc.name} data-tooltip-id="pm-task-tip" data-tooltip-content={projectEpc.name} /> : projectEpc.name}</span></div>}
            {project.sgipId && <div className="meta-item"><span className="k">{strings.SgipId()}</span><span className="v">{project.sgipId}</span></div>}
            {projectUtility && <div className="meta-item"><span className="k">{strings.UtilityCompany()}</span><span className="v">{projectUtility.logo ? <img className="meta-logo" src={fileHref(projectUtility.logo)} alt={projectUtility.name} data-tooltip-id="pm-task-tip" data-tooltip-content={projectUtility.name} /> : projectUtility.name}</span></div>}
            {/* Floor, not round: a PPA signed this morning is 0 days old, not 1. */}
            {projectAgeDays != null && <div className="meta-item"><span className="k">{strings.ProjectAge()}</span><span className="v">{strings.DaysCount(projectAgeDays)}</span></div>}
          </div>
          {
            (project.onsiteContactName || project.onsiteEmail || project.onsitePhone) &&
            <div className="contact-card">
              <span className="k">{strings.OnsiteContact()}</span>
              {project.onsiteContactName && <span className="name">{project.onsiteContactName}</span>}
              {
                (project.onsiteEmail || project.onsitePhone) &&
                <div className="contact-lines">
                  {project.onsiteEmail && <a className="line email" href={`mailto:${project.onsiteEmail}`} data-tooltip-id="pm-task-tip" data-tooltip-content={project.onsiteEmail}>{project.onsiteEmail}</a>}
                  {project.onsitePhone && <a className="line phone" href={`tel:${project.onsitePhone}`}>{project.onsitePhone}</a>}
                </div>
              }
            </div>
          }
        </div>


        {
          // Shown to members too — their Attention Now is scoped to their own tasks (see the memo above).
          <div className="attention">
            <button className="attention-head" onClick={() => toggleAttention()}>
              <span className="caret">{attentionOpen ? '▾' : '▸'}</span>
              <span className="attention-title">{strings.AttentionNow()}</span>
              {/* The summary is the whole point when collapsed: overdue first, since that is what decides
                  whether it is worth opening. */}
              <span className="attention-summary">
                {!!attention.overdue && <span className="overdue">{strings.OverdueCount(attention.overdue)}</span>}
                {!!attention.overdue && !!attention.total && <span className="sep">·</span>}
                {attention.total ? strings.ItemsCount(attention.total) : strings.NothingNeedsAttention()}
              </span>
            </button>
            {
              attentionOpen && !!attention.rows.length &&
                attention.rows.map((item) => {
                  // Same thresholds as milestoneHealth, so this card, the timeline rail and the calendar
                  // never disagree about what counts as late.
                  const state = item.overdue > 0 ? 'late' : item.overdue > -14 ? 'soon' : 'ahead';
                  return (
                    <div className={`attention-row ${state}`} key={`${item.kind}-${item.id}`}>
                      <span className="dot" />
                      <span className="label">{item.label}</span>
                      <span className="role">{item.role || strings.Unassigned()}</span>
                      <span className="when">
                        {
                          item.overdue > 0 ? strings.DaysOverdue(item.overdue)
                            : item.overdue > -14 ? strings.DueInDays(-item.overdue)
                              : strings.PlannedOn(formatCalendarDate(item.date))
                        }
                      </span>
                      {
                        item.kind === 'task' ?
                          <button className="link-btn" onClick={() => { setTaskCardId(item.id); selectTab('delivery'); }}>{strings.Fix()} →</button>
                          :
                          <button className="link-btn" onClick={() => selectTab('schedule')}>{strings.Fix()} →</button>
                      }
                    </div>
                  );
                })
            }
          </div>
        }

        {/* Finance viewers only. isAdmin was too broad: build cost and dev fee are not every PM admin's
            business, and the banner puts them at the top of the page rather than behind a tab. */}
        {canSeeFinances
          ? <ProjectManagementMetricsBanner className="large-sub" metrics={bannerMetrics} />
          : <ProjectManagementMetricsBanner metrics={memberBannerMetrics} />}

        <div className="content-row">
          <div className="tab-area">
            <DividerTabs activeTab={tab} onTabChange={selectTab} tabs={projectTabs.map((t) => ({ id: t.key, label: t.label }))} />
            <div className="pm-grid">
              {tab === 'allDocuments' &&
            <div className="panel">
              <div className="panel-tools">
                <div className="param-search"><Input value={allDocsSearch} onChange={(v) => setAllDocsSearch(v || '')} placeholder={strings.Search()} /></div>
                {isAdmin && <Button className="secondary compact" onClick={() => setShowAddDoc(true)}>{strings.AddDocument()}</Button>}
              </div>
              <div className="panel-scroll">
                {
                  (() => {
                    /** One document line, wherever it hangs in the tree. */
                    const docRow = (d: typeof shownDocuments[number]) => (
                      <div className="doc-row" key={d._id}>
                        <span>
                          <button className="link-btn" onClick={() => setDocModalId(d._id)}>{d.label}</button>
                          {taskNamesOf(d.docType) && <span className="doc-task">{taskNamesOf(d.docType)}</span>}
                          {
                            d.budgetRef && !!budgetRecordLabel(d.budgetRef) &&
                            <button className="link-btn doc-task" onClick={() => d.budgetRef && setBudgetRecord({ kind: d.budgetRef.kind, id: d.budgetRef.id })}>{budgetRecordLabel(d.budgetRef)}</button>
                          }
                          <AiReadIndicator doc={d} />
                          {contentHits[d._id] && <div className="meta-row">…{contentHits[d._id]}…</div>}
                        </span>
                        <span className="doc-badge" data-tooltip-id="pm-task-tip" data-tooltip-content={strings.Manage()} onClick={() => setDocModalId(d._id)}><Badge status={d.status} label={statusLabel(d.status)} /></span>
                        <span>
                          {
                            d.currentVersion ? versionOpenLink(d, d.currentVersion, d.label) : '—'
                          }
                        </span>
                        <span className="num">v{d.versions.length}</span>
                      </div>
                    );
                    // A search reveals its matches wherever they sit, regardless of what is collapsed.
                    const isOpen = (key: string) => !!docQuery || !collapsedDocGroups.has(key);
                    const head = (key: string, className: string, label: React.ReactNode, count: number) => (
                      <button className={`tree-head ${className}`} onClick={() => toggleDocGroup(key)}>
                        <span className="caret">{isOpen(key) ? '▾' : '▸'}</span>
                        {label}
                        <span className="tree-count">{count}</span>
                      </button>
                    );
                    if (!documentTree.length && !budgetDocs.length && !unplacedDocs.length) {
                      return <div className="tree-empty meta-row">{!user?.isSuperUser && !visibleDocuments.length ? strings.NoAssignedDocuments() : '—'}</div>;
                    }
                    return (
                      <div className="doc-tree">
                        {
                          documentTree.map(({ role, docs }) => {
                            const sKey = `s:${role.key}`;
                            return (
                              <div key={role.key}>
                                {head(sKey, 'stage', <span className="tree-label">{role.label}</span>, docs.length)}
                                {isOpen(sKey) && docs.map(docRow)}
                              </div>
                            );
                          })
                        }
                        {
                          !!budgetDocs.length &&
                          <div>
                            {head('budget', 'stage', <span className="tree-label">{strings.Budget()}</span>, budgetDocs.length)}
                            {isOpen('budget') && budgetDocs.map(docRow)}
                          </div>
                        }
                        {
                          !!unplacedDocs.length &&
                          <div>
                            {head('other', 'stage', <span className="tree-label">{strings.OtherDocuments()}</span>, unplacedDocs.length)}
                            {isOpen('other') && unplacedDocs.map(docRow)}
                          </div>
                        }
                      </div>
                    );
                  })()
                }
              </div>
            </div>
              }

              {tab === 'schedule' &&
            <div className="panel">
              <div className="panel-scroll">
                <ProjectManagementScheduleView
                  tasks={tasks}
                  // Members see their own tasks (publication-scoped) laid over the full project milestone
                  // schedule as read-only context — otherwise a member with few dated tasks gets a blank
                  // calendar. The view exposes no milestone edit/delete, so this stays read-only for them.
                  milestones={milestones}
                  milestoneColor={milestoneDotColor}
                  onOpenTask={(taskId) => openTaskFromSchedule(taskId)}
                />
              </div>
            </div>
              }

              {tab === 'invoices' &&
            <div className="panel">
              <div className="panel-scroll">{renderInvoices()}</div>
            </div>
              }

              {/* RIC's money model. A vertical with different economics renders its own panel below, so
                  changing one cannot disturb the other. */}
              {tab === 'finances' && financeModel === 'ric' &&
            <div className="panel finances budget">
              <div className="panel-scroll">
                {/* Sources of cash beside what Luminia keeps of it. The two panels are gated separately: the
                    luminia role sees where the money comes from, while what Luminia keeps of it stays with the
                    fee viewers. Neither side's data is published to anyone else, so a panel that is hidden was
                    also never sent, and the wrapper disappears when both are. */}
                {canSeeFundingSources &&
                <div className="fin-panels">
                  <div className="fin-panel">
                    <div className="fin-section-title">{strings.FundingSources()}{isAdmin && <Button className="secondary compact" onClick={() => editFundingSource()}>{strings.Add()}</Button>}</div>
                    <table className="fin-table">
                      <thead><tr><th>{strings.Type()}</th><th>{strings.Party()}</th><th className="num">{strings.Estimated()}</th><th className="num">{strings.Actual()}</th></tr></thead>
                      <tbody>
                        {
                          fundingSources.map((s) => {
                            // A stored estimate (set by the financial model, or by hand) wins; a row without one
                            // rolls up its cash ledger (Project Payment = the three work orders).
                            const totals = projectManagementFundingSourceTotals(s, cashEvents);
                            return (
                              <tr className="br-row" key={s._id} onClick={() => setBudgetRecord({ kind: 'fundingSource', id: s._id })}>
                                <td>{projectManagementFundingSourceTypeLabel(s.type)}</td>
                                <td>{stakeholderName(s.partyStakeholderId) || s.party || '—'}</td>
                                <td className="num">{cents(totals.estimated)}</td>
                                <td className="num">{cents(totals.actual)}</td>
                              </tr>
                            );
                          })
                        }
                        {!fundingSources.length && <tr><td className="meta-row">—</td></tr>}
                      </tbody>
                    </table>
                  </div>

                  {canSeeFees &&
                  <div className="fin-panel">
                    {/* Luminia's margin on the project: the work-order draws in minus the EPC payments out.
                  Budget is an admin-only tab, which is what keeps this fee figure off external eyes. */}
                    <div className="fin-section-title">
                      {strings.LuminiaFees()}
                      {isAdmin && workOrderDraws.length === Object.keys(projectManagementDevFeeReleaseSplit).length &&
                        <Button className="secondary compact" onClick={() => setShowDevFeeSplit(true)}>{strings.DevFeeSplit()}</Button>}
                    </div>
                    <table className="fin-table">
                      <thead><tr><th></th><th className="num">{strings.Estimated()}</th><th className="num">{strings.Actual()}</th><th className="num">{strings.Variance()}</th></tr></thead>
                      <tbody>
                        {/* The dev fee by work-order draw (projectManagementTotalFees): each draw's release share of the
                            modeled fee, less whatever part of the unabsorbed change orders landed on it, released as the
                            draw books money. A row's figure before change orders is kept beneath while a reduction applies. */}
                        {draws.map((draw) => {
                          const gross = devFeesEstimated != null ? devFeesEstimated * draw.share : undefined;
                          return (
                            <tr key={draw.milestoneKey}>
                              <td>{strings.DevFeeForDraw(draw.label || draw.milestoneKey.toUpperCase())}</td>
                              <td className="num">
                                {draw.estimated != null ? cents(draw.estimated) : ''}
                                {draw.estimated != null && draw.estimated !== gross && <span className="sub">{strings.BeforeChangeOrders(money(gross))}</span>}
                              </td>
                              <td className="num">{draw.drawn && draw.actual != null ? cents(draw.actual) : '—'}</td>
                              {/* Same convention as the cash tables: actual − estimated. */}
                              <td className="num">{draw.drawn && draw.actual != null && draw.estimated != null ? cents(draw.actual - draw.estimated) : '—'}</td>
                            </tr>
                          );
                        })}
                        <tr>
                          <td>{strings.DevFeesTotal()}</td>
                          <td className="num">
                            {devFeesNet != null ? cents(devFeesNet) : ''}
                            {devFeesEstimated != null && waterfall.devFeeReduction > 0 && <span className="sub">{strings.BeforeChangeOrders(money(devFeesEstimated))}</span>}
                          </td>
                          <td className="num">{totalFees.actual != null ? cents(totalFees.actual) : '—'}</td>
                          {/* Like the rows, no variance while a draw has yet to come in — the gap then is timing, not a miss. */}
                          <td className="num">{totalFees.actual != null && devFeesNet != null && draws.every((d) => d.drawn) ? cents(totalFees.actual - devFeesNet) : '—'}</td>
                        </tr>
                        <tr>
                          <td>{strings.TenantBilling()}</td>
                          {/* (Tenant Billing per W) × (PV System Size) × 1000, estimated params; blank if either is missing. */}
                          <td className="num">{tenantBillingEstimated != null ? cents(tenantBillingEstimated) : ''}</td>
                          <td className="num">—</td>
                          <td className="num">—</td>
                        </tr>
                      </tbody>
                    </table>

                    <div className="fin-section-title">{strings.AncillaryFees()}</div>
                    <table className="fin-table">
                      <thead><tr><th></th><th className="num">{strings.Estimated()}</th><th className="num">{strings.Actual()}</th><th className="num">{strings.Variance()}</th></tr></thead>
                      <tbody>
                        {/* Figures from projectManagementAncillaryFees; blank while an input is missing. Contingency and
                            Buyout Savings are the cushions accepted change orders draw down, in that order, before the dev fee. */}
                        <tr>
                          <td>{strings.Contingency()}</td>
                          <td className="num">
                            {ancillary.contingency != null ? cents(ancillary.contingency) : ''}
                            {waterfall.contingencyUsed > 0 && <span className="sub">{strings.DrawnByChangeOrders(money(waterfall.contingencyUsed), money((ancillary.contingency || 0) - waterfall.contingencyUsed))}</span>}
                          </td>
                          <td className="num">—</td>
                          <td className="num">—</td>
                        </tr>
                        <tr>
                          <td>{strings.Commissioning()}</td>
                          <td className="num">{ancillary.commissioning != null ? cents(ancillary.commissioning) : ''}</td>
                          <td className="num">—</td>
                          <td className="num">—</td>
                        </tr>
                        <tr>
                          <td>{strings.BuyoutSavings()}</td>
                          <td className="num">{ancillary.buyoutSavings != null ? cents(ancillary.buyoutSavings) : ''}</td>
                          <td className="num">—</td>
                          <td className="num">—</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  }
                </div>
                }

                <div className="fin-section-title budget-milestone-filter">
                  {strings.Milestones()}:
                  <Select
                    value={budgetMilestoneFilter}
                    options={[{ value: '', label: strings.AllMilestones() }, ...milestones.filter((m) => cashEvents.some((c) => c.milestoneId === m._id)).map((m) => ({ value: m._id, label: m.label }))]}
                    onChange={(v) => setBudgetMilestoneFilter(v || '')}
                  />
                </div>
                <div className="fin-panels cash">
                  <div className="fin-panel">
                    {renderCashTable('in')}
                  <div className="fin-section-title">
                    {strings.ChangeOrders()}
                    {isAdmin && <Button className="secondary compact" onClick={() => editChangeOrder()}>{strings.Add()}</Button>}
                    <span className="meta-row">
                      {coSummary.count} · {cents(coSummary.totalValue)} ({strings.Accepted()}: {cents(coSummary.acceptedValue)})
                    </span>
                  </div>
                  <table className="fin-table">
                    <thead><tr><th>{strings.Description()}</th><th className="num">{strings.Value()}</th>{canSeeFundingSources && <th>{strings.FundingSources()}</th>}<th>{strings.Status()}</th></tr></thead>
                    <tbody>
                      {
                        changeOrders.map((co) => (
                            <tr className="br-row" key={co._id} onClick={() => setBudgetRecord({ kind: 'changeOrder', id: co._id })}>
                              <td>
                                {co.title}
                                {co.rationale && <div className="meta-row">{co.rationale}</div>}
                                {co.decisionNote && <div className="meta-row">{coStatusLabel[co.status]}: {co.decisionNote}</div>}
                              </td>
                              <td className="num">{changeOrderValue(co)}</td>
                              {canSeeFundingSources && <td>{co.fundingSourceId ? (fundingSourceOptions.find((o) => o.value === co.fundingSourceId)?.label || '—') : '—'}</td>}
                              <td><Badge status={co.status} label={coStatusLabel[co.status]} /></td>
                            </tr>
                        ))
                      }
                      {!changeOrders.length && <tr><td className="meta-row">—</td></tr>}
                    </tbody>
                  </table>

                  <div className="fin-section-title">{strings.PurchaseOrders()}{isAdmin && <Button className="secondary compact" onClick={() => editPurchaseOrder()}>{strings.Add()}</Button>}</div>
                  <table className="fin-table">
                    <thead><tr><th>{strings.Reference()}</th><th>{strings.OrderDate()}</th><th>{strings.EquipmentType()}</th><th>{strings.Source()}</th><th>{strings.Supplier()}</th><th className="num">{strings.Value()}</th></tr></thead>
                    <tbody>
                      {
                        purchaseOrders.map((po) => (
                          <tr className="br-row" key={po._id} onClick={() => setBudgetRecord({ kind: 'purchaseOrder', id: po._id })}>
                            <td>
                              {po.reference}
                              {po.note && <div className="meta-row">{po.note}</div>}
                            </td>
                            <td>{formatCalendarDate(po.orderDate) || '—'}</td>
                            <td>
                              {po.equipmentType || '—'}
                              {po.serials && <div className="meta-row">{strings.Serials()}: {po.serials}</div>}
                            </td>
                            <td>{po.source === 'inventory' ? strings.Inventory() : strings.External()}</td>
                            <td>{stakeholderName(po.supplierStakeholderId) || '—'}</td>
                            <td className="num">{cents(po.value)}</td>
                          </tr>
                        ))
                      }
                      {!purchaseOrders.length && <tr><td className="meta-row">—</td></tr>}
                    </tbody>
                  </table>
                  </div>
                  <div className="fin-panel">{renderCashTable('out')}</div>
                </div>

                {
                  inventorySites.length > 0 &&
                <>
                  <div className="fin-section-title">
                    {strings.InventoryEquipmentHeading()}
                    <Link to={`/inventory/pallet-inventory?project=${project._id}`}><Button className="secondary compact">{strings.Inventory()}</Button></Link>
                  </div>
                  <table className="fin-table">
                    <thead>
                      <tr>
                        <th>{strings.InventorySiteId()}</th>
                        <th className="num">{strings.InventoryModulesRequired()}</th>
                        <th className="num">{strings.InventoryModulesAllocated()}</th>
                        <th className="num">{strings.InventoryModuleBalance()}</th>
                        <th className="num">{strings.InventoryPalletsAllocated()}</th>
                        <th>{strings.InventoryAllocationStatus()}</th>
                        <th>{strings.InventoryDelivery()}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {
                        inventorySites.map((site) => {
                          const sitePallets = inventoryPallets.filter((p) => p.assignedSiteId === site.siteId);
                          const allocated = sitePallets.reduce((n, p) => n + (p.modules || 0), 0);
                          const required = inventorySiteModulesRequired(site);
                          const delivery = inventoryDeliveries.find((d) => d.siteId === site.siteId);
                          return (
                            <tr key={site._id}>
                              <td><Link to={`/inventory/sites/${site.siteId}`}>{site.siteId}</Link> {site.propertyName}</td>
                              <td className="num">{required == null ? '—' : required.toLocaleString()}</td>
                              <td className="num">{allocated.toLocaleString()}</td>
                              <td className="num">{required == null ? '—' : (required - allocated).toLocaleString()}</td>
                              <td className="num">{sitePallets.length.toLocaleString()} / {inventoryPalletsFor(required) ?? '—'}</td>
                              <td>{inventoryAllocationStatusLabel(inventoryAllocationStatus(required, allocated))}</td>
                              <td>{delivery ? inventoryDeliveryStatusLabel(delivery.status) : '—'}</td>
                            </tr>
                          );
                        })
                      }
                    </tbody>
                  </table>
                </>
                }

                <div className="fin-section-title">{strings.InvoicesPayable()}</div>
                {renderInvoices()}

              </div>
            </div>
              }

              {tab === 'finances' && financeModel === 'resTpo' && !!lifecycle?.finance &&
            <div className="panel finances budget">
              <div className="panel-scroll">
                <ProjectManagementBudgetResTpo
                  projectId={project._id}
                  finance={lifecycle.finance}
                  cashEvents={cashEvents}
                  milestones={milestones}
                  parameters={parameters}
                  fundingSources={fundingSources}
                  canEdit={isAdmin}
                  canSeeFundingSources={canSeeFundingSources}
                />
                <div className="fin-section-title">{strings.InvoicesPayable()}</div>
                {renderInvoices()}
              </div>
            </div>
              }

              {tab === 'parameters' &&
            <div className="panel finances">
              <div className="panel-tools">
                <div className="param-search"><Input value={parameterSearch} onChange={(v) => setParameterSearch(v || '')} placeholder={strings.Search()} /></div>
                {
                  // Auto-approving is exercising the approval gate, so this is Luminia's own people only —
                  // super users and the luminia role. An org admin uploads the model the ordinary way from
                  // the Documents tab, where it waits for an approver like any other document.
                  isLuminiaUser(user, account) &&
                  <FileInput
                    className="task-doc-upload"
                    label={strings.UploadFinancialModel()}
                    accept=".xlsx,.xlsm,.ods"
                    maxSize={maxSpreadsheetUploadBytes}
                    onChange={(file) => uploadFinancialModel(file)}
                  />
                }
                <Button className="secondary compact" onClick={() => openParam()}>{strings.Add()}</Button>
              </div>
              <div className="panel-scroll">
                {!parameterGroups.length && <div className="meta-row">—</div>}
                {
                  parameterGroups.map((group) => (
                    <div className="param-group" key={group.cat || 'other'}>
                      <div className="fin-section-title">{group.label}</div>
                      {/* Two tables per category rather than one with mostly-blank columns: a forecast
                          that reality confirms and a plain figure are different things, and a shared
                          header made every single-value row look like it was missing an actual. */}
                      {!!group.items.filter((mv) => projectManagementTracksActual(mv)).length &&
                      <table className="fin-table">
                        <thead><tr><th>{strings.Name()}</th><th className="num">{strings.Estimated()}</th><th className="promote-col"></th><th className="num">{strings.Actual()}</th><th className="num">{strings.Variance()}</th><th className="pd-notes">{strings.NotesStatus()}</th><th></th></tr></thead>
                        <tbody>
                          {
                            group.items.filter((mv) => projectManagementTracksActual(mv)).map((mv) => {
                              const variance = projectManagementParameterVariance(mv);  // number delta or date timedelta
                              const hasEstimated = mv.estimatedValue != null && mv.estimatedValue !== '';
                              const hasActual = mv.actualValue != null && mv.actualValue !== '';
                              return (
                                <tr key={mv._id}>
                                  <td>{mv.name}{mv.source === 'llm' ? ' ✨' : ''}{mv.source === 'model' && <Icon className="model-icon" icon="queryStats" fill={css.primaryColor} tip={strings.FromFinancialModel()} />}</td>
                                  <td className="num">{formatValue(mv.estimatedValue, mv.unit, mv.type)}</td>
                                  <td className="promote-col"><PromoteToActual estimated={hasEstimated} actual={hasActual} onClick={() => promoteParameter(mv)} /></td>
                                  <td className="num"><button className="link-btn" onClick={() => openParam(mv)}>{hasActual ? formatValue(mv.actualValue, mv.unit, mv.type) : strings.Add()}</button></td>
                                  <td className="num">{variance ?? '—'}</td>
                                  <td className="pd-notes">{mv.notes || '—'}</td>
                                  <td className="num">
                                    <button className="link-btn" onClick={() => setHistoryParam(mv)}>{strings.History()}</button>
                                    <span className="sep">|</span>
                                    <button className="link-btn" onClick={() => openParam(mv)}>{strings.Edit()}</button>
                                    <span className="sep">|</span>
                                    <button className="link-btn" onClick={() => delParameter(mv._id)}>{strings.Delete()}</button>
                                  </td>
                                </tr>
                              );
                            })
                          }
                        </tbody>
                      </table>
                      }
                      {!!group.items.filter((mv) => !projectManagementTracksActual(mv)).length &&
                      <table className="fin-table pd-single">
                        <thead><tr><th>{strings.Name()}</th><th className="num">{strings.Value()}</th><th className="pd-notes">{strings.NotesStatus()}</th><th></th></tr></thead>
                        <tbody>
                          {
                            group.items.filter((mv) => !projectManagementTracksActual(mv)).map((mv) => (
                              <tr key={mv._id}>
                                <td>{mv.name}{mv.source === 'llm' ? ' ✨' : ''}{mv.source === 'model' && <Icon className="model-icon" icon="queryStats" fill={css.primaryColor} tip={strings.FromFinancialModel()} />}</td>
                                <td className="num"><button className="link-btn" onClick={() => openParam(mv)}>{formatValue(mv.estimatedValue, mv.unit, mv.type)}</button></td>
                                <td className="pd-notes">{mv.notes || '—'}</td>
                                <td className="num">
                                  <button className="link-btn" onClick={() => setHistoryParam(mv)}>{strings.History()}</button>
                                  <span className="sep">|</span>
                                  <button className="link-btn" onClick={() => openParam(mv)}>{strings.Edit()}</button>
                                  <span className="sep">|</span>
                                  <button className="link-btn" onClick={() => delParameter(mv._id)}>{strings.Delete()}</button>
                                </td>
                              </tr>
                            ))
                          }
                        </tbody>
                      </table>
                      }
                    </div>
                  ))
                }
              </div>
            </div>
              }

              {tab === 'delivery' &&
            <div className="panel divider tasks-panel">
              <div className="panel-tools task-filters">
                <Input value={taskSearch} onChange={(v) => setTaskSearch(v || '')} placeholder={strings.Search()} />
                <Select
                  value={taskMilestoneFilter}
                  options={[{ value: '', label: strings.AllMilestones() }, ...milestones.filter((m) => tasks.some((t) => t.milestoneId === m._id)).map((m) => ({ value: m._id, label: m.label }))]}
                  onChange={(v) => setTaskMilestoneFilter(v || '')}
                />
                {canAddTask && <Button className="secondary compact add-task" onClick={() => editTask()}>{strings.Add()}</Button>}
              </div>
              {/* Bucket strip: counts always cover the bucket's FULL task set — the search/milestone
                  filters narrow the table below, never the strip. */}
              <div className="bucket-strip">
                {
                  taskGroups.map((g, i) => {
                    const done = g.tasks.filter((t) => t.status === 'done').length;
                    const complete = !!g.tasks.length && done === g.tasks.length;
                    // When the bucket is finished, when it finished: the latest date across its tasks.
                    // actualDate is the date the work is REPORTED to have happened and is what a person
                    // means, so it wins; completedAt is the fallback — derived from the requirements on
                    // the auto-computed path, but still the keying-in day on a manually ticked or
                    // imported task.
                    const finishedOn = complete
                      ? g.tasks.reduce((latest: { at: Date, label: string } | undefined, t) => {
                        const d = t.actualDate || t.completedAt;
                        if (!d) return latest;
                        const at = new Date(d);
                        // The reported date is a calendar day and reads in UTC; the stamp is a real moment
                        // and reads in the viewer's zone. Formatting both alike would move one of them.
                        const label = t.actualDate ? formatCalendarDate(at) : at.toLocaleDateString();
                        return !latest || at > latest.at ? { at, label } : latest;
                      }, undefined)
                      : undefined;
                    return (
                      <div className={`bucket-step${g.key === selectedBucketKey ? ' selected' : ''}${complete ? ' complete' : ''}`} key={g.key}>
                        <button className="step-col" onClick={() => setTaskBucket(g.key)}>
                          <span className="step-circle">{complete ? <Icon icon="check" fill={css.white} /> : i + 1}</span>
                          <span className="step-label">{g.label}</span>
                          <span className="step-count">{done} / {g.tasks.length}</span>
                          {finishedOn && <span className="step-done-date">{finishedOn.label}</span>}
                        </button>
                        {i < taskGroups.length - 1 && <span className="step-line" />}
                      </div>
                    );
                  })
                }
              </div>
              <div className="panel-scroll">
                {(() => {
                  const source = taskGroups.find((g) => g.key === selectedBucketKey)?.tasks || [];
                  const rows = source.filter((t) => t.label.toLowerCase().includes(taskSearch.trim().toLowerCase())
                    && (!taskMilestoneFilter || t.milestoneId === taskMilestoneFilter));
                  if (!rows.length) return <div className="meta-row">—</div>;
                  const taskDate = (d?: Date) => formatCalendarDate(d) || '—';
                  return (
                    <table className="fin-table task-table">
                      <thead>
                        <tr>
                          <th>{strings.Name()}</th>
                          <th>{strings.Status()}</th>
                          <th>{strings.Milestone()}</th>
                          <th>{strings.TaskDates()}</th>
                          <th>{strings.Owner()}</th>
                          <th className="num">{strings.Completion()}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {
                          rows.map((t) => {
                            const taskParams = parameters.filter((pm) => pm.taskId === t._id);
                            const docsReq = t.requiredDocSlots || [];
                            // Completion = approved documents + details holding their value (the actual when the parameter
                            // tracks one, else its single figure), over everything required.
                            const done = docsReq.filter((dt) => documents.find((d) => d.docType === dt)?.status === 'approved').length
                              + taskParams.filter(projectManagementParameterFilled).length;
                            const total = docsReq.length + taskParams.length;
                            return (
                              <tr key={t._id} onClick={() => setTaskCardId(t._id)}>
                                <td data-tooltip-id="pm-task-tip" data-tooltip-content={t.description || undefined}>{t.label}</td>
                                <td><span className={`badge status-badge ${t.status === 'done' ? 'done' : 'open'}`}>{t.status === 'done' ? strings.Done() : strings.Open()}</span></td>
                                <td>
                                  {(() => {
                                    const ms = milestones.find((m) => m._id === t.milestoneId);
                                    return ms ? <span className="task-ms"><span className="task-ms-dot" style={{ background: milestoneDotColor(ms) }} />{ms.label}</span> : '—';
                                  })()}
                                </td>
                                <td>{taskDate(t.estimatedDate)}{t.actualDate ? ` → ${taskDate(t.actualDate)}` : ''}</td>
                                <td>{projectManagementOwnersOf(t, stakeholders).map((s) => profileOfStakeholder(s)?.name || '—').join(', ') || strings.Unassigned()}</td>
                                <td className={`num completion${total && done === total ? ' complete' : ''}`}>{total ? `${done} / ${total}` : '—'}</td>
                              </tr>
                            );
                          })
                        }
                      </tbody>
                    </table>
                  );
                })()}
              </div>
            </div>
              }

              {tab === 'stakeholders' &&
            <div className="panel">
              <div className="panel-tools">
                <div className="param-search"><Input value={stakeholderSearch} onChange={(v) => setStakeholderSearch(v || '')} placeholder={strings.Search()} /></div>
                <Button className="secondary compact" onClick={() => editStakeholder()}>{strings.Add()}</Button>
              </div>
              <div className="panel-scroll">
                {
                  filteredStakeholders.map((s) => {
                    const profile = profileOfStakeholder(s);
                    return (
                      <div className="transition" key={s._id}>
                        <b>{profile?.name || '—'}</b>
                        <button className="link-btn" style={{ marginLeft: '0.5rem' }} onClick={() => editStakeholder(s)}>{strings.Edit()}</button>
                        <span className="sep">|</span>
                        <button className="link-btn" onClick={() => delStakeholder(s._id)}>{strings.Delete()}</button>
                        {profile?.organization && <div className="when">{profile.organization}</div>}
                      </div>
                    );
                  })
                }
                {!filteredStakeholders.length && <div className="meta-row">—</div>}
              </div>
            </div>
              }

              {tab === 'contacts' &&
            <div className="panel">
              <div className="panel-scroll">
                <div className="fin-section-title">
                  {strings.OnsiteContact()}
                  <Button className="secondary compact" onClick={() => editOnsiteContact()}>{strings.Edit()}</Button>
                </div>
                <div className="onsite-contact">
                  <b>{project.onsiteContactName || '—'}</b>
                  {' · '}{project.onsiteEmail ? <a className="dl" href={`mailto:${project.onsiteEmail}`}>{project.onsiteEmail}</a> : '—'}
                  {' · '}{project.onsitePhone || '—'}
                </div>

                <div className="panel-title">
                  {/* <Icon icon="person" /> {strings.Contacts()} */}
                  <span style={{ marginLeft: 'auto' }}><Button className="secondary compact" onClick={() => editContact()}>{strings.New()}</Button></span>
                </div>
                <table className="fin-table">
                  <thead><tr><th>{strings.Name()}</th><th>{strings.Title()}</th><th>{strings.Email()}</th><th>{strings.Phone()}</th><th></th></tr></thead>
                  <tbody>
                    {
                      contacts.map((c) => (
                        <tr key={c._id}>
                          <td>{c.name}</td>
                          <td>{c.title || '—'}</td>
                          <td>{c.email ? <a className="dl" href={`mailto:${c.email}`}>{c.email}</a> : '—'}</td>
                          <td>{c.phone || '—'}</td>
                          <td className="num">
                            <button className="link-btn" onClick={() => editContact(c)}>{strings.Edit()}</button>
                            <span className="sep">|</span>
                            <button className="link-btn" onClick={() => delContact(c._id)}>{strings.Delete()}</button>
                          </td>
                        </tr>
                      ))
                    }
                    {!contacts.length && <tr><td className="meta-row">—</td></tr>}
                  </tbody>
                </table>

                <div className="panel-title">
                  {/* <Icon icon="link2" /> {strings.Links()} */}
                  <span style={{ marginLeft: 'auto' }}><Button className="secondary compact" onClick={() => editLink()}>{strings.New()}</Button></span>
                </div>
                <table className="fin-table">
                  <thead><tr><th>{strings.Name()}</th><th>{strings.Url()}</th><th></th></tr></thead>
                  <tbody>
                    {
                      links.map((l) => (
                        <tr key={l._id}>
                          <td>{l.label}</td>
                          <td><a className="dl" href={l.url} target="_blank" rel="noreferrer">{l.url}</a></td>
                          <td className="num">
                            <button className="link-btn" onClick={() => editLink(l)}>{strings.Edit()}</button>
                            <span className="sep">|</span>
                            <button className="link-btn" onClick={() => delLink(l._id)}>{strings.Delete()}</button>
                          </td>
                        </tr>
                      ))
                    }
                    {!links.length && <tr><td className="meta-row">—</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
              }

              {tab === 'history' &&
            <div className="panel">
              <div className="panel-scroll">
                {
                  hiddenEventKinds.size > 0 &&
                <div className="hidden-kinds">
                  <span className="label">{strings.Hidden()}:</span>
                  {[...hiddenEventKinds].map((k) => (
                    <button key={k} className="chip" onClick={() => setHiddenEventKinds((s) => { const n = new Set(s); n.delete(k); return n; })}>
                      {eventKindLabel(k as ProjectManagementProjectEvent['kind'])} <Icon icon="close" />
                    </button>
                  ))}
                  <button className="link" onClick={() => setHiddenEventKinds(new Set())}>{strings.ShowAll()}</button>
                </div>
                }
                {
                  events.filter((e) => !hiddenEventKinds.has(e.kind)).map((e) => (
                    <div className="transition" key={e._id}>
                      <span className="hide-kind" onClick={() => setHiddenEventKinds((s) => new Set(s).add(e.kind))}>
                        <Icon icon="visibilityOff" tip={strings.HideTheseEvents()} />
                      </span>
                      {renderEventHeadline(e)}
                      <div className="when">{e.createdByName ? `${e.createdByName} · ` : ''}{e.createdAt && formatDateTime(e.createdAt)}</div>
                    </div>
                  ))
                }
                {!events.length && <div className="meta-row">—</div>}
              </div>
            </div>
              }
            </div>
          </div>
          {/* Milestones timeline: pinned to the right of every tab (the schedule is always in view). */}
          <aside className="timeline-side">
            <div className="panel">
              <div className="panel-title">
                <Icon icon="circleCheck" /> {strings.Milestones()}
                {isAdmin && <Button className="secondary compact" onClick={() => editMilestone()}>{strings.Add()}</Button>}
                <Button className="secondary compact" onClick={() => toggleMilestonesCompact()}>{milestonesCompact ? strings.Expand() : strings.Collapse()}</Button>
              </div>
              {!milestonesCompact && <div className="param-search"><Input value={milestoneSearch} onChange={(v) => setMilestoneSearch(v || '')} placeholder={strings.Search()} /></div>}
              <div className={`timeline${milestonesCompact ? ' compact' : ''}`}>
                {
                  milestones.filter((m) => milestonesCompact || m.label.toLowerCase().includes(milestoneSearch.trim().toLowerCase())).map((m) => {
                    const label = (
                      <div className="tl-label">
                        {m.label}
                        {/* Via the root tooltip, opening leftwards: an inline tooltip here is clipped by the panel's
                            scroll box at the right edge of the window. */}
                        {m.isPaymentTrigger && <Icon className="pay-icon" icon="attachMoney" fill={css.primaryColor} data-tooltip-id="pm-task-tip" data-tooltip-content={strings.PaymentTriggerHint()} data-tooltip-place="left" />}
                        {!!m.actualDate && <Icon className="ms-done" icon="circleCheck" fill={css.primaryColor} data-tooltip-id="pm-task-tip" data-tooltip-content={strings.CompletedFromTasksHint()} data-tooltip-place="left" />}
                        {m.reviewed && !!m.reviewStatus && <Badge status={projectManagementMilestoneReviewBadge(m.reviewStatus)} label={projectManagementMilestoneReviewLabel(m.reviewStatus)} />}
                      </div>
                    );
                    return (
                      <div className="tl-item" key={m._id}>
                        <span className="tl-dot" style={{ background: milestoneDotColor(m) }} />
                        {
                          milestonesCompact ?
                            // Compact: one line per milestone — label left, its (actual, else estimated) date right.
                            <div className="tl-body">
                              {label}
                              <div className="tl-date">{formatCalendarDate(m.actualDate || m.plannedDate) || '—'}</div>
                            </div>
                            :
                            <div className="tl-body">
                              {label}
                              <div className="tl-date">
                                {
                                  isAdmin ?
                                    <>
                                      {strings.Estimated()}: <button className="link-btn" onClick={() => editMilestoneDate(m, 'plannedDate')}>{formatCalendarDate(m.plannedDate) || '—'}</button>
                                      {' · '}{strings.Actual()}: <button className="link-btn" onClick={() => editMilestoneDate(m, 'actualDate')}>{formatCalendarDate(m.actualDate) || '—'}</button>
                                    </>
                                    :
                                    // Members see their schedule read-only — dates and completion are set by Luminia.
                                    <>
                                      {strings.Estimated()}: {formatCalendarDate(m.plannedDate) || '—'}
                                      {' · '}{strings.Actual()}: {formatCalendarDate(m.actualDate) || '—'}
                                    </>
                                }
                              </div>
                              {
                                isAdmin &&
                                <div className="tl-actions">
                                  {
                                    // Only once the engine has put it in front of the reviewer: an unreached
                                    // milestone has nothing to decide on.
                                    canApproveDocuments && m.reviewed && !!m.reviewStatus &&
                                    <>
                                      <button className="link-btn" onClick={() => setReviewMilestoneId(m._id)}>{strings.MilestoneReview()}</button>
                                      <span className="sep">|</span>
                                    </>
                                  }
                                  <button className="link-btn" onClick={() => editMilestone(m)}>{strings.Edit()}</button>
                                  <span className="sep">|</span>
                                  <button className="link-btn" onClick={() => removeMilestone(m)}>{strings.Delete()}</button>
                                </div>
                              }
                            </div>
                        }
                      </div>
                    );
                  })
                }
              </div>
              {!milestones.some((m) => milestonesCompact || m.label.toLowerCase().includes(milestoneSearch.trim().toLowerCase())) && <div className="meta-row">—</div>}
            </div>
          </aside>
        </div>
      </PageBody>

      {
        showAddDoc &&
        <Modal styled={WideModalStyled} title={strings.AddDocument()} onClose={() => setShowAddDoc(false)}>
          <div className="add-fields">
            <div>
              <div className="meta-row">{strings.Name()}</div>
              <Input value={newDoc.label} onChange={(v) => setNewDoc({ ...newDoc, label: v || '' })} />
            </div>
            <div>
              <div className="meta-row">{strings.Type()}</div>
              <Select
                value={newDoc.docType || 'other'}
                options={[
                  { value: 'other', label: strings.Other() },
                  ...(lifecycle?.docSlots || [])
                    .filter((s, i, all) => all.findIndex((o) => o.docType === s.docType) === i)
                    .map((s) => ({ value: s.docType, label: s.label })),
                ]}
                onChange={(v) => {
                  const slot = lifecycle?.docSlots.find((s) => s.docType === v);
                  // Picking a known slot type pre-fills the (still editable) display name.
                  setNewDoc({ docType: v, label: newDoc.label || slot?.label || '' });
                }}
              />
            </div>
          </div>
          <div className="modal-buttons">
            <Button className="secondary" onClick={() => setShowAddDoc(false)}>{strings.Cancel()}</Button>
            <Button className="primary" onClick={() => addDoc()}>{strings.Create()}</Button>
          </div>
        </Modal>
      }
      {fieldsPrompt && <FieldsModal prompt={fieldsPrompt} onClose={() => setFieldsPrompt(undefined)} />}
      {/* ConversationFeed is already the slide-up bottom-right panel, so this is only the wiring. Keyed
          on the conversation so a project switch remounts it rather than showing the previous chat. */}
      {
        chatOpen && chatConversationId &&
        <ConversationFeed key={chatConversationId} conversationId={chatConversationId} onClose={() => setChatOpen(false)} />
      }
      {renderBudgetRecordModal()}
      {allocating && <InvoiceAllocationModal invoice={allocating} uses={outUses} raised={changeOrders.filter((co) => co.invoiceId === allocating._id)} onClose={() => setAllocating(undefined)} />}
      {reviewing && <ProjectManagementInvoiceReviewModal invoice={reviewing} onClose={() => setReviewing(undefined)} />}
      {showStakeholder && <StakeholderModal projectId={project._id} stakeholder={showStakeholder.stakeholder} profile={profileOfStakeholder(showStakeholder.stakeholder)} onClose={() => setShowStakeholder(undefined)} />}
      {historyParam && <ParameterHistoryModal parameter={historyParam} onClose={() => setHistoryParam(undefined)} />}
      {paramModal && <ParameterModal parameter={paramModal.parameter} taskId={paramModal.taskId} taskOptions={paramTaskOptions} projectId={project._id} onClose={() => setParamModal(undefined)} />}
      {
        (() => {
          const d = docModalId ? documents.find((x) => x._id === docModalId) : undefined;
          return d ? <Modal styled={DocModalStyled} title={d.label} onClose={() => setDocModalId(undefined)}>{renderDocCard(d)}</Modal> : null;
        })()
      }
      {/* One tooltip serves every data-tooltip-id="pm-task-tip" anchor on the page. Rendered HERE, at
          the root, on purpose: inside .task-param (a subgrid) an extra node would take a column track
          and shift the row. */}
      {
        taskCardId && (() => {
          const t = tasks.find((x) => x._id === taskCardId);
          return t ? (
            <Modal styled={TaskCardModalStyled} title={t.role || strings.Tasks()} onClose={() => setTaskCardId(undefined)}>
              <div className="task-modal-layout">
                <div className="task-modal-main">{renderTaskCard(t)}</div>
                {renderTaskHistory(t)}
              </div>
            </Modal>
          ) : null;
        })()
      }
      {
        // Rendered at the root so it stacks ON TOP of whichever modal opened it — the task card stays
        // where it was underneath, and Esc closes the viewer first.
        viewerFile && <FileViewerModal file={viewerFile.file} title={viewerFile.title} downloadName={viewerFile.downloadName} onClose={() => setViewerFile(undefined)} />
      }
      {
        // Same reason as the viewer above: at the root, on top of the card that opened it. The loader is
        // Project Management's own method, so reading these photographs asks for no Documents role.
        galleryVersion && (
          <DriveGalleryModal
            title={galleryVersion.title}
            load={(from, limit) => projectManagementDocumentGallery.call({ versionId: galleryVersion.versionId, from, limit })}
            onClose={() => setGalleryVersion(undefined)}
          />
        )
      }
      <Tooltip id="pm-task-tip" />
      <ProjectStatusTooltip />
      {showEdit && <EditProjectModal project={project} hideSolar={solarKwParam != null} hideStorage={bessKwhParam != null} onClose={() => setShowEdit(false)} />}
      {showStatus && <ProjectStatusModal project={project} onClose={() => setShowStatus(false)} />}
      {showDevFeeSplit && <DevFeeSplitModal projectId={project._id} draws={workOrderDraws} devFee={devFeesEstimated} onClose={() => setShowDevFeeSplit(false)} />}
      {showDrawSplit && <DrawSplitModal projectId={project._id} draws={workOrderDraws} totalSolarCost={totalSolarCostValue} onClose={() => setShowDrawSplit(false)} />}
      {!!reviewMilestone && <MilestoneReviewModal milestone={reviewMilestone} onClose={() => setReviewMilestoneId(undefined)} />}
      {
        confirm &&
        <ConfirmModal
          title={confirm.title || strings.Delete()}
          body={confirm.body}
          confirmText={confirm.confirmText || strings.Delete()}
          onConfirm={() => { const run = confirm.run; setConfirm(undefined); run(); }}
          onClose={() => setConfirm(undefined)}
        />
      }
      {
        parsedModel &&
        <ParsedModelModal
          rows={parsedModel}
          onAccept={(rows) => acceptParsedModel(rows)}
          onClose={() => setParsedModel(undefined)}
        />
      }
      {
        showDelete &&
        <ConfirmModal
          title={strings.DeleteProject()}
          body={strings.DeleteProjectConfirm()}
          confirmText={strings.Delete()}
          onConfirm={() => { setShowDelete(false); deleteProject(); }}
          onClose={() => setShowDelete(false)}
        />
      }
    </ProjectManagementProjectPageStyled>
  );
};

/**
 * Localized label for a document/version status.
 * @param status - status key
 * @returns translated status label
 */
const statusLabel = (status: string): string => {
  switch (status) {
    case 'approved': return strings.Approved();
    case 'pending': return strings.Pending();
    case 'rejected': return strings.Rejected();
    case 'superseded': return strings.Superseded();
    default: return '—';
  }
};
